<template>
  <div>
    <!-- Header Action Bar -->
    <div class="px-3 pt-3">
      <v-card class="view-mode-bar">
        <v-layout row wrap align-center class="ma-0 px-3 py-2">
          <v-icon class="mr-2" color="rgb(92, 184, 96)" medium
            >mdi-bus-alert</v-icon
          >
          <span class="title font-weight-medium">
            Bus Change Requests
          </span>
          <v-btn
            icon
            small
            class="ml-2 my-0"
            title="Refresh"
            :loading="loadingSimpleRequests"
            @click="loadSimpleViewData"
          >
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
          <span v-if="simpleLastRefreshed" class="caption grey--text ml-1">
            Updated {{ simpleLastRefreshed }}
          </span>
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            outline
            small
            class="my-1 mr-3"
            @click="openSetupDialog"
          >
            <v-icon left small>mdi-settings</v-icon>
            Setup
          </v-btn>
          <v-btn
            color="success"
            outline
            class="my-1 mr-2 header-main-action-btn header-report-btn"
            @click="openReportDialog"
          >
            <v-icon left>mdi-download</v-icon>
            Download Report
          </v-btn>
          <v-btn
            color="success"
            dark
            class="my-1 header-main-action-btn header-add-btn"
            @click="openAddBusChangeDialog"
          >
            <v-icon left>mdi-plus-circle</v-icon>
            Add Bus Change
          </v-btn>
        </v-layout>
      </v-card>
    </div>

    <!-- Simple Card View -->
    <div class="simple-view pa-3 pt-2">
      <v-card
        v-for="section in simpleSections"
        :key="section.key"
        class="simple-section mb-3"
        :class="{
          'simple-section-minimized': isSimpleSectionCollapsed(section),
          'simple-section-collapsible': section.collapsible,
        }"
      >
        <div
          class="simple-section-header"
          :class="{ 'simple-section-header-clickable': section.collapsible }"
          @click="section.collapsible && toggleSimpleSection(section.key)"
        >
          <v-icon class="mr-2" :color="section.color">{{
            section.icon
          }}</v-icon>
          <span class="simple-section-title">{{ section.title }}</span>
          <span class="section-count" :class="section.key + '-count'">
            {{
              sectionFilterActive(section)
                ? filteredSectionItems(section).length + " / "
                : ""
            }}{{ section.items.length }}
          </span>
          <span
            v-if="section.key === 'stillOpen' && overdueStillOpenCount > 0"
            class="section-overdue-count"
          >
            <v-icon x-small dark class="mr-1">mdi-alert</v-icon>
            {{ overdueStillOpenCount }} overdue
          </span>
          <v-btn
            v-if="section.key === 'stillOpen' && overdueStillOpenCount > 0"
            flat
            small
            color="red darken-2"
            class="my-0 ml-2"
            :loading="printingOverdueList"
            @click.stop="printOverdueList"
          >
            <v-icon left small>mdi-download</v-icon>
            Download overdue report
          </v-btn>
          <v-btn
            v-if="section.key === 'stillOpen'"
            color="warning"
            outline
            small
            class="my-0 ml-2"
            @click.stop="openEndBusChangeDialog"
          >
            <v-icon left small>mdi-calendar-remove</v-icon>
            End Bus Change
          </v-btn>
          <v-spacer></v-spacer>
          <span class="caption grey--text section-hint">{{
            section.key === "pending" &&
            !loadingSimpleRequests &&
            section.items.length === 0
              ? "All caught up"
              : section.hint
          }}</span>
          <v-btn
            v-if="section.collapsible"
            icon
            small
            class="my-0 ml-2"
            title="Expand / collapse section"
            @click.stop="toggleSimpleSection(section.key)"
          >
            <v-icon small>{{
              isSimpleSectionCollapsed(section)
                ? "mdi-chevron-down"
                : "mdi-chevron-up"
            }}</v-icon>
          </v-btn>
        </div>
        <template v-if="!isSimpleSectionCollapsed(section)">
          <v-divider></v-divider>
          <div class="pa-3">
            <div
              v-if="!loadingSimpleRequests && section.items.length > 0"
              class="section-filter-bar"
            >
              <v-text-field
                v-model="sectionFilters[section.key].search"
                prepend-inner-icon="mdi-magnify"
                placeholder="Search..."
                clearable
                dense
                hide-details
                single-line
                class="section-filter-search"
              ></v-text-field>
              <v-autocomplete
                v-if="showSectionBuildingFilter(section)"
                v-model="sectionFilters[section.key].buildings"
                :items="sectionBuildingOptions(section)"
                placeholder="Buildings"
                multiple
                chips
                small-chips
                deletable-chips
                clearable
                dense
                hide-details
                single-line
                no-data-text="No buildings"
                class="section-filter-buildings"
              ></v-autocomplete>
              <v-btn
                v-if="sectionFilterActive(section)"
                flat
                small
                class="my-0"
                @click="clearSectionFilter(section)"
                >Clear</v-btn
              >
            </div>
            <div v-if="loadingSimpleRequests" class="simple-loading">
              <v-progress-circular
                indeterminate
                color="success"
                size="28"
                width="3"
              ></v-progress-circular>
              <span class="ml-2 grey--text">Loading...</span>
            </div>
            <div v-else-if="section.items.length === 0" class="simple-empty">
              <v-icon large color="grey lighten-2">{{
                section.emptyIcon
              }}</v-icon>
              <div class="mt-1 grey--text">{{ section.emptyText }}</div>
            </div>
            <div
              v-else-if="filteredSectionItems(section).length === 0"
              class="simple-empty"
            >
              <v-icon large color="grey lighten-2">mdi-filter-remove-outline</v-icon>
              <div class="mt-1 grey--text">Nothing matches your search.</div>
            </div>
            <v-layout v-else row wrap>
              <v-flex
                xs12
                sm6
                md4
                lg3
                v-for="(request, index) in filteredSectionItems(section)"
                :key="
                  section.key + '-' + (getBusChangeRequestId(request) || index)
                "
                class="pa-1"
              >
                <v-card
                  class="simple-card"
                  :class="[
                    section.key + '-card',
                    {
                      'stillOpen-card-overdue':
                        section.key === 'stillOpen' &&
                        isLongRunningBusChange(request),
                    },
                  ]"
                  hover
                  @click.native="
                    section.key === 'pending'
                      ? onRowClicked(request)
                      : onBusChangeCardClicked(request)
                  "
                >
                  <v-card-text class="pa-3">
                    <div
                      v-if="
                        section.key === 'stillOpen' &&
                          isLongRunningBusChange(request)
                      "
                      class="overdue-banner"
                    >
                      <v-icon x-small class="mr-1">mdi-alert</v-icon>
                      Overdue · {{ getLongRunningLabel(request) }}
                    </div>
                    <div class="card-header-row">
                      <div class="card-student-name">
                        {{
                          request.studentNameHeb ||
                            request.studentName ||
                            "Unknown Student"
                        }}
                        <span class="card-student-id" v-if="request.studentID">
                          #{{ trimId(request.studentID) }}
                        </span>
                      </div>
                      <span
                        class="change-type-chip"
                        :class="
                          isSingleDayChange(request)
                            ? 'chip-single'
                            : 'chip-extended'
                        "
                      >
                        <v-icon small dark class="mr-1">{{
                          isSingleDayChange(request)
                            ? "mdi-calendar-today"
                            : "mdi-calendar-range"
                        }}</v-icon>
                        {{ getChangeTypeChipLabel(request) }}
                      </span>
                    </div>
                    <div
                      v-if="request.studentNameHeb && request.studentName"
                      class="card-eng-name"
                    >
                      {{ request.studentName }}
                    </div>
                    <div class="card-info-line" v-if="request.building">
                      <v-icon x-small class="card-line-icon"
                        >mdi-office-building</v-icon
                      >
                      <span>Building {{ request.building }}</span>
                    </div>
                    <div class="card-info-line" v-if="isWithinSummerSeason()">
                      <v-icon x-small class="card-line-icon">mdi-school</v-icon>
                      <span>{{ getCardGradeClass(request) }}</span>
                    </div>
                    <div
                      class="card-info-line"
                      v-if="!isWithinSummerSeason() && getCardTeacher(request)"
                    >
                      <v-icon x-small class="card-line-icon"
                        >mdi-account</v-icon
                      >
                      <span>{{ getCardTeacher(request) }}</span>
                    </div>
                    <div class="card-info-line">
                      <v-icon x-small class="card-line-icon"
                        >mdi-calendar-clock</v-icon
                      >
                      <span>{{ formatCardDateRange(request) }}</span>
                    </div>
                    <div class="card-info-line" v-if="request.busChangeAddress">
                      <v-icon x-small class="card-line-icon"
                        >mdi-map-marker</v-icon
                      >
                      <span class="address-line-content">
                        <span
                          :class="{
                            'address-original': !!getCardOriginalAddress(
                              request
                            ),
                          }"
                          >{{ request.busChangeAddress }}</span
                        >
                        <template v-if="getCardOriginalAddress(request)">
                          <v-icon x-small class="address-arrow"
                            >mdi-arrow-right</v-icon
                          >
                          <span class="address-changed">{{
                            getCardOriginalAddress(request)
                          }}</span>
                        </template>
                      </span>
                    </div>
                    <div
                      class="card-footer-row"
                      v-if="section.key === 'pending'"
                    >
                      <span
                        class="card-meta"
                        v-if="request.source || request.Source"
                      >
                        {{ request.source || request.Source }}
                      </span>
                      <span
                        class="card-meta"
                        v-if="request.callerID || request.CallerID"
                      >
                        <v-icon x-small>mdi-phone</v-icon>
                        {{ formatPhone(request.callerID || request.CallerID) }}
                      </span>
                      <span class="card-meta">
                        Requested
                        {{
                          formatCardDate(request.date || request.Date) || "-"
                        }}
                      </span>
                    </div>
                    <div class="card-footer-row" v-else>
                      <span
                        class="active-status-chip"
                        :class="getActiveStatusInfo(request).cssClass"
                      >
                        <v-icon
                          v-if="isEndedBusChange(request)"
                          x-small
                          dark
                          class="mr-1"
                          >mdi-flag-checkered</v-icon
                        >
                        {{ getActiveStatusInfo(request).label }}
                      </span>
                      <span
                        class="card-meta"
                        v-if="request.enteredBy || request.EnteredBy"
                      >
                        by {{ request.enteredBy || request.EnteredBy }}
                      </span>
                    </div>
                  </v-card-text>
                </v-card>
              </v-flex>
            </v-layout>
          </div>
        </template>
      </v-card>
    </div>

    <!-- Simple Add Bus Change Dialog -->
    <v-dialog
      persistent
      scrollable
      v-model="addBusChangeDialog"
      max-width="860"
    >
      <v-card class="request-dialog">
        <v-card-title class="dialog-header py-2">
          <v-icon class="mr-3 pt-1" color="white" size="24"
            >mdi-bus-clock</v-icon
          >
          <span class="dialog-title">New Bus Change</span>
          <v-spacer></v-spacer>
          <v-btn
            class="white--text my-0"
            icon
            dark
            @click="closeAddBusChangeDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-3 dialog-body">
          <!-- Student Search -->
          <v-card
            v-if="!addSelectedStudent"
            outlined
            class="pa-3 mb-2 form-section-card"
          >
            <div class="section-header mb-2">
              <v-icon small class="mr-1">mdi-account-search</v-icon>
              Find Student
            </div>

            <div class="add-phone-search-panel mb-3">
              <v-avatar
                color="rgb(92, 184, 96)"
                size="40"
                class="add-phone-search-avatar"
              >
                <v-icon dark size="20">mdi-phone</v-icon>
              </v-avatar>
              <div class="add-phone-search-body">
                <div class="add-phone-search-label">Search by Phone Number</div>
                <v-text-field
                  v-model="addPhoneSearch"
                  placeholder="###-###-####"
                  single-line
                  clearable
                  hide-details
                  mask="###-###-####"
                  :loading="addStudentSearchLoading"
                  @input="onAddPhoneSearchInput"
                  autocomplete="no-password"
                  class="add-phone-search-field"
                ></v-text-field>
              </div>
            </div>

            <div class="add-search-or-divider mb-2">
              <span>or search by name</span>
            </div>

            <v-layout row wrap align-center class="mb-2">
              <v-flex xs12 sm6 class="pr-2">
                <v-btn-toggle
                  v-model="addSearchNameMode"
                  mandatory
                  class="search-toggle"
                  @change="onAddSearchNameModeChange"
                >
                  <v-btn flat small value="yiddish">אידיש</v-btn>
                  <v-btn flat small value="english">English</v-btn>
                </v-btn-toggle>
              </v-flex>
            </v-layout>

            <v-layout row wrap class="mb-2">
              <v-flex xs12 sm6 class="pr-2">
                <v-text-field
                  v-model="addLastNameSearch"
                  :label="
                    addSearchNameMode === 'yiddish'
                      ? 'לעצטע נאמען'
                      : 'Last Name'
                  "
                  prepend-inner-icon="mdi-account"
                  outlined
                  dense
                  clearable
                  hide-details
                  :loading="addStudentSearchLoading"
                  @input="onAddNameSearchInput"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field
                  v-model="addFirstNameSearch"
                  :label="
                    addSearchNameMode === 'yiddish'
                      ? 'ערשטע נאמען'
                      : 'First Name'
                  "
                  prepend-inner-icon="mdi-account"
                  outlined
                  dense
                  clearable
                  hide-details
                  @input="onAddNameSearchInput"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <v-layout row wrap class="mb-2">
              <v-flex xs12 sm6 class="pr-2">
                <v-autocomplete
                  v-model="addGradeSearch"
                  :items="addGradeOptions"
                  label="Grade (optional)"
                  prepend-inner-icon="mdi-school"
                  outlined
                  dense
                  clearable
                  hide-details
                  :loading="addLoadingGrades"
                  @change="onAddGradeSearchChange"
                  autocomplete="no-password"
                ></v-autocomplete>
              </v-flex>
            </v-layout>

            <div
              class="account-list-container mb-1"
              v-if="addStudentResults.length > 0"
            >
              <div class="caption grey--text my-2 px-2">
                Select a student ({{ addStudentResults.length }} found)
                <span v-if="addHasMultiSelectableBuilding">
                  — check 2+ siblings in the same building to enter the same bus
                  change for all of them at once (different grades are OK)
                </span>
              </div>
              <v-list
                dense
                :two-line="!addIsDayCampMode"
                :three-line="addIsDayCampMode"
                class="account-list"
              >
                <v-list-tile
                  v-for="student in addStudentResults"
                  :key="student.studentID"
                  @click="selectAddStudent(student)"
                  class="account-item"
                >
                  <v-list-tile-action
                    v-if="addBuildingCounts()[student.building || ''] > 1"
                    class="add-student-check-action"
                  >
                    <v-checkbox
                      :input-value="isAddStudentChecked(student)"
                      color="rgb(92, 184, 96)"
                      hide-details
                      class="ma-0 pa-0"
                      @click.native.stop
                      @change="toggleAddStudentCheck(student)"
                    ></v-checkbox>
                  </v-list-tile-action>
                  <v-list-tile-avatar>
                    <v-icon color="rgb(92, 184, 96)">mdi-account-circle</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title>
                      <strong>{{
                        getAddStudentHebName(student) || student.engName
                      }}</strong>
                      <span
                        v-if="getAddStudentHebName(student) && student.engName"
                        class="grey--text ml-2"
                        >({{ student.engName }})</span
                      >
                    </v-list-tile-title>
                    <template v-if="addIsDayCampMode">
                      <v-list-tile-sub-title>
                        Grade: {{ student.class || "-" }}
                        <span v-if="student.room">
                          | Room: {{ student.room }}</span
                        >
                        <span v-if="student.building">
                          | Building: {{ student.building }}</span
                        >
                        <span v-if="student.accountName">
                          | {{ student.accountName }}</span
                        >
                        <span v-if="student.homePhone || student.cellPhone">
                          |
                          {{
                            formatPhone(student.homePhone || student.cellPhone)
                          }}</span
                        >
                      </v-list-tile-sub-title>
                      <v-list-tile-sub-title v-if="student.regType">
                        <v-icon x-small class="mr-1">mdi-tent</v-icon>
                        {{ getEnrollmentDisplay(student) }}
                      </v-list-tile-sub-title>
                    </template>
                    <v-list-tile-sub-title v-else>
                      Grade: {{ student.class || "-"
                      }}<span v-if="student.section"
                        >-{{ student.section }}</span
                      >
                      <span v-if="student.building">
                        | Building: {{ student.building }}</span
                      >
                      <span v-if="student.teacherH || student.teacherH2">
                        | Teacher: {{ getTeacherDisplay(student) }}</span
                      >
                      <span v-if="student.accountName">
                        | {{ student.accountName }}</span
                      >
                      <span v-if="student.homePhone || student.cellPhone">
                        |
                        {{
                          formatPhone(student.homePhone || student.cellPhone)
                        }}</span
                      >
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                  <v-list-tile-action>
                    <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
                  </v-list-tile-action>
                </v-list-tile>
              </v-list>
            </div>

            <v-layout
              v-if="addCheckedStudentIds.length > 0"
              row
              wrap
              align-center
              class="add-multi-select-bar pa-2 mb-2"
            >
              <span class="caption">
                <strong>{{ addCheckedStudentIds.length }}</strong>
                student{{ addCheckedStudentIds.length > 1 ? "s" : "" }}
                selected
                <span v-if="addCheckedStudentIds.length > 1"
                  >— same bus change will be entered for all</span
                >
              </span>
              <v-spacer></v-spacer>
              <v-btn small flat @click="clearAddCheckedStudents">Clear</v-btn>
              <v-btn
                small
                dark
                color="rgb(92, 184, 96)"
                @click="confirmAddStudentSelection"
                >Continue</v-btn
              >
            </v-layout>

            <v-alert
              :value="
                addStudentSearched &&
                  !addStudentSearchLoading &&
                  addStudentResults.length === 0
              "
              type="warning"
              outline
              dense
              class="mb-0 mt-2"
            >
              No students found. Try a different name, grade, or phone number.
            </v-alert>
            <v-alert
              :value="!!addGradeError"
              type="error"
              outline
              dense
              class="mb-0 mt-2"
            >
              {{ addGradeError }}
            </v-alert>
          </v-card>

          <!-- Selected Student(s) — primary + any siblings, all shown the -->
          <!-- same way so it's clear the same bus change applies to each. -->
          <div
            v-if="addSelectedStudent && addSiblingStudents.length"
            class="caption grey--text mb-1 add-selected-students-hint"
          >
            <v-icon x-small class="mr-1">mdi-account-multiple</v-icon>
            Entering the same bus change for
            {{ addSiblingStudents.length + 1 }} students:
          </div>
          <v-card
            v-for="(displayStudent, displayIdx) in addDisplayStudents"
            :key="displayStudent.studentID"
            outlined
            class="pa-2 mb-2 add-selected-student-card"
          >
            <v-btn
              icon
              small
              class="add-student-clear-btn ma-0"
              :title="
                displayIdx === 0
                  ? 'Choose a different student'
                  : 'Remove from this bus change'
              "
              @click="
                displayIdx === 0
                  ? clearAddStudent()
                  : removeAddSiblingStudent(displayStudent)
              "
            >
              <v-icon color="grey" small>mdi-close-circle</v-icon>
            </v-btn>
            <v-layout row wrap align-center class="add-student-row">
              <v-flex xs12 sm6 class="pr-2">
                <v-layout align-center>
                  <v-avatar
                    color="rgb(92, 184, 96)"
                    size="36"
                    class="mr-2 ml-1"
                  >
                    <v-icon dark size="20">mdi-account</v-icon>
                  </v-avatar>
                  <v-flex class="add-student-main">
                    <div class="add-student-name">
                      {{
                        displayStudent.hebName || displayStudent.studentName
                      }}
                      <span
                        v-if="
                          displayStudent.hebName && displayStudent.studentName
                        "
                        class="caption grey--text ml-1"
                        >({{ displayStudent.studentName }})</span
                      >
                    </div>
                    <div class="add-student-class-chip">
                      <template v-if="addIsDayCampMode">
                        <v-icon x-small class="mr-1">mdi-school</v-icon>
                        {{ displayStudent.class || "-" }}
                        <template v-if="displayStudent.room">
                          · Room {{ displayStudent.room }}
                        </template>
                      </template>
                      <template v-else>
                        <v-icon x-small class="mr-1">mdi-account-star</v-icon>
                        {{ displayStudent.class || "-"
                        }}<template v-if="displayStudent.section"
                          >-{{ displayStudent.section }}</template
                        >
                        <span
                          v-if="
                            displayStudent.teacherH || displayStudent.teacherH2
                          "
                          class="add-student-teacher"
                        >
                          · {{ getTeacherDisplay(displayStudent) }}
                        </span>
                      </template>
                    </div>
                  </v-flex>
                </v-layout>
              </v-flex>

              <v-flex xs12 sm6 class="pl-2 add-student-meta-col">
                <div class="caption grey--text add-student-meta-line">
                  <strong>ID</strong> {{ displayStudent.studentID }}
                  <span class="mx-1">·</span>
                  <strong>Bldg</strong>
                  {{ displayStudent.building || "-" }}
                </div>
                <div
                  v-if="addIsDayCampMode && displayStudent.regType"
                  class="caption daycamp-enrollment-line add-student-meta-line"
                >
                  <v-icon x-small color="rgb(92, 184, 96)" class="mr-1"
                    >mdi-tent</v-icon
                  >
                  {{ getEnrollmentDisplay(displayStudent) }}
                </div>
                <div
                  class="caption grey--text add-student-meta-line"
                  v-if="
                    displayStudent.accountAddress1 ||
                      displayStudent.homePhone ||
                      displayStudent.cellPhone
                  "
                >
                  {{ displayStudent.accountAddress1 }}
                  <span
                    v-if="
                      displayStudent.homePhone || displayStudent.cellPhone
                    "
                  >
                    ·
                    {{
                      formatPhone(
                        displayStudent.homePhone || displayStudent.cellPhone
                      )
                    }}
                  </span>
                </div>
              </v-flex>
            </v-layout>
          </v-card>

          <template v-if="addSelectedStudent">
            <!-- How Long -->
            <v-card outlined class="pa-3 mb-3 form-section-card">
              <div class="section-header mb-2">
                <v-icon small class="mr-1">mdi-calendar-range</v-icon>
                How Long?
              </div>
              <div class="duration-options">
                <div
                  v-for="option in addDurationOptions"
                  :key="option.value"
                  class="duration-option"
                  :class="{
                    'duration-option-selected':
                      addDurationOption === option.value,
                  }"
                  @click="selectAddDuration(option.value)"
                >
                  <div class="duration-option-title">
                    <v-icon
                      v-if="addDurationOption === option.value"
                      small
                      class="mr-1 duration-option-check"
                      >mdi-check-circle</v-icon
                    >
                    {{ option.title }}
                  </div>
                </div>
              </div>
              <div class="duration-selected-caption">
                <v-icon x-small class="mr-1" color="rgb(92, 184, 96)"
                  >mdi-information-outline</v-icon
                >
                {{ addSelectedDurationCaption }}
              </div>

              <!-- Custom Dates -->
              <v-layout
                v-if="addDurationOption === 'custom'"
                row
                wrap
                class="mt-3"
              >
                <v-flex xs12 sm6 class="pr-2">
                  <div class="caption period-title mb-1">Starting Run</div>
                  <v-menu
                    v-model="addStartDateMenu"
                    :close-on-content-click="false"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="addForm.startDate"
                        label="Start Date *"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        hide-details
                        class="mb-2"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="addForm.startDate"
                      color="rgb(92, 184, 96)"
                      @input="addStartDateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                  <v-select
                    v-model="addForm.startPeriod"
                    :items="periodOptions"
                    item-text="label"
                    item-value="value"
                    label="Run *"
                    prepend-inner-icon="mdi-weather-sunset-up"
                    outlined
                    dense
                    hide-details
                  ></v-select>
                </v-flex>
                <v-flex xs12 sm6 class="pl-2">
                  <div class="caption period-title mb-1">Ending After</div>
                  <v-menu
                    v-model="addEndDateMenu"
                    :close-on-content-click="false"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="addForm.endDate"
                        label="End Date"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        hide-details
                        class="mb-2"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="addForm.endDate"
                      color="rgb(92, 184, 96)"
                      :min="addForm.startDate"
                      @input="addEndDateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                  <v-select
                    v-model="addForm.endPeriod"
                    :items="periodOptions"
                    item-text="label"
                    item-value="value"
                    label="Run *"
                    prepend-inner-icon="mdi-weather-sunset-down"
                    outlined
                    dense
                    hide-details
                  ></v-select>
                </v-flex>
              </v-layout>

              <div
                v-if="addEnrollmentWarning"
                class="caption orange--text text--darken-2 mt-2"
              >
                <v-icon x-small color="orange darken-2" class="mr-1"
                  >mdi-alert</v-icon
                >
                {{ addEnrollmentWarning }}
              </div>
            </v-card>

            <!-- Destination -->
            <v-card outlined class="pa-3 mb-3 form-section-card">
              <div class="section-header mb-3">
                <v-icon small class="mr-1">mdi-map-marker</v-icon>
                Where To?
              </div>
              <v-layout row wrap class="mb-2">
                <v-flex xs12 sm6 class="pr-2">
                  <v-text-field
                    v-model="addForm.busChangePhoneNumber"
                    label="Phone Number"
                    prepend-inner-icon="mdi-phone"
                    outlined
                    dense
                    hide-details
                    mask="###-###-####"
                    :rules="[rules.phoneNumber]"
                    autocomplete="no-password"
                    @input="onAddDestinationPhoneInput"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 class="pl-2">
                  <v-text-field
                    v-model="addForm.busChangeName"
                    label="Family Name"
                    prepend-inner-icon="mdi-account"
                    outlined
                    dense
                    hide-details
                    autocomplete="no-password"
                  ></v-text-field>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex xs12 sm6 class="pr-2">
                  <v-combobox
                    v-model="addForm.busChangeAddress"
                    :items="addAddressSuggestions"
                    :search-input.sync="addAddressSearchText"
                    label="Destination Address *"
                    prepend-inner-icon="mdi-map-marker"
                    outlined
                    dense
                    clearable
                    hide-details
                    :loading="addAddressSearchLoading"
                    :filter="addAddressNoFilter"
                    autocomplete="no-password"
                    @update:searchInput="onAddAddressSearchInput"
                    @change="onAddBusChangeAddressChange"
                  ></v-combobox>
                </v-flex>
                <v-flex xs12 sm6 class="pl-2">
                  <v-combobox
                    v-model="addForm.geoPhoneNumber"
                    :items="addGeoPhoneSelectItems"
                    item-text="text"
                    item-value="value"
                    label="Geo Tracking Phone"
                    prepend-inner-icon="mdi-phone-in-talk"
                    outlined
                    dense
                    clearable
                    hide-details
                    :rules="[rules.phoneNumber]"
                    autocomplete="no-password"
                    @change="onAddGeoPhoneChange"
                  ></v-combobox>
                </v-flex>
              </v-layout>

              <!-- Notification (compact summary + edit) -->
              <div class="add-notification-row mt-2">
                <template v-if="!addNotificationEditing">
                  <div class="caption grey--text add-notification-summary">
                    <v-icon x-small class="mr-1">mdi-bell-outline</v-icon>
                    <span v-if="addForm.notificationPhoneNumber">
                      Notify
                      {{ formatPhone(addForm.notificationPhoneNumber) }}
                      <span v-if="addForm.notificationType">
                        ·
                        {{
                          addForm.notificationType === "CALL"
                            ? "Call"
                            : "Text message"
                        }}
                      </span>
                    </span>
                    <span v-else>No notification set</span>
                    <v-btn
                      icon
                      x-small
                      class="ml-1 my-0"
                      title="Edit notification"
                      @click="startAddNotificationEdit"
                    >
                      <v-icon x-small>mdi-pencil</v-icon>
                    </v-btn>
                  </div>
                </template>
                <template v-else>
                  <v-layout row wrap align-center>
                    <v-flex xs12 sm5 class="pr-2">
                      <v-combobox
                        v-model="addForm.notificationPhoneNumber"
                        :items="addNotificationPhoneSelectItems"
                        :search-input.sync="addNotificationPhoneSearch"
                        item-text="text"
                        item-value="value"
                        label="Notification Phone"
                        prepend-inner-icon="mdi-bell"
                        outlined
                        dense
                        clearable
                        hide-details
                        :rules="[rules.phoneNumber]"
                        autocomplete="no-password"
                        @change="onAddNotificationPhoneEdited"
                        @update:searchInput="onAddNotificationPhoneTyping"
                      ></v-combobox>
                    </v-flex>
                    <v-flex xs12 sm5 class="pr-2">
                      <v-select
                        v-model="addForm.notificationType"
                        :items="addNotificationTypeOptions"
                        item-text="text"
                        item-value="value"
                        label="Type"
                        prepend-inner-icon="mdi-message-alert"
                        outlined
                        dense
                        clearable
                        hide-details
                        @change="onAddNotificationPhoneEdited"
                        @input="onAddNotificationPhoneEdited"
                      ></v-select>
                    </v-flex>
                    <v-flex xs12 sm2 class="text-xs-right">
                      <v-btn
                        icon
                        small
                        dark
                        class="my-0"
                        color="grey darken-1 white--text"
                        title="Cancel"
                        @click="cancelAddNotificationEdit"
                      >
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        small
                        dark
                        class="my-0"
                        color="success"
                        title="Done"
                        @click="doneAddNotificationEdit"
                      >
                        <v-icon small>mdi-check</v-icon>
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </template>
              </div>
            </v-card>

            <!-- Bus -->
            <v-card outlined class="pa-3 mb-1 form-section-card">
              <div class="section-header mb-3">
                <v-icon small class="mr-1">mdi-bus</v-icon>
                Bus
              </div>
              <div
                v-if="addSiblingsHaveDifferentGrades"
                class="sibling-grade-route-note mb-2"
              >
                <v-alert
                  v-if="!addHasSharedGradeRoutes && !addSiblingsShareSameBus"
                  type="warning"
                  outline
                  dense
                  class="mb-2"
                >
                  These students are in different grades ({{
                    addDistinctSelectedGrades.join(", ")
                  }}) and no bus is set up for all of them. Enter them
                  separately, or check below if they ride the same bus.
                </v-alert>
                <v-alert
                  v-else-if="addHasSharedGradeRoutes && !addSiblingsShareSameBus"
                  type="info"
                  outline
                  dense
                  class="mb-2"
                >
                  Showing buses that serve all selected grades ({{
                    addDistinctSelectedGrades.join(", ")
                  }}).
                </v-alert>
                <v-alert
                  v-else
                  type="info"
                  outline
                  dense
                  class="mb-2"
                >
                  Showing buses for any of their grades ({{
                    addDistinctSelectedGrades.join(", ")
                  }}).
                </v-alert>
                <v-checkbox
                  v-model="addSiblingsShareSameBus"
                  label="They ride the same bus even though they are in different grades"
                  hide-details
                  class="mt-0 pt-0 sibling-share-bus-check"
                  color="rgb(92, 184, 96)"
                ></v-checkbox>
              </div>
              <div v-if="addIsAfternoonOnly" class="caption grey--text mb-2">
                <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                Afternoon-only change — select dropoff route and stop.
              </div>
              <v-layout row wrap>
                <v-flex v-if="!addIsAfternoonOnly" xs12 sm6 class="pr-2">
                  <bus-route-picker
                    label="Pickup Bus"
                    :value="addSelectedPickupRoute"
                    :routes="addMainPickupRoutes"
                    :extra-groups="addPickupExtraGroups"
                    :color-map="busRouteColorMap"
                    :building="
                      (addSelectedStudent && addSelectedStudent.building) || ''
                    "
                    :loading="addLoadingBusRoutes"
                    @select="selectAddPickupRoute"
                    @clear="clearAddPickupSelection"
                  ></bus-route-picker>
                  <div
                    v-if="addSelectedPickupRoute"
                    class="geo-stop-picker mt-2"
                  >
                    <v-autocomplete
                      v-model="addSelectedPickupStopNumber"
                      :items="addPickupRouteStops"
                      item-text="label"
                      item-value="stopNumber"
                      label="Pickup Stop"
                      prepend-inner-icon="mdi-map-marker-radius"
                      outlined
                      dense
                      clearable
                      hide-details
                      :loading="addLoadingPickupStops"
                      :disabled="addLoadingPickupStops"
                      placeholder="Select stop on this bus..."
                      :menu-props="{
                        bottom: true,
                        offsetY: true,
                        maxHeight: 320,
                      }"
                      @change="onAddPickupStopNumberChange"
                    >
                      <template v-slot:selection="{ item }">
                        <span class="geo-stop-selection">
                          <span class="geo-stop-badge">{{
                            item.stopNumber
                          }}</span>
                          <span class="geo-stop-address">{{
                            item.stopAddress || item.label
                          }}</span>
                        </span>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-tile-content>
                          <v-list-tile-title class="geo-stop-item-title">
                            <span class="geo-stop-badge">{{
                              item.stopNumber
                            }}</span>
                            {{ item.stopAddress }}
                          </v-list-tile-title>
                        </v-list-tile-content>
                      </template>
                    </v-autocomplete>
                  </div>
                </v-flex>
                <v-flex
                  xs12
                  :sm6="!addIsAfternoonOnly"
                  :class="addIsAfternoonOnly ? '' : 'pl-2'"
                >
                  <bus-route-picker
                    label="Dropoff Bus"
                    :value="addSelectedDropoffRoute"
                    :routes="addMainDropoffRoutes"
                    :extra-groups="addDropoffExtraGroups"
                    :color-map="busRouteColorMap"
                    :building="
                      (addSelectedStudent && addSelectedStudent.building) || ''
                    "
                    :loading="addLoadingBusRoutes"
                    @select="selectAddDropoffRoute"
                    @clear="clearAddDropoffSelection"
                  ></bus-route-picker>
                  <div
                    v-if="addSelectedDropoffRoute"
                    class="geo-stop-picker mt-2"
                  >
                    <v-autocomplete
                      v-model="addSelectedDropoffStopNumber"
                      :items="addDropoffRouteStops"
                      item-text="label"
                      item-value="stopNumber"
                      label="Dropoff Stop"
                      prepend-inner-icon="mdi-map-marker-radius"
                      outlined
                      dense
                      clearable
                      hide-details
                      :loading="addLoadingDropoffStops"
                      :disabled="addLoadingDropoffStops"
                      placeholder="Select stop on this bus..."
                      :menu-props="{
                        bottom: true,
                        offsetY: true,
                        maxHeight: 320,
                      }"
                    >
                      <template v-slot:selection="{ item }">
                        <span class="geo-stop-selection">
                          <span class="geo-stop-badge">{{
                            item.stopNumber
                          }}</span>
                          <span class="geo-stop-address">{{
                            item.stopAddress || item.label
                          }}</span>
                        </span>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-tile-content>
                          <v-list-tile-title class="geo-stop-item-title">
                            <span class="geo-stop-badge">{{
                              item.stopNumber
                            }}</span>
                            {{ item.stopAddress }}
                          </v-list-tile-title>
                        </v-list-tile-content>
                      </template>
                    </v-autocomplete>
                  </div>
                </v-flex>
                <v-flex xs12 class="mt-3">
                  <v-btn
                    v-if="!addShowSpecialDayPicker && !addSelectedSpecialDayRoute"
                    small
                    outline
                    color="grey darken-1"
                    class="ma-0"
                    @click="addShowSpecialDayPicker = true"
                  >
                    <v-icon left small>mdi-calendar-star</v-icon>
                    Add Friday / special schedule bus
                  </v-btn>
                  <v-layout v-else row wrap>
                    <v-flex xs12 sm6 class="pr-2">
                      <bus-route-picker
                        label="Friday / Special Schedule Bus"
                        :value="addSelectedSpecialDayRoute"
                        :sections="addSpecialDaySections"
                        list-mode
                        :color-map="busRouteColorMap"
                        :building="
                          (addSelectedStudent && addSelectedStudent.building) ||
                            ''
                        "
                        :loading="addLoadingBusRoutes"
                        @select="addSelectedSpecialDayRoute = $event"
                        @clear="addSelectedSpecialDayRoute = null"
                      ></bus-route-picker>
                    </v-flex>
                    <v-flex xs12 sm6 class="pl-2 caption grey--text">
                      Optional. Used on Fridays and other special schedule days instead of the
                      regular buses. Prints on reports; drivers do not get a
                      notification for this bus.
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-card>
          </template>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="py-3 pr-3">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="closeAddBusChangeDialog">
            Cancel
          </v-btn>
          <v-btn
            color="success"
            :dark="!!addSelectedStudent"
            :loading="addSaving"
            :disabled="!addSelectedStudent"
            @click="saveAddBusChange"
            class="save-btn"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Bus Change
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Destination phone → matching account popup -->
    <v-dialog v-model="addDestinationPhoneDialog" persistent max-width="560">
      <v-card>
        <v-card-title class="dialog-header py-2">
          <v-icon class="mr-3 pt-1" color="white" size="24"
            >mdi-account-search</v-icon
          >
          <span class="dialog-title">Matching Account Found</span>
          <v-spacer></v-spacer>
          <v-btn
            class="white--text my-0"
            icon
            dark
            @click="declineAddDestinationAccount"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-3">
          <div class="caption grey--text mb-2">
            Select an account to fill Family Name and Destination Address, or
            decline to keep what you typed.
          </div>
          <div class="account-list-container">
            <v-list dense class="account-list">
              <v-list-tile
                v-for="account in addDestinationPhoneAccounts"
                :key="account.accountID"
                @click="selectAddDestinationAccount(account)"
                class="account-item"
              >
                <v-list-tile-avatar>
                  <v-icon color="rgb(92, 184, 96)">mdi-account-box</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>
                  <v-list-tile-title>
                    <strong>{{ account.name }}</strong>
                    <span v-if="account.hname" class="grey--text ml-2"
                      >({{ account.hname }})</span
                    >
                  </v-list-tile-title>
                  <v-list-tile-sub-title>
                    <span v-if="account.address1">{{ account.address1 }}</span>
                    <span v-if="account.homePhone || account.cellPhone">
                      ·
                      {{ formatPhone(account.homePhone || account.cellPhone) }}
                    </span>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
                <v-list-tile-action>
                  <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="py-2 pr-3">
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            dark
            @click="declineAddDestinationAccount"
          >
            Don't Use
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- End Bus Change Dialog -->
    <v-dialog
      persistent
      scrollable
      v-model="endBusChangeDialog"
      max-width="720"
    >
      <v-card class="request-dialog">
        <v-card-title class="dialog-header py-2">
          <v-icon class="mr-3 pt-1" color="white" size="24"
            >mdi-calendar-remove</v-icon
          >
          <span class="dialog-title">End Bus Change</span>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!endSelectedBusChange"
            flat
            small
            class="end-bc-overdue-filter my-0 mr-1"
            :class="{ 'end-bc-overdue-filter-on': endBusChangeOverdueOnly }"
            @click="endBusChangeOverdueOnly = !endBusChangeOverdueOnly"
          >
            <v-icon left small>mdi-alert</v-icon>
            Overdue
            <span class="ml-1">{{ overdueOpenExtendedCount }}</span>
          </v-btn>
          <v-btn
            v-if="
              !endSelectedBusChange &&
                endBusChangeOverdueOnly &&
                overdueBusChangesForPrint.length > 0
            "
            class="white--text my-0 mr-1"
            icon
            dark
            title="Download overdue report"
            :loading="printingOverdueList"
            @click="printOverdueList"
          >
            <v-icon>mdi-download</v-icon>
          </v-btn>
          <v-btn
            class="white--text my-0"
            icon
            dark
            @click="closeEndBusChangeDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-3 dialog-body">
          <template v-if="!endSelectedBusChange">
            <v-text-field
              v-model="endBusChangeSearch"
              label="Search by name, student ID, or phone"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              clearable
              hide-details
              autocomplete="no-password"
              name="end-bus-change-search"
              class="mb-2"
            ></v-text-field>
            <div
              v-if="filteredOpenExtendedBusChanges.length > 0"
              class="caption grey--text mb-2"
            >
              {{ filteredOpenExtendedBusChanges.length }}
              {{ endBusChangeOverdueOnly ? "overdue" : "open extended" }}
              {{
                filteredOpenExtendedBusChanges.length === 1
                  ? "change"
                  : "changes"
              }}
              <span
                v-if="!endBusChangeOverdueOnly && overdueOpenExtendedCount > 0"
              >
                · {{ overdueOpenExtendedCount }} overdue
              </span>
            </div>
            <div
              v-if="filteredOpenExtendedBusChanges.length === 0"
              class="simple-empty py-4"
            >
              <v-icon large color="grey lighten-2">mdi-bus</v-icon>
              <div class="mt-1 grey--text">
                {{
                  endBusChangeOverdueOnly
                    ? "No overdue bus changes found."
                    : "No open extended bus changes found."
                }}
              </div>
            </div>
            <div v-else class="end-bc-list">
              <div
                v-for="bc in filteredOpenExtendedBusChanges"
                :key="'end-bc-' + (bc.id || bc.ID)"
                class="end-bc-item"
                :class="{ 'end-bc-item-overdue': isLongRunningBusChange(bc) }"
                @click="selectEndBusChange(bc)"
              >
                <v-avatar color="rgb(92, 184, 96)" size="36" class="mr-3">
                  <v-icon dark size="20">mdi-account</v-icon>
                </v-avatar>
                <div class="end-bc-item-body">
                  <div
                    v-if="isLongRunningBusChange(bc)"
                    class="overdue-banner"
                  >
                    <v-icon x-small class="mr-1">mdi-alert</v-icon>
                    Overdue · {{ getLongRunningLabel(bc) }}
                  </div>
                  <div class="end-bc-item-title">
                    <span
                      v-if="bc.id || bc.ID"
                      class="end-bc-item-id caption grey--text"
                    >
                      #{{ trimId(bc.id || bc.ID) }}
                    </span>
                    {{
                      bc.studentNameHeb || bc.studentName || "Unknown Student"
                    }}
                    <span v-if="bc.studentID" class="caption grey--text ml-1">
                      · Student #{{ trimId(bc.studentID) }}
                    </span>
                  </div>
                  <div
                    v-if="bc.studentNameHeb && bc.studentName"
                    class="end-bc-item-eng"
                  >
                    {{ bc.studentName }}
                  </div>
                  <div class="end-bc-item-meta">
                    <span class="end-bc-meta-chip">
                      <v-icon x-small>mdi-calendar-clock</v-icon>
                      {{ formatCardDateRange(bc) }}
                    </span>
                    <span v-if="bc.busChangeAddress" class="end-bc-meta-chip">
                      <v-icon x-small>mdi-map-marker</v-icon>
                      {{ bc.busChangeAddress }}
                    </span>
                  </div>
                  <div
                    v-if="getEndBusChangeName(bc) || getEndBusChangePhone(bc)"
                    class="end-bc-item-meta"
                  >
                    <span
                      v-if="getEndBusChangeName(bc)"
                      class="end-bc-meta-chip"
                    >
                      <v-icon x-small>mdi-account</v-icon>
                      {{ getEndBusChangeName(bc) }}
                    </span>
                    <span
                      v-if="getEndBusChangePhone(bc)"
                      class="end-bc-meta-chip"
                    >
                      <v-icon x-small>mdi-phone</v-icon>
                      {{ formatPhone(getEndBusChangePhone(bc)) }}
                    </span>
                  </div>
                </div>
                <v-icon color="grey lighten-1" class="end-bc-item-chevron"
                  >mdi-chevron-right</v-icon
                >
              </div>
            </div>
          </template>

          <template v-else>
            <v-card outlined class="pa-3 mb-3 form-section-card">
              <div class="section-header mb-2">
                <v-icon small class="mr-1">mdi-account</v-icon>
                Selected Bus Change
              </div>
              <div
                v-if="isLongRunningBusChange(endSelectedBusChange)"
                class="overdue-banner mb-2"
              >
                <v-icon x-small class="mr-1">mdi-alert</v-icon>
                Overdue · {{ getLongRunningLabel(endSelectedBusChange) }}
              </div>
              <v-layout align-center class="mb-2">
                <v-avatar color="rgb(92, 184, 96)" size="36" class="mr-2">
                  <v-icon dark size="20">mdi-account</v-icon>
                </v-avatar>
                <div>
                  <div class="font-weight-medium">
                    {{
                      endSelectedBusChange.studentNameHeb ||
                        endSelectedBusChange.studentName ||
                        "Unknown Student"
                    }}
                    <span
                      v-if="endSelectedBusChange.studentID"
                      class="caption grey--text"
                    >
                      #{{ trimId(endSelectedBusChange.studentID) }}
                    </span>
                  </div>
                  <div
                    v-if="
                      endSelectedBusChange.studentNameHeb &&
                        endSelectedBusChange.studentName
                    "
                    class="caption grey--text"
                  >
                    {{ endSelectedBusChange.studentName }}
                  </div>
                </div>
              </v-layout>
              <div class="card-info-line">
                <v-icon x-small class="card-line-icon"
                  >mdi-calendar-clock</v-icon
                >
                <span>{{ formatCardDateRange(endSelectedBusChange) }}</span>
              </div>
              <div
                v-if="endSelectedBusChange.busChangeAddress"
                class="card-info-line"
              >
                <v-icon x-small class="card-line-icon">mdi-map-marker</v-icon>
                <span>{{ endSelectedBusChange.busChangeAddress }}</span>
              </div>
              <div
                v-if="
                  getEndBusChangeName(endSelectedBusChange) ||
                    getEndBusChangePhone(endSelectedBusChange)
                "
                class="card-info-line"
              >
                <template v-if="getEndBusChangeName(endSelectedBusChange)">
                  <v-icon x-small class="card-line-icon">mdi-account</v-icon>
                  <span class="mr-3">{{
                    getEndBusChangeName(endSelectedBusChange)
                  }}</span>
                </template>
                <template v-if="getEndBusChangePhone(endSelectedBusChange)">
                  <v-icon x-small class="card-line-icon">mdi-phone</v-icon>
                  <span>{{
                    formatPhone(getEndBusChangePhone(endSelectedBusChange))
                  }}</span>
                </template>
              </div>
              <v-btn
                flat
                small
                color="success"
                class="mt-2 ml-0"
                @click="clearEndBusChangeSelection"
              >
                <v-icon left small>mdi-swap-horizontal</v-icon>
                Choose a different bus change
              </v-btn>
            </v-card>

            <v-card outlined class="pa-3 form-section-card">
              <div class="section-header mb-2">
                <v-icon small class="mr-1">mdi-bus</v-icon>
                Return Bus
              </div>
              <bus-route-picker
                label="Return Bus"
                :show-label="false"
                :value="endSelectedReturnRoute"
                :routes="endFilteredReturnRoutes"
                :extra-groups="endReturnExtraGroups"
                :color-map="busRouteColorMap"
                :building="
                  (endSelectedBusChange &&
                    (endSelectedBusChange.building ||
                      endSelectedBusChange.Building)) ||
                    ''
                "
                :loading="endLoadingBusRoutes"
                @select="endSelectedReturnRoute = $event"
                @clear="clearEndReturnSelection"
              ></bus-route-picker>
            </v-card>
          </template>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="py-3 pr-2">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="closeEndBusChangeDialog">
            Cancel
          </v-btn>
          <v-btn
            color="warning"
            dark
            class="end-bus-change-btn"
            :loading="endSaving"
            :disabled="!canSaveEndBusChange"
            @click="saveEndBusChange"
          >
            <v-icon left small>mdi-calendar-remove</v-icon>
            End Bus Change
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Process Request Dialog -->
    <v-dialog persistent v-model="processDialog" max-width="1100">
      <v-card class="request-dialog">
        <v-card-title class="dialog-header py-2">
          <v-icon class="mr-3 pt-1" color="white" size="24"
            >mdi-message-alert</v-icon
          >
          <span class="dialog-title">Process Bus Change Request</span>
          <v-spacer></v-spacer>
          <v-btn class="white--text my-0" icon dark @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-3 dialog-body">
          <!-- Three Column Layout -->
          <v-layout row wrap>
            <!-- Left Column: Student Info -->
            <v-flex xs12 md3 class="pr-2">
              <v-card
                v-if="getRecordingUrl"
                outlined
                class="pa-2 original-request-card mb-2"
              >
                <div class="recording-header">
                  <v-icon small color="#ff6b6b" class="recording-icon"
                    >mdi-microphone</v-icon
                  >
                  <span class="recording-label">Original Request</span>
                </div>
                <audio
                  ref="audioPlayer"
                  controls
                  :src="getRecordingUrl"
                  class="inline-audio-player mt-1"
                ></audio>
              </v-card>
              <v-card outlined class="pa-2 compact-info" v-if="selectedRequest">
                <div class="section-header mb-2">
                  <v-icon x-small class="mr-1">mdi-account</v-icon>
                  Info
                </div>
                <div class="info-row">
                  <span class="info-label">Name:</span>
                  <span class="info-value">{{
                    selectedRequest.studentName
                  }}</span>
                </div>
                <div class="info-row" v-if="selectedRequest.studentNameHeb">
                  <span class="info-label">Hebrew:</span>
                  <span class="info-value">{{
                    selectedRequest.studentNameHeb
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Student ID:</span>
                  <span class="info-value">{{
                    selectedRequest.studentID
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Source:</span>
                  <span class="info-value">{{
                    selectedRequest.source || selectedRequest.Source || "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Caller:</span>
                  <span class="info-value">{{
                    formatPhone(
                      selectedRequest.callerID || selectedRequest.CallerID
                    ) || callerFallback
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Requested:</span>
                  <span class="info-value">{{
                    formatDate(selectedRequest.date || selectedRequest.Date) ||
                      "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">{{
                    dayCampEnrollment
                      ? "Building/Grade/Room:"
                      : "Building/Grade/Section:"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-value info-value-full">
                    {{
                      (dayCampEnrollment && dayCampEnrollment.building) ||
                        selectedRequest.building
                    }}
                    -
                    {{
                      (dayCampEnrollment && dayCampEnrollment.class) ||
                        selectedRequest.class
                    }}
                    -
                    {{
                      dayCampEnrollment
                        ? dayCampEnrollment.room || "-"
                        : selectedRequest.section
                    }}
                  </span>
                </div>
                <div
                  class="info-row"
                  v-if="dayCampEnrollment && getDcTeacher(dayCampEnrollment)"
                >
                  <span class="info-label">Teacher:</span>
                  <span class="info-value">{{
                    getDcTeacher(dayCampEnrollment)
                  }}</span>
                </div>
                <div
                  class="info-row"
                  v-else-if="
                    !dayCampEnrollment &&
                      (selectedRequest.teacherH || selectedRequest.teacherH2)
                  "
                >
                  <span class="info-label">Teacher:</span>
                  <span class="info-value">{{
                    getTeacherDisplay(selectedRequest)
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Home:</span>
                  <span class="info-value">{{
                    selectedRequest.homePhone || "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Cell:</span>
                  <span class="info-value">{{
                    selectedRequest.cellPhone || "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Bus Addr:</span>
                  <span class="info-value">{{
                    selectedRequest.busAddress1 || "-"
                  }}</span>
                </div>
                <div
                  class="info-row"
                  v-if="selectedRequest.notificationPhoneNumber"
                >
                  <span class="info-label">Notify:</span>
                  <span class="info-value">
                    {{ formatPhone(selectedRequest.notificationPhoneNumber) }}
                    <span
                      class="grey--text"
                      v-if="selectedRequest.notificationType"
                    >
                      ({{ selectedRequest.notificationType }})
                    </span>
                  </span>
                </div>
                <div
                  class="info-row"
                  v-if="dayCampEnrollment && dayCampEnrollment.regType"
                >
                  <span class="info-label">Day Camp:</span>
                  <span class="info-value info-value-full">{{
                    getEnrollmentDisplay(dayCampEnrollment)
                  }}</span>
                </div>
              </v-card>
              <v-card outlined class="pa-2 mt-2 form-section-card">
                <div class="d-flex align-center mb-2">
                  <v-spacer></v-spacer>
                  <v-btn
                    v-if="editingExtraInfo"
                    small
                    outline
                    color="grey darken-1"
                    class="my-0 mr-1"
                    style="min-width: 0; padding: 0 8px;"
                    @click="cancelExtraInfoEdit"
                  >
                    <v-icon left small>mdi-close</v-icon>
                    Cancel
                  </v-btn>
                  <v-btn
                    small
                    outline
                    :color="editingExtraInfo ? 'success' : 'grey darken-1'"
                    class="my-0 ml-0"
                    style="min-width: 0; padding: 0 8px;"
                    @click="toggleExtraInfoEdit"
                  >
                    <v-icon left small>{{
                      editingExtraInfo ? "mdi-check" : "mdi-pencil"
                    }}</v-icon>
                    {{ editingExtraInfo ? "Done" : "Edit" }}
                  </v-btn>
                </div>
                <div v-if="!editingExtraInfo">
                  <div class="info-row">
                    <span class="info-label">Geo Phone:</span>
                    <span class="info-value">{{
                      formData.geoPhoneNumber || "-"
                    }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Note:</span>
                    <span class="info-value info-value-full">{{
                      formData.note || "-"
                    }}</span>
                  </div>
                </div>
                <div v-else>
                  <v-text-field
                    v-model="formData.geoPhoneNumber"
                    label="Geo Tracking Phone"
                    prepend-inner-icon="mdi-phone-in-talk"
                    outlined
                    dense
                    hide-details
                    class="mb-2"
                    :rules="[rules.phoneNumber]"
                    mask="###-###-####"
                    autocomplete="off"
                  ></v-text-field>
                  <v-textarea
                    v-model="formData.note"
                    label="Note"
                    prepend-inner-icon="mdi-note-text"
                    outlined
                    dense
                    hide-details
                    rows="2"
                  ></v-textarea>
                </div>
              </v-card>
            </v-flex>

            <!-- Middle Column: Bus Change Form -->
            <v-flex xs12 md6 class="px-2">
              <!-- Dates -->
              <v-card outlined class="pa-3 mb-3 form-section-card">
                <div class="section-header mb-2">
                  <v-icon small class="mr-1">mdi-calendar-range</v-icon>
                  Dates
                  <v-spacer></v-spacer>
                  <v-btn
                    small
                    color="success"
                    outline
                    class="my-0"
                    @click="setTodayOnlyAfternoon"
                  >
                    <v-icon left small>mdi-weather-sunset</v-icon>
                    Today Only Afternoon
                  </v-btn>
                </div>
                <v-layout row wrap>
                  <v-flex xs12 sm6 class="pr-2">
                    <div class="caption period-title mb-1">Starting Run</div>
                    <v-menu
                      v-model="startDateMenu"
                      :close-on-content-click="false"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="formData.startDate"
                          label="Start Date *"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          outlined
                          dense
                          hide-details
                          class="mb-2"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="formData.startDate"
                        color="rgb(92, 184, 96)"
                        @input="startDateMenu = false"
                      ></v-date-picker>
                    </v-menu>
                    <v-select
                      v-model="formData.startPeriod"
                      :items="periodOptions"
                      item-text="label"
                      item-value="value"
                      label="Run *"
                      prepend-inner-icon="mdi-weather-sunset-up"
                      outlined
                      dense
                      hide-details
                      :rules="[rules.required]"
                    ></v-select>
                  </v-flex>
                  <v-flex xs12 sm6 class="pl-2">
                    <div class="caption period-title mb-1">Ending After</div>
                    <v-menu
                      v-model="endDateMenu"
                      :close-on-content-click="false"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="formData.endDate"
                          label="End Date"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          outlined
                          dense
                          hide-details
                          class="mb-2"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="formData.endDate"
                        color="rgb(92, 184, 96)"
                        :min="formData.startDate"
                        @input="endDateMenu = false"
                      ></v-date-picker>
                    </v-menu>
                    <v-select
                      v-model="formData.endPeriod"
                      :items="periodOptions"
                      item-text="label"
                      item-value="value"
                      label="Run *"
                      prepend-inner-icon="mdi-weather-sunset-down"
                      outlined
                      dense
                      hide-details
                      :rules="[rules.required]"
                    ></v-select>
                  </v-flex>
                </v-layout>
                <div
                  class="caption grey--text text--darken-1 mt-3 mb-2 period-helper-text"
                >
                  Morning Pickup: Includes the trip TO school. Afternoon
                  Dropoff: Includes the trip FROM school.
                </div>
                <v-alert
                  :value="!!processGradeNotAllowedMessage"
                  type="error"
                  outline
                  dense
                  class="mb-2 pa-2"
                >
                  {{ processGradeNotAllowedMessage }}
                </v-alert>
                <v-alert
                  :value="!!deadlineWarningMessage"
                  type="warning"
                  outline
                  dense
                  class="mb-2 pa-2"
                >
                  {{ deadlineWarningMessage }}
                </v-alert>
                <v-alert
                  :value="!!enrollmentDateWarning"
                  type="warning"
                  outline
                  dense
                  class="mb-0 pa-2"
                >
                  {{ enrollmentDateWarning }}
                </v-alert>
              </v-card>

              <!-- Destination Info -->
              <v-card outlined class="pa-3 mb-3 form-section-card">
                <div class="section-header mb-3">
                  <v-icon small class="mr-1">mdi-map-marker</v-icon>
                  Destination Info
                </div>
                <v-text-field
                  v-model="formData.busChangeAddress"
                  label="Destination Address *"
                  prepend-inner-icon="mdi-map-marker"
                  outlined
                  dense
                  hide-details
                  class="mb-2"
                  autocomplete="off"
                ></v-text-field>
                <v-layout row wrap class="mb-2">
                  <v-flex xs12 sm8 class="pr-1">
                    <v-text-field
                      v-model="formData.busChangePhoneNumber"
                      label="Destination Phone"
                      prepend-inner-icon="mdi-phone"
                      outlined
                      dense
                      hide-details
                      autocomplete="off"
                      :rules="[rules.phoneNumber]"
                      mask="###-###-####"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm4 class="pl-1">
                    <v-btn
                      color="success"
                      outline
                      block
                      class="mt-0"
                      :disabled="!formData.busChangePhoneNumber"
                      @click="searchAccountByPhone"
                    >
                      <v-icon left small>mdi-magnify</v-icon>
                      Search
                    </v-btn>
                  </v-flex>
                </v-layout>
                <v-text-field
                  v-model="formData.busChangeName"
                  label="Family Name"
                  prepend-inner-icon="mdi-account"
                  outlined
                  dense
                  hide-details
                  autocomplete="off"
                ></v-text-field>
                <div
                  v-if="formData.busChangeAccountID"
                  class="caption grey--text mt-1"
                >
                  Account ID: {{ formData.busChangeAccountID }}
                </div>
              </v-card>

              <!-- Bus -->
              <v-card outlined class="pa-3 mb-1 form-section-card">
                <div class="section-header mb-3">
                  <v-icon small class="mr-1">mdi-bus</v-icon>
                  Bus
                </div>
                <v-layout row wrap>
                  <v-flex xs6 class="pr-1">
                    <v-autocomplete
                      v-model="selectedPickupRoute"
                      :items="filteredPickupRoutes"
                      :filter="customRouteFilter"
                      item-text="description"
                      item-value="id"
                      label="Pickup Route"
                      prepend-inner-icon="mdi-bus-clock"
                      outlined
                      dense
                      clearable
                      hide-details
                      :loading="loadingBusRoutes"
                      placeholder="Start typing to search..."
                      return-object
                      @change="onPickupRouteChange"
                      class="route-autocomplete"
                      :menu-props="{ bottom: true, offsetY: true }"
                    >
                      <template v-slot:selection="{ item }">
                        <div class="route-selection-wrapper">
                          <div class="route-selection-main">
                            {{ item.description }}
                            <span
                              v-if="getRouteBusNumber(item)"
                              class="route-detail-chip"
                              :style="getRouteChipStyle(item)"
                            >
                              Bus #{{ getRouteBusNumber(item) }}
                            </span>
                          </div>
                          <div
                            v-if="item.driver"
                            class="route-selection-driver"
                          >
                            <v-icon x-small>mdi-account</v-icon>
                            {{ item.driver }}
                          </div>
                        </div>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-tile-content
                          class="route-list-item"
                          :style="
                            getRouteColor(item.color)
                              ? `border-left: 4px solid ${getRouteColor(
                                  item.color
                                )}`
                              : ''
                          "
                        >
                          <v-list-tile-title>
                            <strong>{{ item.description }}</strong>
                          </v-list-tile-title>
                          <v-list-tile-sub-title>
                            <span v-if="getRouteBusNumber(item)" class="mr-2">
                              <v-icon x-small color="grey darken-1"
                                >mdi-bus</v-icon
                              >
                              Bus #{{ getRouteBusNumber(item) }}
                            </span>
                            <span v-if="item.driver" class="mr-2">
                              <v-icon x-small color="grey darken-1"
                                >mdi-account</v-icon
                              >
                              {{ item.driver }}
                            </span>
                            <span v-if="item.color" class="mr-2">
                              <v-icon
                                x-small
                                :style="
                                  getRouteColor(item.color)
                                    ? `color: ${getRouteColor(item.color)}`
                                    : ''
                                "
                                >mdi-circle</v-icon
                              >
                              {{ item.color }}
                            </span>
                          </v-list-tile-sub-title>
                        </v-list-tile-content>
                      </template>
                    </v-autocomplete>
                    <div
                      v-if="selectedPickupRoute"
                      class="geo-stop-picker mt-2"
                    >
                      <v-autocomplete
                        v-model="selectedPickupStopNumber"
                        :items="pickupRouteStops"
                        item-text="label"
                        item-value="stopNumber"
                        label="Pickup Geo Stop"
                        prepend-inner-icon="mdi-map-marker-radius"
                        outlined
                        dense
                        clearable
                        :loading="loadingPickupStops"
                        :disabled="loadingPickupStops"
                        placeholder="Select stop on this bus..."
                        :menu-props="{
                          bottom: true,
                          offsetY: true,
                          maxHeight: 320,
                        }"
                        :error="
                          !!selectedPickupRoute && !selectedPickupStopNumber
                        "
                        :hint="
                          selectedPickupStopNumber
                            ? 'Used for GeoTrack'
                            : 'Required for GeoTrack sync'
                        "
                        persistent-hint
                        @change="onPickupStopNumberChange"
                      >
                        <template v-slot:selection="{ item }">
                          <span class="geo-stop-selection">
                            <span class="geo-stop-badge">{{
                              item.stopNumber
                            }}</span>
                            <span class="geo-stop-address">{{
                              item.stopAddress || item.label
                            }}</span>
                          </span>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-tile-content>
                            <v-list-tile-title class="geo-stop-item-title">
                              <span class="geo-stop-badge">{{
                                item.stopNumber
                              }}</span>
                              {{ item.stopAddress }}
                            </v-list-tile-title>
                          </v-list-tile-content>
                        </template>
                      </v-autocomplete>
                    </div>
                  </v-flex>
                  <v-flex xs6 class="pl-1">
                    <v-autocomplete
                      v-model="selectedDropoffRoute"
                      :items="filteredDropoffRoutes"
                      :filter="customRouteFilter"
                      item-text="description"
                      item-value="id"
                      label="Dropoff Route"
                      prepend-inner-icon="mdi-bus-clock"
                      outlined
                      dense
                      clearable
                      hide-details
                      :loading="loadingBusRoutes"
                      placeholder="Start typing to search..."
                      return-object
                      @change="onDropoffRouteChange"
                      class="route-autocomplete"
                      :menu-props="{ bottom: true, offsetY: true }"
                    >
                      <template v-slot:selection="{ item }">
                        <div class="route-selection-wrapper">
                          <div class="route-selection-main">
                            {{ item.description }}
                            <span
                              v-if="getRouteBusNumber(item)"
                              class="route-detail-chip"
                              :style="getRouteChipStyle(item)"
                            >
                              Bus #{{ getRouteBusNumber(item) }}
                            </span>
                          </div>
                          <div
                            v-if="item.driver"
                            class="route-selection-driver"
                          >
                            <v-icon x-small>mdi-account</v-icon>
                            {{ item.driver }}
                          </div>
                        </div>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-tile-content
                          class="route-list-item"
                          :style="
                            getRouteColor(item.color)
                              ? `border-left: 4px solid ${getRouteColor(
                                  item.color
                                )}`
                              : ''
                          "
                        >
                          <v-list-tile-title>
                            <strong>{{ item.description }}</strong>
                          </v-list-tile-title>
                          <v-list-tile-sub-title>
                            <span v-if="getRouteBusNumber(item)" class="mr-2">
                              <v-icon x-small color="grey darken-1"
                                >mdi-bus</v-icon
                              >
                              Bus #{{ getRouteBusNumber(item) }}
                            </span>
                            <span v-if="item.driver" class="mr-2">
                              <v-icon x-small color="grey darken-1"
                                >mdi-account</v-icon
                              >
                              {{ item.driver }}
                            </span>
                            <span v-if="item.color" class="mr-2">
                              <v-icon
                                x-small
                                :style="
                                  getRouteColor(item.color)
                                    ? `color: ${getRouteColor(item.color)}`
                                    : ''
                                "
                                >mdi-circle</v-icon
                              >
                              {{ item.color }}
                            </span>
                          </v-list-tile-sub-title>
                        </v-list-tile-content>
                      </template>
                    </v-autocomplete>
                    <div
                      v-if="selectedDropoffRoute"
                      class="geo-stop-picker mt-2"
                    >
                      <v-autocomplete
                        v-model="selectedDropoffStopNumber"
                        :items="dropoffRouteStops"
                        item-text="label"
                        item-value="stopNumber"
                        label="Dropoff Geo Stop"
                        prepend-inner-icon="mdi-map-marker-radius"
                        outlined
                        dense
                        clearable
                        :loading="loadingDropoffStops"
                        :disabled="loadingDropoffStops"
                        placeholder="Select stop on this bus..."
                        :menu-props="{
                          bottom: true,
                          offsetY: true,
                          maxHeight: 320,
                        }"
                        :error="
                          !!selectedDropoffRoute && !selectedDropoffStopNumber
                        "
                        :hint="
                          selectedDropoffStopNumber
                            ? 'Used for GeoTrack'
                            : 'Required for GeoTrack sync'
                        "
                        persistent-hint
                      >
                        <template v-slot:selection="{ item }">
                          <span class="geo-stop-selection">
                            <span class="geo-stop-badge">{{
                              item.stopNumber
                            }}</span>
                            <span class="geo-stop-address">{{
                              item.stopAddress || item.label
                            }}</span>
                          </span>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-tile-content>
                            <v-list-tile-title class="geo-stop-item-title">
                              <span class="geo-stop-badge">{{
                                item.stopNumber
                              }}</span>
                              {{ item.stopAddress }}
                            </v-list-tile-title>
                          </v-list-tile-content>
                        </template>
                      </v-autocomplete>
                    </div>
                  </v-flex>
                </v-layout>
              </v-card>
            </v-flex>

            <!-- Right Column: Parent Messages -->
            <v-flex xs12 md3 class="pl-2">
              <div
                v-if="
                  isPendingRequest(selectedRequest) &&
                    busChangeComments.length > 0
                "
                class="pending-request-indicator mb-2"
              >
                Last request is awaiting action.
              </div>
              <v-card outlined class="pa-2 comments-card">
                <div class="section-header mb-2">
                  <v-icon x-small class="mr-1">mdi-message-text</v-icon>
                  Messages
                </div>
                <div
                  v-if="loadingBusChangeComments"
                  class="d-flex align-center grey--text text--darken-1"
                >
                  <v-progress-circular
                    size="18"
                    width="2"
                    indeterminate
                    color="success"
                    class="mr-2"
                  ></v-progress-circular>
                  Loading...
                </div>
                <div
                  v-else-if="busChangeComments.length === 0"
                  class="grey--text text--darken-1"
                >
                  No messages.
                </div>
                <div v-else>
                  <div
                    v-for="(comment, index) in busChangeComments"
                    :key="comment.id || comment.ID || comment.callUUID || index"
                    class="comment-entry"
                  >
                    <div class="comment-meta">
                      <span class="font-weight-medium">
                        {{ getCommentEnteredBy(comment) || "Parent" }}
                      </span>
                      <span class="mx-1">-</span>
                      <span>{{
                        formatDate(getCommentEnteredOn(comment))
                      }}</span>
                    </div>
                    <div
                      v-if="
                        isCommentAwaitingResponse(
                          comment,
                          index,
                          busChangeComments
                        )
                      "
                      class="comment-awaiting mt-1"
                    >
                      Action required
                    </div>
                    <div
                      v-if="getCommentCallerID(comment)"
                      class="comment-caller grey--text text--darken-1"
                    >
                      <v-icon x-small class="mr-1">mdi-phone</v-icon>
                      {{ formatPhone(getCommentCallerID(comment)) }}
                    </div>
                    <div
                      v-if="getCommentText(comment)"
                      class="comment-text grey--text text--darken-3"
                    >
                      {{ getCommentText(comment) }}
                    </div>
                    <audio
                      v-if="getCommentRecordingUrl(comment)"
                      controls
                      controlsList="nodownload"
                      :src="getCommentRecordingUrl(comment)"
                      class="comment-audio mt-2"
                    ></audio>
                  </div>
                </div>
              </v-card>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="py-3">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="closeDialog">
            Cancel
          </v-btn>
          <v-btn
            v-if="isPendingRequest(selectedRequest)"
            color="error"
            dark
            :loading="deletingRequest"
            :disabled="saving"
            @click="promptDeleteRequest"
          >
            <v-icon left>mdi-delete</v-icon>
            Delete Request
          </v-btn>
          <v-btn
            color="success"
            dark
            :loading="saving"
            :disabled="deletingRequest || !!processGradeNotAllowedMessage"
            @click="saveRequest"
            class="save-btn"
          >
            <v-icon left>mdi-content-save</v-icon>
            Complete & Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Account Search Dialog -->
    <v-dialog v-model="accountSearchDialog" max-width="800">
      <v-card>
        <v-card-title class="dialog-header py-2">
          <v-icon class="mr-3 pt-1" color="white" size="24"
            >mdi-account-search</v-icon
          >
          <span class="dialog-title">Search Account</span>
          <v-spacer></v-spacer>
          <v-btn
            class="white--text my-0"
            icon
            dark
            @click="closeAccountSearchDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Phone Number Search -->
          <v-layout row wrap class="mb-1">
            <v-flex xs12 class="pr-2">
              <v-text-field
                v-model="dialogPhoneSearch"
                label="Search by Phone Number"
                prepend-inner-icon="mdi-phone"
                outlined
                dense
                clearable
                mask="###-###-####"
                @input="searchDialogByPhone"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Search Name Mode Toggle -->
          <v-layout row wrap align-center class="mb-2">
            <v-flex xs12 class="pr-2">
              <div class="caption grey--text mb-1">Or Search By Name</div>
              <v-btn-toggle
                v-model="dialogSearchNameMode"
                mandatory
                class="search-toggle"
              >
                <v-btn flat small value="yiddish">אידיש</v-btn>
                <v-btn flat small value="english">English</v-btn>
              </v-btn-toggle>
            </v-flex>
          </v-layout>

          <!-- Account Search Fields -->
          <v-layout row wrap class="mb-3">
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field
                v-model="dialogLastNameSearch"
                :label="
                  dialogSearchNameMode === 'yiddish'
                    ? 'לעצטע נאמען'
                    : 'Last Name'
                "
                prepend-inner-icon="mdi-account"
                outlined
                dense
                clearable
                @input="searchDialogByName"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="dialogFirstNameSearch"
                :label="
                  dialogSearchNameMode === 'yiddish'
                    ? 'טאטעס נאמען'
                    : 'Father Name'
                "
                prepend-inner-icon="mdi-account"
                outlined
                dense
                clearable
                @input="searchDialogByName"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Address Search -->
          <v-layout row wrap class="mb-2">
            <v-flex xs12>
              <div class="caption grey--text mb-1">Or Search By Address</div>
            </v-flex>
          </v-layout>
          <v-layout row wrap class="mb-3">
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field
                v-model="dialogHouseNumberSearch"
                label="House Number"
                prepend-inner-icon="mdi-home"
                outlined
                dense
                clearable
                @input="searchDialogByAddress"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="dialogStreetSearch"
                label="Street Name"
                prepend-inner-icon="mdi-road"
                outlined
                dense
                clearable
                @input="searchDialogByAddress"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Account List -->
          <div class="account-list-container" v-if="dialogAccounts.length > 0">
            <div class="caption grey--text my-2 px-2">
              Select an account ({{ dialogAccounts.length }} found)
            </div>
            <v-list dense class="account-list">
              <v-list-tile
                v-for="account in dialogAccounts"
                :key="account.accountID"
                @click="onAccountSelected(account)"
                class="account-item"
              >
                <v-list-tile-avatar>
                  <v-icon color="rgb(92, 184, 96)">mdi-account-box</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>
                  <v-list-tile-title>
                    <strong>{{ account.name }}</strong>
                    <span class="grey--text ml-2">({{ account.hname }})</span>
                  </v-list-tile-title>
                  <v-list-tile-sub-title>
                    Account ID: {{ account.accountID }}
                    <span v-if="account.homePhone" class="ml-1">
                      | {{ account.homePhone }}
                    </span>
                    <span v-if="account.cellPhone" class="ml-1">
                      | {{ account.cellPhone }}
                    </span>
                    <span v-if="account.address1" class="ml-1">
                      | {{ account.address1 }}
                    </span>
                    <span v-if="account.busAddress1" class="ml-1">
                      | Bus: {{ account.busAddress1 }}
                    </span>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
                <v-list-tile-action>
                  <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
          </div>

          <!-- No Accounts Message -->
          <v-alert
            v-if="dialogShowNoAccountsMessage && dialogAccounts.length === 0"
            type="warning"
            outline
            dense
            value="true"
            class="mb-3"
          >
            No accounts found. Please try a different search.
          </v-alert>

          <!-- Empty state -->
          <div
            v-if="!dialogShowNoAccountsMessage && dialogAccounts.length === 0"
            class="text-center px-2 pb-3 grey--text"
          >
            <v-icon large color="grey lighten-1">mdi-account-search</v-icon>
            <div class="mt-2">
              Search for an account by phone, name, or address
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Day Camp Enrollment Warning Dialog -->
    <v-dialog v-model="enrollmentWarningDialog" max-width="520">
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-tent</v-icon>
          Outside Day Camp Enrollment
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outline value="true" class="mb-0 pa-2">
            {{ enrollmentDateWarning }}
          </v-alert>
          <div class="mt-3">Do you want to continue saving anyway?</div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="cancelEnrollmentWarning"
            >Cancel</v-btn
          >
          <v-btn color="warning" dark @click="confirmSaveWithEnrollmentWarning">
            Continue Saving
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Incomplete Route Confirmation Dialog -->
    <v-dialog v-model="incompleteRouteDialog" max-width="500">
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-alert</v-icon>
          Incomplete Route Selection
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outline value="true" class="mb-0 pa-2">
            {{ getIncompleteRouteMessage() }}
          </v-alert>
          <div class="mt-3">Do you want to continue saving anyway?</div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="incompleteRouteDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="warning" dark @click="confirmSaveWithIncompleteRoute">
            Continue Saving
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Missing Geo Stop Confirmation Dialog -->
    <v-dialog v-model="missingGeoStopDialog" max-width="520">
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-map-marker-off</v-icon>
          Geo Stop Not Selected
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outline value="true" class="mb-0 pa-2">
            {{ missingGeoStopMessage }}
          </v-alert>
          <div class="mt-3">
            GeoTrack will not sync for routes without a stop. Continue saving
            anyway?
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="missingGeoStopDialog = false"
            >Go Back</v-btn
          >
          <v-btn color="warning" dark @click="confirmSaveWithoutGeoStop">
            Continue Without Geo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Request Confirmation Dialog -->
    <v-dialog v-model="deleteRequestConfirmDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="headline error white--text">
          <v-icon left dark>mdi-delete-forever</v-icon>
          Delete Bus Change Request
        </v-card-title>
        <v-card-text class="pt-3 pb-2">
          <v-alert
            type="warning"
            outline
            value="true"
            class="delete-warning-banner mb-3"
          >
            <strong>Permanent action:</strong> deleting this request cannot be
            undone.
          </v-alert>
          <div v-if="selectedRequest" class="delete-summary-card">
            <div class="delete-summary-title">
              <v-icon small color="error" class="mr-1">mdi-bus-alert</v-icon>
              Request Details
            </div>
            <div class="delete-summary-grid">
              <div class="delete-summary-item">
                <div class="delete-label">Student</div>
                <div class="delete-value">
                  {{ selectedRequest.studentName || "-" }}
                  <span v-if="selectedRequest.studentID">
                    ({{ selectedRequest.studentID }})
                  </span>
                </div>
              </div>
              <div class="delete-summary-item">
                <div class="delete-label">Branch / Class</div>
                <div class="delete-value">
                  {{ selectedRequest.branch || "-" }} /
                  {{ selectedRequest.class || "-" }}-{{
                    selectedRequest.section || "-"
                  }}
                </div>
              </div>
              <div class="delete-summary-item">
                <div class="delete-label">Date Range</div>
                <div class="delete-value">
                  {{ formData.startDate || "-" }} to
                  {{ formData.endDate || "Open" }}
                </div>
              </div>
              <div class="delete-summary-item">
                <div class="delete-label">Run</div>
                <div class="delete-value">
                  {{ formatPeriodLabel(formData.startPeriod) }} to
                  {{ formatPeriodLabel(formData.endPeriod) }}
                </div>
              </div>
              <div class="delete-summary-item delete-summary-item--full">
                <div class="delete-label">Address</div>
                <div class="delete-value">
                  {{ formData.busChangeAddress || "-" }}
                </div>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="deleteRequestConfirmDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="error"
            dark
            :loading="deletingRequest"
            @click="deleteRequest"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Notification Confirmation Dialog -->
    <v-dialog v-model="notificationConfirmDialog" max-width="550" persistent>
      <v-card>
        <v-card-title class="headline success white--text">
          <v-icon left dark>mdi-information</v-icon>
          Notification Will Be Sent
        </v-card-title>
        <v-card-text class="pt-3">
          <v-alert
            value="true"
            type="info"
            outline
            class="mb-3"
            v-if="notificationDialogPhone"
          >
            <div class="font-weight-bold mb-1">
              <v-icon small color="info" class="mr-1">
                {{
                  notificationDialogType === "CALL"
                    ? "mdi-phone"
                    : "mdi-message-text"
                }}
              </v-icon>
              {{
                notificationDialogType === "CALL"
                  ? "A phone call"
                  : "A text message"
              }}
              will automatically be sent to:
            </div>
            <div class="ml-4">
              {{ formatPhoneNumber(notificationDialogPhone) }}
            </div>
          </v-alert>
          <div class="grey--text text--darken-1">
            The bus change will be saved and the notification will be sent
            automatically.
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-btn color="default" dark @click="cancelSave">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="notificationDialogSource === 'add'"
            color="success"
            outline
            @click="proceedWithoutNotification"
          >
            Save Without Notification
          </v-btn>
          <v-btn color="success" dark @click="proceedWithNotification">
            <v-icon left small>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Missing Notification Fields Dialog -->
    <v-dialog
      v-model="missingNotificationFieldsDialog"
      max-width="550"
      persistent
    >
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-alert-circle</v-icon>
          Missing Notification Information
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outline value="true" class="mb-3">
            <div class="font-weight-bold mb-2">
              Cannot send notification - missing required fields:
            </div>
            <ul class="ml-4">
              <li v-if="!notificationDialogPhone">
                Notification Phone Number
              </li>
              <li v-if="!notificationDialogType">
                Notification Type
              </li>
            </ul>
          </v-alert>
          <div class="mt-3">
            Would you like to save without sending a notification?
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-btn color="default" dark @click="cancelSave">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="warning" @click="saveWithoutNotification">
            Save Without Notification
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cleared Notification Warning Dialog (IVR) -->
    <v-dialog
      v-model="clearedNotificationWarningDialog"
      max-width="550"
      persistent
    >
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-alert</v-icon>
          Notification Fields Cleared
        </v-card-title>
        <v-card-text class="pt-3">
          <v-alert type="warning" outline value="true" outlined class="mb-3">
            <div class="font-weight-bold mb-2 grey--text text--darken-4">
              This request originally had notification information
            </div>
            <div class="grey--text text--darken-3">
              By not having notification fields, the user will NOT receive a
              notification about this bus change.
            </div>
          </v-alert>
          <div class="mt-3">
            Do you want to continue saving without notification?
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="cancelClearedNotificationWarning">
            Cancel
          </v-btn>
          <v-btn color="warning" dark @click="confirmSaveWithoutNotification">
            Save Without Notification
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Deadline grace: today override vs tomorrow -->
    <v-dialog persistent v-model="addDeadlineChoiceDialog" max-width="520">
      <v-card>
        <v-card-title class="dialog-header py-2">
          <v-icon class="mr-3 pt-1" color="white" size="24"
            >mdi-clock-alert</v-icon
          >
          <span class="dialog-title">{{
            addDeadlineChoiceClosedDay
              ? "No Bus Changes Today"
              : "Deadline Just Passed"
          }}</span>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert
            v-if="addDeadlineChoiceClosedDay"
            :value="true"
            type="warning"
            outline
            class="mb-3 pa-2"
          >
            No bus changes are scheduled for today.
          </v-alert>
          <v-alert v-else :value="true" type="warning" outline class="mb-3 pa-2">
            The deadline for bus changes was
            {{ addDeadlineChoiceFormatted }}
            <span v-if="addDeadlineMinutesPast != null">
              ({{ addDeadlineMinutesPast }}
              {{ addDeadlineMinutesPast === 1 ? "minute" : "minutes" }}
              ago) </span
            >.
          </v-alert>
          <div>
            Place this bus change for today anyway, or start it tomorrow?
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="chooseAddDeadlineTomorrow">
            Tomorrow
          </v-btn>
          <v-btn color="success" dark @click="chooseAddDeadlineToday">
            Place for Today
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bus Change View Dialog for Completed Requests -->
    <bus-change-view-dialog
      :dialog.sync="viewDialog"
      :busChangeId="viewBusChangeId"
      :isViewOnly="false"
      :sendNotificationOnSave="viewDialogSendNotificationOnSave"
      @saved="onBusChangeSaved"
      @close="onViewDialogClose"
    ></bus-change-view-dialog>

    <bus-change-report-dialog ref="busChangeReportDialog" />

    <bus-change-setup-dialog
      v-model="setupDialog"
      :can-manage-presets="canManageRoutePresets()"
      :branch="userBranch || ''"
      :default-kind="isWithinSummerSeason() ? 'DayCamp' : 'Regular'"
      @grades-changed="busChangeGrades = $event"
      @routes-saved="onRouteIdentitySaved"
    ></bus-change-setup-dialog>
  </div>
</template>

<script>
import moment from "moment";
import BusChangeViewDialog from "./BusChangeViewDialog.vue";
import BusChangeReportDialog from "./BusChangeReportDialog.vue";
import { normalizeRecordingUrl } from "../../utils/helpers.js";
import { getBusChangeNotificationNotices } from "./busChangeNotification.js";
import {
  closedDayInRange,
  closedDayMessage,
  deadlineForDate,
  isNoBusChangeDeadline,
} from "./busChangeDeadline.js";
import {
  routeHasDriver,
  routeMatchesStudent,
  routeMatchesStudents,
  ensureRouteIncluded,
} from "./busChangeRouteFilter.js";
import {
  buildColorMap,
  buildExtraRouteGroups,
  filterSingleDayRoutes,
  groupRoutesByTime,
  isRouteInPicker,
  parseBusChangeRouteBuildings,
  routesForStudentBuildings,
} from "./busRouteDisplay.js";
import BusRoutePicker from "./components/BusRoutePicker.vue";
import BusChangeSetupDialog from "./components/BusChangeSetupDialog.vue";

export default {
  components: {
    BusChangeViewDialog,
    BusChangeReportDialog,
    BusRoutePicker,
    BusChangeSetupDialog,
  },

  data: () => ({
    refreshTimer: null,
    userDayCampBuildings: [],
    userBusChangeRouteBuildings: [],
    busRouteColors: [],
    setupDialog: false,
    summerDates: null,

    simpleRequests: [],
    simpleBusChanges: [],
    loadingSimpleRequests: false,
    simpleLastRefreshed: "",

    // Dialog state
    processDialog: false,
    saving: false,
    deletingRequest: false,
    selectedRequest: null,
    busChangeComments: [],
    loadingBusChangeComments: false,

    // View dialog for completed requests
    viewDialog: false,
    viewBusChangeId: null,
    viewDialogSendNotificationOnSave: false,

    // Account search dialog
    accountSearchDialog: false,
    dialogPhoneSearch: "",
    dialogFirstNameSearch: "",
    dialogLastNameSearch: "",
    dialogHouseNumberSearch: "",
    dialogStreetSearch: "",
    dialogAccounts: [],
    dialogSearchNameMode: "yiddish",
    dialogShowNoAccountsMessage: false,
    dialogSearchDebounceTimer: null,

    // Incomplete route confirmation dialog
    incompleteRouteDialog: false,
    skipRouteWarning: false,
    addSkipRouteWarning: false,
    missingGeoStopDialog: false,
    skipGeoStopWarning: false,
    addSkipGeoStopWarning: false,
    missingGeoStopMessage: "",
    warningDialogSource: null, // 'add' | 'process'
    selectedPickupStopNumber: null,
    selectedDropoffStopNumber: null,
    pickupRouteStops: [],
    dropoffRouteStops: [],
    loadingPickupStops: false,
    loadingDropoffStops: false,
    deleteRequestConfirmDialog: false,

    // Day camp enrollment (when converting a request during summer)
    dayCampEnrollment: null,
    enrollmentWarningDialog: false,
    skipEnrollmentWarning: false,

    // Bus change notification dialogs and state
    notificationConfirmDialog: false,
    missingNotificationFieldsDialog: false,
    clearedNotificationWarningDialog: false,
    skipNotification: false,
    notificationDialogSource: null, // 'add' | 'process'
    originalPickupRouteID: null,
    originalDropoffRouteID: null,
    originalNotificationPhone: null,
    originalNotificationType: null,
    savedBusChangeId: null,
    sendingNotification: false,

    // Date pickers
    startDateMenu: false,
    endDateMenu: false,
    editingExtraInfo: false,
    extraInfoBackup: null,

    // Bus routes
    busRoutes: [],
    filteredPickupRoutes: [],
    filteredDropoffRoutes: [],
    selectedPickupRoute: null,
    selectedDropoffRoute: null,
    loadingBusRoutes: false,
    selectedGradeDeadlines: [],
    busChangeGrades: [],

    // Form data
    formData: {
      startDate: "",
      endDate: "",
      startPeriod: "",
      endPeriod: "",
      busChangeAddress: "",
      busChangePhoneNumber: "",
      geoPhoneNumber: "",
      busChangeName: "",
      busChangeAccountID: "",
      note: "",
    },
    periodOptions: [
      { label: "Morning Pickup", value: "AM" },
      { label: "Afternoon Dropoff", value: "PM" },
    ],

    // Simple Add Bus Change dialog
    addBusChangeDialog: false,
    addSaving: false,

    // End Bus Change dialog
    endBusChangeDialog: false,
    endSaving: false,
    endBusChangeSearch: "",
    endBusChangeOverdueOnly: false,
    printingOverdueList: false,
    endSelectedBusChange: null,
    endBusRoutes: [],
    endFilteredReturnRoutes: [],
    endSelectedReturnRoute: null,
    endLoadingBusRoutes: false,

    // Still Open section starts collapsed; user can expand via header
    collapsedSimpleSections: {
      stillOpen: true,
    },

    addPhoneSearch: "",
    addLastNameSearch: "",
    addFirstNameSearch: "",
    addGradeSearch: "",
    addGradeOptions: [],
    addLoadingGrades: false,
    addSearchNameMode: "yiddish",
    addStudentSearchLoading: false,
    addStudentResults: [],
    addStudentSearched: false,
    addGradeError: "",
    addStudentSearchDebounce: null,
    addSelectedStudent: null,
    // Additional siblings (same building) selected alongside addSelectedStudent
    // so the same bus change info can be saved for all of them at once.
    addSiblingStudents: [],
    // When siblings are in different grades, a route must list every grade
    // unless staff confirms they still ride the same bus.
    addSiblingsShareSameBus: false,
    addCheckedStudentIds: [],
    addDayCampEnrollment: null,
    addDurationOption: "today",
    addDeadlineOverrideToday: false,
    addDeadlineChoiceDialog: false,
    addDeadlineChoiceClosedDay: false,
    sectionFilters: {
      pending: { search: "", buildings: [] },
      enteredToday: { search: "", buildings: [] },
      stillOpen: { search: "", buildings: [] },
    },
    addForm: {
      startDate: "",
      endDate: "",
      startPeriod: "",
      endPeriod: "",
      busChangeAddress: "",
      busChangePhoneNumber: "",
      geoPhoneNumber: "",
      busChangeName: "",
      busChangeAccountID: "",
      notificationPhoneNumber: "",
      notificationType: "",
    },
    addAccountPhones: [],
    addNotificationEditing: false,
    addNotificationManuallyEdited: false,
    addNotificationEditBackup: null,
    addNotificationPhoneSearch: null,
    addSelectedGradeDeadlines: [],
    addNotificationTypeOptions: [
      { text: "Text message", value: "MESSAGE" },
      { text: "Call", value: "CALL" },
    ],
    addDestinationPhoneDialog: false,
    addDestinationPhoneAccounts: [],
    addDestinationPhoneLookupDone: "",
    addDestinationPhoneDebounce: null,
    addDestinationSelectedAccount: null,
    addAddressSuggestions: [],
    addAddressSearchText: null,
    addAddressSearchLoading: false,
    addAddressSearchDebounce: null,
    addAutoSelectBusDebounce: null,
    addAutoSelectingBus: false,
    addLastAutoSelectKey: "",
    addStartDateMenu: false,
    addEndDateMenu: false,
    addBusRoutes: [],
    addFilteredPickupRoutes: [],
    addFilteredDropoffRoutes: [],
    addSelectedPickupRoute: null,
    addSelectedDropoffRoute: null,
    addSelectedSpecialDayRoute: null,
    addShowSpecialDayPicker: false,
    addSelectedPickupStopNumber: null,
    addSelectedDropoffStopNumber: null,
    addPickupRouteStops: [],
    addDropoffRouteStops: [],
    addLoadingBusRoutes: false,
    addLoadingPickupStops: false,
    addLoadingDropoffStops: false,
    // Validation rules
    rules: {
      required: (value) => !!value || "Required.",
      phoneNumber: (v) => {
        if (!v) return true;
        const raw =
          typeof v === "object" ? v.value || v.text || v.phoneNum || "" : v;
        const digits = String(raw).replace(/\D/g, "");
        return (
          digits.length === 10 ||
          (digits.length === 11 && digits.charAt(0) === "1") ||
          "Invalid Phone Number"
        );
      },
    },
  }),

  computed: {
    userBranch() {
      const branch = this.$store.state.authentication.branch;
      if (branch && !branch.includes("null")) {
        return branch;
      }
      return null;
    },
    getRecordingUrl() {
      if (!this.selectedRequest) return null;
      return normalizeRecordingUrl(
        this.selectedRequest.recordingURL ||
          this.selectedRequest.RecordingURL ||
          this.selectedRequest.recordingUrl ||
          null
      );
    },
    deadlineWarningMessage() {
      if (!this.selectedRequest) return null;
      const ctx = this.getProcessDeadlineGradeContext();
      return this.checkDeadlineWarning(
        ctx.grade,
        ctx.scope,
        this.formData.startDate,
        this.formData.endDate
      );
    },
    processGradeNotAllowedMessage() {
      if (!this.selectedRequest || !this.busChangeGrades.length) return null;
      const ctx = this.getProcessDeadlineGradeContext();
      const gradeCheck = this.checkGradeAllowsBusChange(
        ctx.grade,
        ctx.scope,
        ctx.isDayCamp
      );
      return gradeCheck.allowed ? null : gradeCheck.error;
    },
    enrollmentDateWarning() {
      return this.getEnrollmentDateWarning();
    },
    callerFallback() {
      const source = (
        (this.selectedRequest &&
          (this.selectedRequest.source || this.selectedRequest.Source)) ||
        ""
      ).toUpperCase();
      return source === "ONLINE" ? "N/A" : "Unknown";
    },
    summerSeasonName() {
      const start =
        this.dayCampEnrollment && this.dayCampEnrollment.enrollmentStartDate;
      const year = start ? moment(start).year() : moment().year();
      return `Summer ${year}`;
    },
    addIsDayCampMode() {
      return this.isWithinSummerSeason();
    },
    // Primary selected student + any siblings, for rendering identical
    // "Selected Student" cards for all of them.
    addDisplayStudents() {
      return [this.addSelectedStudent, ...this.addSiblingStudents].filter(
        Boolean
      );
    },
    // True when at least one building has 2+ matching students in the
    // current search results — i.e. siblings that can be selected together.
    addHasMultiSelectableBuilding() {
      const counts = {};
      (this.addStudentResults || []).forEach((s) => {
        const b = s.building || "";
        counts[b] = (counts[b] || 0) + 1;
      });
      return Object.keys(counts).some((b) => counts[b] > 1);
    },
    addDistinctSelectedGrades() {
      const seen = {};
      const grades = [];
      (this.addDisplayStudents || []).forEach((s) => {
        const g = ((s && s.class) || "").toString().trim();
        if (!g) return;
        const key = g.toUpperCase();
        if (seen[key]) return;
        seen[key] = true;
        grades.push(g);
      });
      return grades;
    },
    addSiblingsHaveDifferentGrades() {
      return (
        this.addSiblingStudents.length > 0 &&
        this.addDistinctSelectedGrades.length > 1
      );
    },
    addHasSharedGradeRoutes() {
      if (!this.addSiblingsHaveDifferentGrades) return true;
      const routes = this.addBusRoutes || [];
      if (!routes.length) return true;
      let matching = routes.filter((r) =>
        routeMatchesStudents(r, this.addDisplayStudents, true)
      );
      if (this.addDayCampEnrollment) {
        const target = (this.addSummerSeasonName || "").trim().toLowerCase();
        matching = matching.filter((r) => {
          const kind = (r.kind || "").trim().toLowerCase();
          if (kind === "daycamp") return true;
          const season = (r.season || "").trim().toLowerCase();
          if (!season) return true;
          return season === target;
        });
      }
      return matching.length > 0;
    },
    addSummerSeasonName() {
      const start =
        this.addDayCampEnrollment &&
        this.addDayCampEnrollment.enrollmentStartDate;
      const year = start ? moment(start).year() : moment().year();
      return `Summer ${year}`;
    },
    addDurationOptions() {
      const base = this.getAddEffectiveBaseDate();
      const calendarToday = moment().startOf("day");
      const baseIsToday = base.isSame(calendarToday, "day");
      const formatDay = (d) => d.format("dddd, MMM D");
      const preset = (value, title, endDays) => {
        const end = base.clone().add(endDays, "days");
        let caption;
        if (endDays === 0) {
          caption = baseIsToday
            ? `Today, ${formatDay(base)} · Afternoon Dropoff`
            : `${formatDay(base)} · Afternoon Dropoff`;
        } else {
          caption = baseIsToday
            ? `Starts today · Ends ${formatDay(end)} · Afternoon Dropoff`
            : `Starts ${formatDay(base)} · Ends ${formatDay(
                end
              )} · Afternoon Dropoff`;
        }
        return { value, title, caption };
      };
      return [
        preset("today", "Today Only", 0),
        // 2 Days = effective start + next day
        preset("2days", "2 Days", 1),
        preset("1week", "1 Week", 7),
        preset("2weeks", "2 Weeks", 14),
        {
          value: "custom",
          title: "Custom",
          caption: "Pick your own dates & runs",
        },
      ];
    },
    addSelectedDurationCaption() {
      const selected = this.addDurationOptions.find(
        (o) => o.value === this.addDurationOption
      );
      return selected ? selected.caption : "";
    },
    addDeadlineChoiceFormatted() {
      const info = this.getAddTodayDeadline();
      return info ? info.formatted : "";
    },
    addDeadlineMinutesPast() {
      const info = this.getAddTodayDeadline();
      return info ? info.minutesPast : null;
    },
    // Same calendar day, PM → PM: only afternoon dropoff is needed
    addIsAfternoonOnly() {
      const start = this.addForm && this.addForm.startDate;
      if (!start) return false;
      const end = (this.addForm && this.addForm.endDate) || start;
      if (!moment(start).isSame(moment(end), "day")) return false;
      const startPm =
        ((this.addForm && this.addForm.startPeriod) || "")
          .toString()
          .trim()
          .toUpperCase() === "PM";
      const endPm =
        ((this.addForm && this.addForm.endPeriod) || "")
          .toString()
          .trim()
          .toUpperCase() === "PM";
      return startPm && endPm;
    },
    addEnrollmentWarning() {
      return this.getAddEnrollmentDateWarning();
    },
    addGeoPhoneSelectItems() {
      return this.buildAddPhoneSelectItems(true);
    },
    busRouteColorMap() {
      return buildColorMap(this.busRouteColors);
    },
    addMainPickupRoutes() {
      return filterSingleDayRoutes(
        this.addFilteredPickupRoutes,
        this.addForm.startDate,
        this.addForm.endDate,
        this.addSelectedPickupRoute
      );
    },
    addMainDropoffRoutes() {
      return filterSingleDayRoutes(
        this.addFilteredDropoffRoutes,
        this.addForm.startDate,
        this.addForm.endDate,
        this.addSelectedDropoffRoute
      );
    },
    addPickupExtraGroups() {
      return this.buildExtraRouteGroups(
        this.filterAddRoutesBySeason(
          (this.addBusRoutes || []).filter(
            (r) => r.direction === "Pickup" && routeHasDriver(r)
          )
        ),
        this.addDisplayStudents,
        this.addMainPickupRoutes
      );
    },
    addDropoffExtraGroups() {
      return this.buildExtraRouteGroups(
        this.filterAddRoutesBySeason(
          (this.addBusRoutes || []).filter(
            (r) => r.direction === "Dropoff" && routeHasDriver(r)
          )
        ),
        this.addDisplayStudents,
        this.addMainDropoffRoutes
      );
    },
    addSpecialDaySections() {
      return groupRoutesByTime(
        routesForStudentBuildings(
          this.filterAddRoutesBySeason(
            (this.addBusRoutes || []).filter(
            (r) => r.direction === "Dropoff" && routeHasDriver(r)
          )
          ),
          this.addDisplayStudents
        )
      );
    },
    endReturnExtraGroups() {
      return this.buildExtraRouteGroups(
        (this.endBusRoutes || []).filter(
          (r) => r.direction === "Dropoff" && routeHasDriver(r)
        ),
        [this.getEndReturnStudent()],
        this.endFilteredReturnRoutes
      );
    },
    addNotificationPhoneSelectItems() {
      return this.buildAddPhoneSelectItems(false);
    },
    notificationDialogPhone() {
      if (this.notificationDialogSource === "add") {
        return (this.addForm && this.addForm.notificationPhoneNumber) || "";
      }
      return (
        (this.selectedRequest &&
          this.selectedRequest.notificationPhoneNumber) ||
        ""
      );
    },
    notificationDialogType() {
      if (this.notificationDialogSource === "add") {
        return (this.addForm && this.addForm.notificationType) || "";
      }
      return (
        (this.selectedRequest && this.selectedRequest.notificationType) || ""
      );
    },
    pendingRequests() {
      // Oldest first so the queue is worked top-down
      return this.simpleRequests
        .filter((r) => this.getRequestStatus(r) === "PENDING")
        .slice()
        .sort((a, b) => {
          const da = moment(a.date || a.Date || 0).valueOf();
          const db = moment(b.date || b.Date || 0).valueOf();
          return da - db;
        });
    },
    activeBusChanges() {
      const today = moment().startOf("day");
      return this.simpleBusChanges
        .filter((bc) => {
          const status = this.getRequestStatus(bc);
          if (status === "DELETED") return false;
          const finished = bc.finished === true || bc.Finished === true;
          const end = bc.endDate || bc.EndDate || bc.startDate || bc.StartDate;
          const endDate = end ? moment(end) : null;
          const endsToday =
            endDate &&
            endDate.isValid() &&
            endDate
              .clone()
              .startOf("day")
              .isSame(today);
          if (finished && endsToday) return true;
          if (status === "COMPLETED") return false;
          if (!end) return true;
          if (!endDate.isValid()) return true;
          return endDate.startOf("day").isSameOrAfter(today);
        })
        .slice()
        .sort((a, b) => {
          const da = moment(a.enteredOn || a.EnteredOn || 0).valueOf();
          const db = moment(b.enteredOn || b.EnteredOn || 0).valueOf();
          return db - da;
        });
    },
    enteredTodayBusChanges() {
      return this.activeBusChanges.filter(
        (bc) => this.wasEnteredToday(bc) || this.wasEndedToday(bc)
      );
    },
    stillOpenBusChanges() {
      return this.activeBusChanges
        .filter((bc) => !this.wasEnteredToday(bc) && !this.wasEndedToday(bc))
        .slice()
        .sort((a, b) => {
          const aOverdue = this.isLongRunningBusChange(a) ? 1 : 0;
          const bOverdue = this.isLongRunningBusChange(b) ? 1 : 0;
          if (aOverdue !== bOverdue) return bOverdue - aOverdue;
          const da = moment(a.enteredOn || a.EnteredOn || 0).valueOf();
          const db = moment(b.enteredOn || b.EnteredOn || 0).valueOf();
          return db - da;
        });
    },
    overdueBusChangesForPrint() {
      return this.activeBusChanges.filter((bc) =>
        this.isLongRunningBusChange(bc)
      );
    },
    overdueStillOpenCount() {
      return this.stillOpenBusChanges.filter((bc) =>
        this.isLongRunningBusChange(bc)
      ).length;
    },
    openExtendedBusChanges() {
      const today = moment().startOf("day");
      const q = (this.endBusChangeSearch || "").trim().toLowerCase();
      const qDigits = q.replace(/\D/g, "");
      return this.simpleBusChanges
        .filter((bc) => {
          if (bc.finished === true || bc.Finished === true) return false;
          const status = this.getRequestStatus(bc);
          if (status === "DELETED" || status === "COMPLETED") return false;
          if (this.isSingleDayChange(bc)) return false;
          const end = bc.endDate || bc.EndDate;
          if (
            end &&
            moment(end)
              .startOf("day")
              .isBefore(today)
          )
            return false;
          if (!q) return true;
          const phones = this.getEndBusChangePhones(bc);
          const phoneDigits = phones
            .map((p) => String(p).replace(/\D/g, ""))
            .join(" ");
          const haystack = (
            (bc.studentNameHeb || "") +
            " " +
            (bc.studentName || "") +
            " " +
            (bc.studentID || "") +
            " " +
            (bc.id || bc.ID || "") +
            " " +
            (this.getEndBusChangeName(bc) || "") +
            " " +
            (bc.busChangeAddress || "") +
            " " +
            phones.join(" ")
          ).toLowerCase();
          if (haystack.indexOf(q) !== -1) return true;
          if (qDigits.length >= 3 && phoneDigits.indexOf(qDigits) !== -1) {
            return true;
          }
          return false;
        })
        .slice()
        .sort((a, b) => {
          const aOverdue = this.isLongRunningBusChange(a) ? 1 : 0;
          const bOverdue = this.isLongRunningBusChange(b) ? 1 : 0;
          if (aOverdue !== bOverdue) return bOverdue - aOverdue;
          const na = (a.studentNameHeb || a.studentName || "").toString();
          const nb = (b.studentNameHeb || b.studentName || "").toString();
          return na.localeCompare(nb);
        });
    },
    overdueOpenExtendedCount() {
      return this.openExtendedBusChanges.filter((bc) =>
        this.isLongRunningBusChange(bc)
      ).length;
    },
    filteredOpenExtendedBusChanges() {
      if (!this.endBusChangeOverdueOnly) return this.openExtendedBusChanges;
      return this.openExtendedBusChanges.filter((bc) =>
        this.isLongRunningBusChange(bc)
      );
    },
    canSaveEndBusChange() {
      return !!(this.endSelectedBusChange && this.endSelectedReturnRoute);
    },
    simpleSections() {
      return [
        {
          key: "pending",
          title: "Pending Requests",
          icon: "mdi-clock-alert",
          color: "#ff9800",
          items: this.pendingRequests,
          hint: "Requests waiting to be processed",
          emptyIcon: "mdi-check-circle-outline",
          emptyText: "No pending requests. You're all caught up!",
          collapsible: false,
        },
        {
          key: "enteredToday",
          title: "Today's Activity",
          icon: "mdi-calendar-today",
          color: "rgb(92, 184, 96)",
          items: this.enteredTodayBusChanges,
          hint: "Bus changes entered or ended today",
          emptyIcon: "mdi-bus",
          emptyText: "No bus changes entered or ended today.",
          collapsible: false,
        },
        {
          key: "stillOpen",
          title: "Still Open",
          icon: "mdi-bus-clock",
          color: "#5c6bc0",
          items: this.stillOpenBusChanges,
          hint:
            this.overdueStillOpenCount > 0
              ? this.overdueStillOpenCount +
                " overdue (open 3+ weeks) · earlier days"
              : "Active changes from earlier days",
          emptyIcon: "mdi-bus",
          emptyText: "No earlier open bus changes.",
          collapsible: true,
        },
      ];
    },
  },

  watch: {
    selectedRequest: {
      handler(request) {
        if (request) {
          this.filterRoutesByBuilding();
        }
      },
      deep: true,
    },
    addSiblingsShareSameBus() {
      this.filterAddRoutesByBuilding();
    },
    dialogSearchNameMode() {
      // Re-search when switching between English and Yiddish
      if (this.dialogFirstNameSearch || this.dialogLastNameSearch) {
        this.searchDialogByName();
      }
    },
    "sectionFilters.pending.buildings": {
      handler: "saveSectionBuildingFilters",
      deep: true,
    },
    "sectionFilters.enteredToday.buildings": {
      handler: "saveSectionBuildingFilters",
      deep: true,
    },
    "sectionFilters.stillOpen.buildings": {
      handler: "saveSectionBuildingFilters",
      deep: true,
    },
    addIsAfternoonOnly(isAfternoonOnly) {
      if (isAfternoonOnly) {
        this.clearAddPickupSelection();
      }
    },
    "addForm.busChangeAddress"() {
      this.scheduleAutoSelectBusFromAddress();
    },
  },

  created() {
    this.restoreSectionBuildingFilters();
  },

  mounted() {
    this.initSimpleView();
    this.refreshTimer = setInterval(() => {
      this.autoRefreshGrid();
    }, 5 * 60 * 1000);
  },

  beforeDestroy() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.addStudentSearchDebounce) {
      clearTimeout(this.addStudentSearchDebounce);
      this.addStudentSearchDebounce = null;
    }
    if (this.addDestinationPhoneDebounce) {
      clearTimeout(this.addDestinationPhoneDebounce);
      this.addDestinationPhoneDebounce = null;
    }
    if (this.addAddressSearchDebounce) {
      clearTimeout(this.addAddressSearchDebounce);
      this.addAddressSearchDebounce = null;
    }
    if (this.addAutoSelectBusDebounce) {
      clearTimeout(this.addAutoSelectBusDebounce);
      this.addAutoSelectBusDebounce = null;
    }
  },

  methods: {
    buildBusChangeRequestsUrl(includeCompleted, includeDeleted) {
      // Backend: building takes precedence; branch is only used when no building filter is sent.
      // DayCampBuilding only applies during the summer season; after summer use branch as usual.
      // OData: always include every query param key, even when the value is empty.
      const useBuilding = this.useDayCampBuildingFilter();
      const branchValue =
        !useBuilding && this.userBranch ? this.userBranch : "";
      const buildingValue = useBuilding
        ? this.userDayCampBuildings.join(",")
        : "";
      const completed = includeCompleted ? "true" : "false";
      const deleted = includeDeleted ? "true" : "false";
      return (
        "busChangeRequests?branch=" +
        branchValue +
        "&building=" +
        buildingValue +
        "&includeCompleted=" +
        completed +
        "&includeDeleted=" +
        deleted
      );
    },

    autoRefreshGrid() {
      // Don't refresh while user is updating a record
      if (this.processDialog || this.viewDialog) return;
      this.loadSimpleViewData();
    },

    openReportDialog() {
      if (this.$refs.busChangeReportDialog) {
        this.$refs.busChangeReportDialog.open();
      }
    },

    isAdmin() {
      const isAdmin = this.$store.getters["authentication/isAdmin"];
      if (!isAdmin || isAdmin == "false") return false;
      return true;
    },

    // Colors list and driver notification schedules: Admin or Transportation role.
    canManageRoutePresets() {
      if (this.isAdmin()) return true;
      const roles = this.$store.state.authentication.roles;
      const list = Array.isArray(roles)
        ? roles
        : roles
        ? String(roles).split(",")
        : [];
      return list.map((r) => String(r).trim()).includes("Transportation");
    },

    async loadUserDayCampBuildings() {
      const username = this.$store.state.authentication.user;

      if (!username) {
        this.userDayCampBuildings = [];
        return;
      }

      try {
        const attributes = await this.$http.get(
          "api/users/GetUserAttributesByUsername?username=" +
            encodeURIComponent(username)
        );

        this.userDayCampBuildings = (attributes || [])
          .filter((a) => a && a.name && a.name.trim() === "DayCampBuilding")
          .map((a) => (a.value ? a.value.trim() : ""))
          .filter(Boolean);
        this.userBusChangeRouteBuildings = parseBusChangeRouteBuildings(
          attributes
        );
      } catch (e) {
        this.userDayCampBuildings = [];
        this.userBusChangeRouteBuildings = [];
      }
    },

    async loadBusRouteColors() {
      try {
        const colors = await this.$http.get(
          "api/busRouteIdentity/GetColors"
        );
        this.busRouteColors = Array.isArray(colors) ? colors : [];
      } catch (e) {
        this.busRouteColors = [];
      }
    },

    openSetupDialog() {
      this.setupDialog = true;
    },

    async onRouteIdentitySaved() {
      await this.loadBusRouteColors();
      if ((this.addBusRoutes || []).length) {
        await this.loadAddBusRoutes();
      }
    },

    buildExtraRouteGroups(routes, students, mainRoutes) {
      return buildExtraRouteGroups(
        routes,
        students,
        mainRoutes,
        this.userBusChangeRouteBuildings
      );
    },

    isRouteInPicker(route, mainRoutes, groups) {
      return isRouteInPicker(route, mainRoutes, groups);
    },

    getEndReturnStudent() {
      const selected = this.endSelectedBusChange;
      if (!selected) return null;
      return {
        building: selected.building || selected.Building,
        class: selected.class || selected.Class,
        branch: selected.branch || selected.Branch,
      };
    },

    async selectAddPickupRoute(route) {
      this.addSelectedPickupRoute = route;
      await this.onAddPickupRouteChange(route);
    },

    async selectAddDropoffRoute(route) {
      this.addSelectedDropoffRoute = route;
      await this.onAddDropoffRouteChange(route);
    },

    hasUserDayCampBuildings() {
      return !this.isAdmin() && this.userDayCampBuildings.length > 0;
    },

    useDayCampBuildingFilter() {
      return this.hasUserDayCampBuildings() && this.isWithinSummerSeason();
    },

    async loadSummerDates() {
      if (this.summerDates) return;
      try {
        const response = await this.$http.get("api/daycamp/getSummerDates");
        if (response && response.startDate && response.endDate) {
          this.summerDates = {
            startDate: response.startDate,
            endDate: response.endDate,
          };
        }
      } catch (e) {
        this.summerDates = null;
      }
    },

    isWithinSummerSeason() {
      if (!this.summerDates) return false;
      const today = moment().startOf("day");
      const start = moment(this.summerDates.startDate).startOf("day");
      const end = moment(this.summerDates.endDate).startOf("day");
      if (!start.isValid() || !end.isValid()) return false;
      return today.isSameOrAfter(start) && today.isSameOrBefore(end);
    },

    async onRowClicked(row) {
      const status = row && row.status ? row.status.toUpperCase() : "";
      const busChangeId =
        row && row.busChangeID ? row.busChangeID : row.busChangeId;

      // Open bus change dialog when bus change already exists
      if (
        busChangeId &&
        (status === "COMPLETED" || status === "PENDING" || status === "DELETED")
      ) {
        if (busChangeId) {
          this.viewDialogSendNotificationOnSave = status === "PENDING";
          this.viewBusChangeId = busChangeId;
          this.viewDialog = true;
        }
        return;
      }

      this.selectedRequest = row;
      this.resetForm();
      // Load day camp enrollment first so deadline lookup can use Day Camp
      // (grade + building) vs School (grade + branch) based on season.
      await this.loadSummerDates();
      await this.loadDayCampEnrollmentForRequest(row);
      const deadlineCtx = this.getProcessDeadlineGradeContext();
      await this.loadSelectedGradeDeadlines(
        deadlineCtx.grade,
        deadlineCtx.scope,
        deadlineCtx.isDayCamp
      );

      // Pre-fill all available fields from the request row
      const rawStartDate = row.startDate || row.StartDate || "";
      if (rawStartDate) {
        this.formData.startDate = moment(rawStartDate).format("YYYY-MM-DD");
      }

      const rawEndDate = row.endDate || row.EndDate || "";
      if (rawEndDate) {
        this.formData.endDate = moment(rawEndDate).format("YYYY-MM-DD");
      }

      this.formData.startPeriod = row.startPeriod || row.StartPeriod || "";
      this.formData.endPeriod = row.endPeriod || row.EndPeriod || "";

      this.originalPickupRouteID =
        row.pickupRouteID || row.PickupRouteID || null;
      this.originalDropoffRouteID =
        row.dropoffRouteID || row.DropoffRouteID || null;

      this.formData.busChangeName =
        row.busChangeName || row.BusChangeName || "";
      this.formData.busChangeAddress =
        row.busChangeAddress || row.BusChangeAddress || "";
      this.formData.busChangePhoneNumber =
        row.busChangePhoneNumber || row.BusChangePhoneNumber || "";
      this.formData.note = row.note || row.Note || "";

      const requestGeoPhoneNumber =
        row.geoPhoneNumber || row.GeoPhoneNumber || "";
      if (requestGeoPhoneNumber) {
        this.formData.geoPhoneNumber = requestGeoPhoneNumber;
      }

      await this.loadBusChangeComments(this.getBusChangeRequestId(row));

      // Fetch full student info to get teacher details
      if (row.studentID) {
        try {
          const studentInfo = await this.$http.get(
            `api/students/getStudent?studentID=${row.studentID}`
          );
          if (studentInfo) {
            this.selectedRequest = {
              ...this.selectedRequest,
              teacherH: studentInfo.teacherH,
              teacherH2: studentInfo.teacherH2,
            };
          }
        } catch (e) {
          // Continue without teacher info if API call fails
        }
      }

      // Reload routes for this request so the season filter (day camp only) is
      // applied server-side based on the request's enrollment.
      await this.loadBusRoutes();

      let pickupStopNumber = null;
      let dropoffStopNumber = null;
      const linkedBusChangeId =
        row.busChangeID || row.busChangeId || row.BusChangeID || null;
      if (linkedBusChangeId) {
        try {
          const detail = await this.$http.get(
            `api/buschange/getById?id=${linkedBusChangeId}`
          );
          if (detail) {
            if (detail.pickupStopNumber != null) {
              pickupStopNumber = detail.pickupStopNumber;
            }
            if (detail.dropoffStopNumber != null) {
              dropoffStopNumber = detail.dropoffStopNumber;
            }
          }
        } catch (e) {
          // keep null values
        }
      }

      if (this.originalPickupRouteID) {
        const pickupRoute = this.busRoutes.find(
          (r) => r.id === this.originalPickupRouteID
        );
        this.selectedPickupRoute = pickupRoute || null;
        if (pickupRoute) {
          await this.loadRouteStops("pickup", pickupRoute.id, pickupStopNumber);
        }
      }
      if (this.originalDropoffRouteID) {
        const dropoffRoute = this.busRoutes.find(
          (r) => r.id === this.originalDropoffRouteID
        );
        this.selectedDropoffRoute = dropoffRoute || null;
        if (dropoffRoute) {
          await this.loadRouteStops(
            "dropoff",
            dropoffRoute.id,
            dropoffStopNumber
          );
        }
      }

      this.filterRoutesByBuilding();

      this.processDialog = true;
    },

    resetForm() {
      this.formData = {
        startDate: "",
        endDate: "",
        startPeriod: "",
        endPeriod: "",
        busChangeAddress: "",
        busChangePhoneNumber: "",
        geoPhoneNumber: "",
        busChangeName: "",
        busChangeAccountID: "",
        note: "",
      };
      this.selectedPickupRoute = null;
      this.selectedDropoffRoute = null;
      this.selectedPickupStopNumber = null;
      this.selectedDropoffStopNumber = null;
      this.pickupRouteStops = [];
      this.dropoffRouteStops = [];
      this.loadingPickupStops = false;
      this.loadingDropoffStops = false;
      this.skipRouteWarning = false;
      this.skipGeoStopWarning = false;
      this.missingGeoStopDialog = false;
      this.missingGeoStopMessage = "";
      this.originalPickupRouteID = null;
      this.originalDropoffRouteID = null;
      this.originalNotificationPhone = null;
      this.originalNotificationType = null;
      this.skipNotification = false;
      this.savedBusChangeId = null;
      this.selectedGradeDeadlines = [];
      this.busChangeComments = [];
      this.loadingBusChangeComments = false;
      this.dayCampEnrollment = null;
      this.enrollmentWarningDialog = false;
      this.skipEnrollmentWarning = false;
      this.editingExtraInfo = false;
      this.extraInfoBackup = null;
    },

    closeDialog() {
      this.processDialog = false;
      this.deleteRequestConfirmDialog = false;
      this.selectedRequest = null;
      this.deletingRequest = false;
      this.busChangeComments = [];
      this.loadingBusChangeComments = false;
      this.resetForm();
    },

    getBusChangeRequestId(request) {
      if (!request) {
        return null;
      }

      if (request.id) return request.id;
      if (request.busChangeRequestID) return request.busChangeRequestID;
      if (request.busChangeRequestId) return request.busChangeRequestId;
      if (request.BusChangeRequestID) return request.BusChangeRequestID;
      return null;
    },

    isPendingRequest(request) {
      const status =
        request && request.status ? request.status.toUpperCase() : "";
      return status === "PENDING";
    },

    getRequestStatus(request) {
      return ((request && request.status) || "")
        .toString()
        .trim()
        .toUpperCase();
    },

    trimId(value) {
      return (value || "").toString().trim();
    },

    getCardGradeClass(request) {
      if (!request) return "-";
      const grade = (request.class || "").toString().trim() || "-";
      if (!this.isWithinSummerSeason()) return grade;
      const room = (
        request.dcRoomNum ||
        request.DcRoomNum ||
        request.roomNum ||
        request.RoomNum ||
        ""
      )
        .toString()
        .trim();
      if (!room) return grade;
      return grade + " · Room #" + room;
    },

    getCardTeacher(request) {
      if (!request) return "";
      const teachers = [];
      if (request.teacherH) teachers.push(request.teacherH);
      if (request.teacherH2) teachers.push(request.teacherH2);
      return teachers.join(" / ");
    },

    async initSimpleView() {
      await Promise.all([
        this.loadUserDayCampBuildings(),
        this.loadSummerDates(),
        this.loadBusRouteColors(),
      ]);
      this.loadSimpleViewData();
    },

    buildBusChangesUrl(includeCompleted, includeDeleted) {
      // Same branch/building scope rules as the requests URL.
      // OData: always include every query param key, even when the value is empty.
      const useBuilding = this.useDayCampBuildingFilter();
      const branchValue =
        !useBuilding && this.userBranch ? this.userBranch : "";
      const buildingValue = useBuilding
        ? this.userDayCampBuildings.join(",")
        : "";
      const completed = includeCompleted ? "true" : "false";
      const deleted = includeDeleted ? "true" : "false";
      return (
        "busChange?branch=" +
        branchValue +
        "&building=" +
        buildingValue +
        "&includeCompleted=" +
        completed +
        "&includeDeleted=" +
        deleted
      );
    },

    async loadSimpleViewData() {
      this.loadingSimpleRequests = true;
      try {
        // Pending section: requests still awaiting action.
        // Active section: real bus changes (NEW/ACTIVE/FUTURE) from the
        // busChange endpoint, not the request rows.
        const requestsUrl =
          "odata/" + this.buildBusChangeRequestsUrl(false, false);
        const busChangesUrl = "odata/" + this.buildBusChangesUrl(false, false);
        const results = await Promise.all([
          this.$http.get(requestsUrl),
          this.$http.get(busChangesUrl),
        ]);
        const requestsResponse = results[0];
        const busChangesResponse = results[1];
        this.simpleRequests =
          requestsResponse && requestsResponse.value
            ? requestsResponse.value
            : [];
        this.simpleBusChanges =
          busChangesResponse && busChangesResponse.value
            ? busChangesResponse.value
            : [];
        this.simpleLastRefreshed = moment().format("h:mm A");
        // Teacher (school year) + DC room (summer) for card lines
        await this.loadSimpleStudentCardInfo();
      } catch (e) {
        this.$notify({
          text: "Failed to load bus change requests",
          group: "notification",
          type: "error",
        });
      } finally {
        this.loadingSimpleRequests = false;
      }
    },

    async loadSimpleStudentCardInfo() {
      const seen = {};
      const uniqueIDs = [];
      const collect = (rows) => {
        (rows || []).forEach((row) => {
          const id = row && row.studentID ? String(row.studentID).trim() : "";
          if (id && !seen[id]) {
            seen[id] = true;
            uniqueIDs.push(id);
          }
        });
      };
      collect(this.simpleRequests);
      collect(this.simpleBusChanges);
      if (!uniqueIDs.length) return;

      try {
        const students = await this.$http.post(
          "api/BusChange/GetStudentInfoForStudentIDs",
          uniqueIDs
        );
        if (!Array.isArray(students)) return;

        const lookup = {};
        students.forEach((s) => {
          const id = s.studentID ? String(s.studentID).trim() : "";
          if (id) lookup[id] = s;
        });

        const mergeInfo = (row) => {
          const id = row && row.studentID ? String(row.studentID).trim() : "";
          const info = id ? lookup[id] : null;
          if (!info) return row;
          return Object.assign({}, row, {
            teacherH: String(info.teacherH || info.TeacherH || "").trim(),
            teacherH2: String(info.teacherH2 || info.TeacherH2 || "").trim(),
            roomNum: String(info.roomNum || info.RoomNum || "").trim(),
            dcRoomNum: String(info.dcRoomNum || info.DcRoomNum || "").trim(),
            busAddress1: String(
              info.busAddress1 || info.BusAddress1 || ""
            ).trim(),
          });
        };

        this.simpleRequests = this.simpleRequests.map(mergeInfo);
        this.simpleBusChanges = this.simpleBusChanges.map(mergeInfo);
      } catch (e) {
        // Cards still work without teacher/room info
      }
    },

    refreshData() {
      this.loadSimpleViewData();
    },

    async openAddBusChangeDialog() {
      this.resetAddBusChangeForm();
      this.applyAddDurationPreset("today");
      this.addBusChangeDialog = true;
      await Promise.all([
        this.loadSummerDates(),
        this.loadUserDayCampBuildings(),
        this.loadBusChangeGrades(),
      ]);
      this.loadAddGradeOptions();
      this.loadAddBusRoutes();
    },

    closeAddBusChangeDialog() {
      this.addBusChangeDialog = false;
      this.addSaving = false;
      this.resetAddBusChangeForm();
    },

    resetAddBusChangeForm() {
      if (this.addStudentSearchDebounce) {
        clearTimeout(this.addStudentSearchDebounce);
        this.addStudentSearchDebounce = null;
      }
      this.addPhoneSearch = "";
      this.addLastNameSearch = "";
      this.addFirstNameSearch = "";
      this.addGradeSearch = "";
      this.addGradeOptions = [];
      this.addLoadingGrades = false;
      this.addSearchNameMode = "yiddish";
      this.addStudentSearchLoading = false;
      this.addStudentResults = [];
      this.addStudentSearched = false;
      this.addGradeError = "";
      this.addSelectedStudent = null;
      this.addSiblingStudents = [];
      this.addSiblingsShareSameBus = false;
      this.addCheckedStudentIds = [];
      this.addDayCampEnrollment = null;
      this.addDurationOption = "today";
      this.addDeadlineOverrideToday = false;
      this.addDeadlineChoiceDialog = false;
      this.addForm = {
        startDate: "",
        endDate: "",
        startPeriod: "",
        endPeriod: "",
        busChangeAddress: "",
        busChangePhoneNumber: "",
        geoPhoneNumber: "",
        busChangeName: "",
        busChangeAccountID: "",
        notificationPhoneNumber: "",
        notificationType: "",
      };
      this.addAccountPhones = [];
      this.addNotificationEditing = false;
      this.addNotificationManuallyEdited = false;
      this.addNotificationEditBackup = null;
      this.addNotificationPhoneSearch = null;
      this.addSelectedGradeDeadlines = [];
      this.addDestinationPhoneDialog = false;
      this.addDestinationPhoneAccounts = [];
      this.addDestinationPhoneLookupDone = "";
      this.addDestinationSelectedAccount = null;
      if (this.addDestinationPhoneDebounce) {
        clearTimeout(this.addDestinationPhoneDebounce);
        this.addDestinationPhoneDebounce = null;
      }
      this.addAddressSuggestions = [];
      this.addAddressSearchText = null;
      this.addAddressSearchLoading = false;
      if (this.addAddressSearchDebounce) {
        clearTimeout(this.addAddressSearchDebounce);
        this.addAddressSearchDebounce = null;
      }
      if (this.addAutoSelectBusDebounce) {
        clearTimeout(this.addAutoSelectBusDebounce);
        this.addAutoSelectBusDebounce = null;
      }
      this.addAutoSelectingBus = false;
      this.addLastAutoSelectKey = "";
      this.addStartDateMenu = false;
      this.addEndDateMenu = false;
      this.addSelectedPickupRoute = null;
      this.addSelectedDropoffRoute = null;
      this.addSelectedSpecialDayRoute = null;
      this.addShowSpecialDayPicker = false;
      this.addSelectedPickupStopNumber = null;
      this.addSelectedDropoffStopNumber = null;
      this.addPickupRouteStops = [];
      this.addDropoffRouteStops = [];
      this.addLoadingPickupStops = false;
      this.addLoadingDropoffStops = false;
      this.addSkipRouteWarning = false;
      this.addSkipGeoStopWarning = false;
      this.skipNotification = false;
      this.savedBusChangeId = null;
      this.notificationDialogSource = null;
      this.notificationConfirmDialog = false;
      this.missingNotificationFieldsDialog = false;
      if (this.warningDialogSource === "add") {
        this.warningDialogSource = null;
        this.incompleteRouteDialog = false;
        this.missingGeoStopDialog = false;
        this.missingGeoStopMessage = "";
      }
    },

    clearAddPickupSelection() {
      this.addSelectedPickupRoute = null;
      this.addSelectedPickupStopNumber = null;
      this.addPickupRouteStops = [];
      this.addLoadingPickupStops = false;
    },

    clearAddDropoffSelection() {
      this.addSelectedDropoffRoute = null;
      this.addSelectedDropoffStopNumber = null;
      this.addDropoffRouteStops = [];
      this.addLoadingDropoffStops = false;
    },

    clearAddStudentSearchResults() {
      this.addStudentResults = [];
      this.addStudentSearched = false;
    },

    onAddSearchNameModeChange() {
      if (
        this.addLastNameSearch ||
        this.addFirstNameSearch ||
        this.addGradeSearch
      ) {
        this.scheduleAddStudentSearch();
      }
    },

    onAddPhoneSearchInput() {
      // Phone search is independent — clear name fields so they don't fight
      this.addLastNameSearch = "";
      this.addFirstNameSearch = "";
      this.scheduleAddStudentSearch();
    },

    onAddNameSearchInput() {
      // Name search is independent — clear phone so they don't fight
      this.addPhoneSearch = "";
      this.scheduleAddStudentSearch();
    },

    onAddGradeSearchChange() {
      // Grade alone is optional; only re-search when name/phone already entered
      this.scheduleAddStudentSearch();
    },

    async loadAddGradeOptions() {
      this.addLoadingGrades = true;
      try {
        let url;
        if (this.addIsDayCampMode) {
          // Scoped to the user's DayCampBuilding attribute(s)
          const building = (this.userDayCampBuildings || []).join(",");
          url =
            "api/students/getDayCampGradeList?building=" +
            encodeURIComponent(building);
        } else {
          // Scoped to the user's year-round branch
          const branch = this.userBranch || "";
          url =
            "api/students/getGradeList?branch=" + encodeURIComponent(branch);
        }
        const response = await this.$http.get(url);
        const grades = Array.isArray(response) ? response : [];
        this.addGradeOptions = grades
          .map((g) => (g == null ? "" : String(g).trim()))
          .filter(Boolean)
          .sort();
      } catch (e) {
        this.addGradeOptions = [];
      } finally {
        this.addLoadingGrades = false;
      }
    },

    scheduleAddStudentSearch() {
      if (this.addStudentSearchDebounce) {
        clearTimeout(this.addStudentSearchDebounce);
      }

      const phoneNumber = (this.addPhoneSearch || "").trim();
      const lastName = (this.addLastNameSearch || "").trim();
      const firstName = (this.addFirstNameSearch || "").trim();
      const grade = (this.addGradeSearch || "").trim();
      const hasPhone = phoneNumber.length >= 10;
      const hasName = !!(lastName || firstName || grade);

      if (!hasPhone && !hasName) {
        this.clearAddStudentSearchResults();
        this.addStudentSearchLoading = false;
        return;
      }

      // Phone searches wait for a full number; name can search earlier
      if (!hasPhone && !lastName && !firstName && grade) {
        // Grade alone is too broad — wait for a name too
        this.clearAddStudentSearchResults();
        this.addStudentSearchLoading = false;
        return;
      }

      this.addStudentSearchDebounce = setTimeout(() => {
        this.searchAddStudents();
      }, 400);
    },

    buildAddStudentSearchUrl() {
      const phoneNumber = (this.addPhoneSearch || "").trim();
      const lastName = (this.addLastNameSearch || "").trim();
      const firstName = (this.addFirstNameSearch || "").trim();
      const grade = (this.addGradeSearch || "").trim();
      const isDayCamp = this.addIsDayCampMode;
      // Non-DC: user's year-round branch. DC: leave branch empty.
      const branch = !isDayCamp && this.userBranch ? this.userBranch : "";
      // DC: user's DayCampBuilding attribute(s). Non-DC: leave building empty.
      const building = isDayCamp
        ? (this.userDayCampBuildings || []).join(",")
        : "";

      // Always include every key — empty values are intentional.
      return (
        "api/buschange/SearchStudents?last=" +
        encodeURIComponent(lastName) +
        "&first=" +
        encodeURIComponent(firstName) +
        "&isYiddish=" +
        (this.addSearchNameMode === "yiddish" ? "true" : "false") +
        "&phoneNumber=" +
        encodeURIComponent(phoneNumber) +
        "&grade=" +
        encodeURIComponent(grade) +
        "&isDayCamp=" +
        (isDayCamp ? "true" : "false") +
        "&branch=" +
        encodeURIComponent(branch) +
        "&building=" +
        encodeURIComponent(building)
      );
    },

    async searchAddStudents() {
      const phoneNumber = (this.addPhoneSearch || "").trim();
      const lastName = (this.addLastNameSearch || "").trim();
      const firstName = (this.addFirstNameSearch || "").trim();
      const hasPhone = phoneNumber.length >= 10;
      const hasName = !!(lastName || firstName);

      if (!hasPhone && !hasName) {
        this.clearAddStudentSearchResults();
        return;
      }

      this.addStudentSearchLoading = true;
      this.addStudentSearched = false;
      try {
        const students = await this.$http.get(this.buildAddStudentSearchUrl());
        this.addStudentResults = students || [];
        this.addStudentSearched = true;

        if (this.addStudentResults.length === 1) {
          this.selectAddStudent(this.addStudentResults[0]);
        }
      } catch (e) {
        this.addStudentResults = [];
        this.addStudentSearched = true;
        this.$notify({
          text: "Failed to search students",
          group: "notification",
          type: "error",
        });
      } finally {
        this.addStudentSearchLoading = false;
      }
    },

    getAddStudentHebName(student) {
      if (!student) return "";
      const first = (student.hebName || "").toString().trim();
      const last = (student.lastNameH || student.LastNameH || "")
        .toString()
        .trim();
      if (first && last) return first + " " + last;
      return first || last;
    },

    mapAddStudentResult(student) {
      return {
        studentID: (student.studentID || "").toString().trim(),
        studentName: student.engName || student.name || "",
        hebName: this.getAddStudentHebName(student),
        building: student.building || "",
        branch: student.branch || "",
        class: student.class || student.grade || "",
        section: student.section || "",
        room: student.room || "",
        address1: student.address || student.address1 || "",
        regID: student.regID,
        teacherH: student.teacherH || student.teacher || "",
        teacherH2: student.teacherH2 || student.teacher2 || "",
        regType: student.regType || "",
        weeks: student.weeks || [],
        enrollmentStartDate: student.enrollmentStartDate || null,
        enrollmentEndDate: student.enrollmentEndDate || null,
        accountID: (student.accountID || "").toString().trim(),
        accountName: student.accountName || "",
        homePhone: student.homePhone || "",
        cellPhone: student.cellPhone || "",
        accountAddress1: student.address1 || student.accountAddress1 || "",
      };
    },

    // Bus change grade rules: school keyed by Grade + Branch,
    // day camp by Grade + Building.
    async checkAddStudentGrade(mapped) {
      const isDayCamp = this.addIsDayCampMode;
      const gradeScope = isDayCamp ? mapped.building : mapped.branch;
      if (!this.busChangeGrades.length) {
        await this.loadBusChangeGrades();
      }
      return this.checkGradeAllowsBusChange(
        mapped.class,
        gradeScope,
        isDayCamp
      );
    },

    // Students whose building appears more than once in the current search
    // results can be selected together (e.g. 2 brothers in the same building)
    // via the checkboxes shown next to their names.
    addBuildingCounts() {
      const counts = {};
      (this.addStudentResults || []).forEach((s) => {
        const b = s.building || "";
        counts[b] = (counts[b] || 0) + 1;
      });
      return counts;
    },

    isAddStudentChecked(student) {
      const id = (student.studentID || "").toString().trim();
      return this.addCheckedStudentIds.indexOf(id) !== -1;
    },

    toggleAddStudentCheck(student) {
      const id = (student.studentID || "").toString().trim();
      const idx = this.addCheckedStudentIds.indexOf(id);
      if (idx !== -1) {
        const next = this.addCheckedStudentIds.slice();
        next.splice(idx, 1);
        this.addCheckedStudentIds = next;
        return;
      }

      if (this.addCheckedStudentIds.length > 0) {
        const firstChecked = (this.addStudentResults || []).find(
          (s) =>
            (s.studentID || "").toString().trim() ===
            this.addCheckedStudentIds[0]
        );
        if (
          firstChecked &&
          (firstChecked.building || "") !== (student.building || "")
        ) {
          this.$notify({
            text:
              "Only students from the same building can be selected together.",
            group: "notification",
            type: "warning",
          });
          return;
        }
      }

      this.addCheckedStudentIds = this.addCheckedStudentIds.concat([id]);
    },

    clearAddCheckedStudents() {
      this.addCheckedStudentIds = [];
    },

    // Confirms the checked students (siblings) found in the search results:
    // the first becomes the primary addSelectedStudent (drives duration /
    // deadline / route defaults) and the rest are kept as addSiblingStudents
    // so the same bus change can be saved for all of them at once.
    async confirmAddStudentSelection() {
      const checkedIds = this.addCheckedStudentIds.slice();
      if (!checkedIds.length) return;

      const checkedStudents = checkedIds
        .map((id) =>
          (this.addStudentResults || []).find(
            (s) => (s.studentID || "").toString().trim() === id
          )
        )
        .filter(Boolean);
      if (!checkedStudents.length) return;

      const [first, ...rest] = checkedStudents;
      await this.selectAddStudent(first);

      // selectAddStudent bails out (without setting addSelectedStudent) when
      // the primary student's grade isn't allowed for bus change.
      if (!this.addSelectedStudent) return;

      const siblings = [];
      const skipped = [];
      for (const s of rest) {
        const mapped = this.mapAddStudentResult(s);
        const gradeCheck = await this.checkAddStudentGrade(mapped);
        if (!gradeCheck.allowed) {
          skipped.push(
            mapped.studentName || mapped.hebName || mapped.studentID
          );
          continue;
        }
        siblings.push(mapped);
      }

      this.addSiblingStudents = siblings;
      this.addCheckedStudentIds = [];
      this.addSiblingsShareSameBus = false;
      this.filterAddRoutesByBuilding();

      if (skipped.length) {
        this.$notify({
          text:
            "Not added (not eligible for bus change): " + skipped.join(", "),
          group: "notification",
          type: "warning",
        });
      }
    },

    removeAddSiblingStudent(sibling) {
      this.addSiblingStudents = this.addSiblingStudents.filter(
        (s) => s.studentID !== sibling.studentID
      );
      if (!this.addSiblingStudents.length) {
        this.addSiblingsShareSameBus = false;
      }
      this.filterAddRoutesByBuilding();
    },

    async selectAddStudent(student) {
      const mapped = this.mapAddStudentResult(student);

      const gradeCheck = await this.checkAddStudentGrade(mapped);
      if (!gradeCheck.allowed) {
        this.addGradeError = gradeCheck.error;
        this.$notify({
          text: gradeCheck.error,
          group: "notification",
          type: "error",
        });
        return;
      }
      this.addGradeError = "";

      const isDayCamp = this.addIsDayCampMode;
      const gradeScope = isDayCamp ? mapped.building : mapped.branch;

      this.addSelectedStudent = mapped;
      this.addDayCampEnrollment = this.addIsDayCampMode ? mapped : null;
      this.addDeadlineOverrideToday = false;
      this.addDeadlineChoiceDialog = false;
      this.addDestinationSelectedAccount = null;
      this.addAccountPhones = [];
      this.addNotificationManuallyEdited = false;
      this.addNotificationEditing = false;
      this.addNotificationEditBackup = null;
      this.addNotificationPhoneSearch = null;
      this.addForm.notificationPhoneNumber = "";
      this.addForm.notificationType = "";

      // Destination ("Where To?") is intentionally left empty — it's where the
      // child is going, not the family's own account info.
      // Notification phones come from the destination account only (not the student).

      this.filterAddRoutesByBuilding();

      await this.loadAddSelectedGradeDeadlines(
        mapped.class,
        gradeScope,
        isDayCamp
      );

      if (this.isWithinAddDeadlineGraceWindow()) {
        const info = this.getAddTodayDeadline();
        this.addDeadlineChoiceClosedDay = !!(info && info.closedDay);
        this.addDeadlineChoiceDialog = true;
        return;
      }

      // Re-apply duration so Today/2 Days respect this grade's deadline
      if (this.addDurationOption !== "custom") {
        this.applyAddDurationPreset(this.addDurationOption);
      }
    },

    clearAddStudent() {
      // Return to search so the user can find a different student
      this.addSelectedStudent = null;
      this.addSiblingStudents = [];
      this.addSiblingsShareSameBus = false;
      this.addCheckedStudentIds = [];
      this.addDayCampEnrollment = null;
      this.addAccountPhones = [];
      this.addSelectedGradeDeadlines = [];
      this.addGradeError = "";
      this.addDeadlineOverrideToday = false;
      this.addDeadlineChoiceDialog = false;
      this.addForm.notificationPhoneNumber = "";
      this.addForm.notificationType = "";
      this.addNotificationManuallyEdited = false;
      this.addNotificationEditing = false;
      this.addNotificationEditBackup = null;
      this.addNotificationPhoneSearch = null;
      this.filterAddRoutesByBuilding();
    },

    onAddDestinationPhoneInput() {
      if (this.addDestinationPhoneDebounce) {
        clearTimeout(this.addDestinationPhoneDebounce);
      }

      const phone = (this.addForm.busChangePhoneNumber || "").trim();
      const digits = phone.replace(/[-\s()]/g, "");
      if (!phone || digits.length < 10) {
        if (digits !== this.addDestinationPhoneLookupDone) {
          this.addDestinationPhoneLookupDone = "";
        }
        return;
      }

      // Already looked up / declined for this exact phone — don't re-prompt
      if (this.addDestinationPhoneLookupDone === digits) {
        return;
      }

      this.addDestinationPhoneDebounce = setTimeout(() => {
        this.lookupAddDestinationPhone(digits);
      }, 400);
    },

    async lookupAddDestinationPhone(digits) {
      try {
        const accounts = await this.$http.get(
          `api/students/getAccountByPhoneNumber?phoneNumber=${digits}`
        );
        const list = accounts || [];
        this.addDestinationPhoneLookupDone = digits;

        if (!list.length) {
          return;
        }

        this.addDestinationPhoneAccounts = list;
        this.addDestinationPhoneDialog = true;
      } catch (e) {
        // Silent — typing a phone shouldn't toast on every miss/failure
        this.addDestinationPhoneLookupDone = digits;
      }
    },

    async selectAddDestinationAccount(account) {
      this.addDestinationSelectedAccount = account;
      this.addForm.busChangeAccountID = (account.accountID || "")
        .toString()
        .trim();
      this.addForm.busChangeName = (account.lastName || "").trim();
      if (account.address1) {
        this.addForm.busChangeAddress = account.address1;
        this.addAddressSearchText = account.address1;
      }

      await this.loadAddAccountPhones(this.addForm.busChangeAccountID);
      this.applyAddNotificationDefaults();
      // Always refresh geo when destination account changes (not only when empty)
      this.fillAddGeoPhoneFromAccount();

      this.addDestinationPhoneDialog = false;
      this.addDestinationPhoneAccounts = [];
    },

    declineAddDestinationAccount() {
      this.addDestinationPhoneDialog = false;
      this.addDestinationPhoneAccounts = [];
      // Keep lookupDone so we don't re-popup until the phone changes
    },

    buildAddPhoneSelectItems(includeTypedDestinationPhone) {
      const items = [];
      const seen = {};
      const pushPhone = (raw, phoneType) => {
        if (!raw) return;
        const digits = String(raw).replace(/[-\s()]/g, "");
        if (!digits || digits.length < 7 || seen[digits]) return;
        seen[digits] = true;
        const formatted = this.formatPhone(raw) || String(raw).trim();
        const type = (phoneType || "")
          .toString()
          .trim()
          .toUpperCase();
        items.push({
          text: type ? type + ": " + formatted : formatted,
          value: formatted,
        });
      };

      if (includeTypedDestinationPhone) {
        pushPhone(this.addForm.busChangePhoneNumber, "");
      }

      (this.addAccountPhones || []).forEach((p) => {
        pushPhone(p.phoneNum || p.phoneNumber || p.phone, p.phoneType);
      });

      return items;
    },

    async loadAddAccountPhones(accountID) {
      const id = (accountID || "").toString().trim();
      if (!id) {
        this.addAccountPhones = [];
        return;
      }
      try {
        const phones = await this.$http.get(
          "api/accounts/getAccountPhones?accountID=" + encodeURIComponent(id)
        );
        this.addAccountPhones = Array.isArray(phones) ? phones : [];
      } catch (e) {
        this.addAccountPhones = [];
      }
    },

    applyAddNotificationDefaults() {
      if (this.addNotificationManuallyEdited) return;

      const phones = this.addAccountPhones || [];
      const findByType = (type) =>
        phones.find(
          (p) =>
            (p.phoneType || "")
              .toString()
              .trim()
              .toUpperCase() === type
        );

      const chosen = findByType("MCEL") || findByType("CEL");
      if (chosen && (chosen.phoneNum || chosen.phoneNumber || chosen.phone)) {
        const raw = chosen.phoneNum || chosen.phoneNumber || chosen.phone;
        this.addForm.notificationPhoneNumber = this.formatPhone(raw) || raw;
        this.addForm.notificationType = "MESSAGE";
        return;
      }

      // No MCEL/CEL — clear auto defaults
      this.addForm.notificationPhoneNumber = "";
      this.addForm.notificationType = "";
    },

    fillAddGeoPhoneFromAccount() {
      const phones = this.addAccountPhones || [];
      const findByType = (type) =>
        phones.find(
          (p) =>
            (p.phoneType || "")
              .toString()
              .trim()
              .toUpperCase() === type
        );

      const chosen =
        findByType("MCEL") ||
        findByType("CEL") ||
        findByType("PHON") ||
        phones[0];
      const raw = chosen
        ? chosen.phoneNum || chosen.phoneNumber || chosen.phone
        : "";
      this.addForm.geoPhoneNumber = raw ? this.formatPhone(raw) || raw : "";
    },

    onAddNotificationPhoneEdited() {
      this.addNotificationManuallyEdited = true;
    },

    onAddGeoPhoneChange(val) {
      this.addForm.geoPhoneNumber = this.normalizeAddPhoneValue(val);
    },

    // Combobox free-text only commits to v-model on blur — keep form in sync
    // while typing so Done/Save don't need to leave the field first.
    onAddNotificationPhoneTyping(val) {
      if (val === null || val === undefined) return;
      this.addForm.notificationPhoneNumber = this.normalizeAddPhoneValue(val);
      this.addNotificationManuallyEdited = true;
    },

    commitAddNotificationPhoneSearch() {
      if (
        this.addNotificationPhoneSearch === null ||
        this.addNotificationPhoneSearch === undefined ||
        this.addNotificationPhoneSearch === ""
      ) {
        return;
      }
      this.onAddNotificationPhoneTyping(this.addNotificationPhoneSearch);
    },

    startAddNotificationEdit() {
      this.addNotificationEditBackup = {
        notificationPhoneNumber: this.addForm.notificationPhoneNumber || "",
        notificationType: this.addForm.notificationType || "",
        manuallyEdited: this.addNotificationManuallyEdited,
      };
      this.addNotificationPhoneSearch =
        this.addForm.notificationPhoneNumber || null;
      this.addNotificationEditing = true;
    },

    doneAddNotificationEdit() {
      this.commitAddNotificationPhoneSearch();
      this.addNotificationPhoneSearch = null;
      this.addNotificationEditBackup = null;
      this.addNotificationEditing = false;
    },

    cancelAddNotificationEdit() {
      if (this.addNotificationEditBackup) {
        this.addForm.notificationPhoneNumber = this.addNotificationEditBackup.notificationPhoneNumber;
        this.addForm.notificationType = this.addNotificationEditBackup.notificationType;
        this.addNotificationManuallyEdited = this.addNotificationEditBackup.manuallyEdited;
      }
      this.addNotificationPhoneSearch = null;
      this.addNotificationEditBackup = null;
      this.addNotificationEditing = false;
    },

    sectionRequestBuilding(request) {
      return String((request && (request.building || request.Building)) || "").trim();
    },

    restoreSectionBuildingFilters() {
      try {
        const raw = localStorage.getItem("busChangeRequests.sectionBuildings");
        const stored = raw ? JSON.parse(raw) : null;
        if (!stored || typeof stored !== "object") return;
        ["pending", "enteredToday", "stillOpen"].forEach((key) => {
          const f = this.sectionFilters[key];
          if (!f || !Array.isArray(stored[key])) return;
          f.buildings = stored[key]
            .map((b) => String(b || "").trim())
            .filter(Boolean);
        });
      } catch (e) {
        // ignore bad stored value
      }
    },

    saveSectionBuildingFilters() {
      try {
        localStorage.setItem(
          "busChangeRequests.sectionBuildings",
          JSON.stringify({
            pending: this.sectionFilters.pending.buildings || [],
            enteredToday: this.sectionFilters.enteredToday.buildings || [],
            stillOpen: this.sectionFilters.stillOpen.buildings || [],
          })
        );
      } catch (e) {
        // ignore quota / private mode
      }
    },

    userAssignedBranchCount() {
      return (this.userBranch || "")
        .split(",")
        .map((b) => b.trim())
        .filter(Boolean).length;
    },

    showSectionBuildingFilter(section) {
      if (this.canManageRoutePresets()) return true;
      if (this.userAssignedBranchCount() > 1) return true;
      if ((this.userDayCampBuildings || []).length > 1) return true;
      return this.sectionBuildingOptions(section).length > 1;
    },

    sectionBuildingOptions(section) {
      const seen = {};
      (section.items || []).forEach((request) => {
        const b = this.sectionRequestBuilding(request);
        if (b) seen[b.toUpperCase()] = b;
      });
      return Object.keys(seen)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map((key) => seen[key]);
    },

    sectionFilterActive(section) {
      const f = this.sectionFilters[section.key];
      if (!f) return false;
      return !!((f.search || "").trim() || (f.buildings || []).length);
    },

    clearSectionFilter(section) {
      const f = this.sectionFilters[section.key];
      if (!f) return;
      f.search = "";
      f.buildings = [];
    },

    filteredSectionItems(section) {
      const items = section.items || [];
      const f = this.sectionFilters[section.key];
      if (!f || !this.sectionFilterActive(section)) return items;

      const buildings = (f.buildings || []).map((b) => String(b).toUpperCase());
      const q = (f.search || "").trim().toLowerCase();
      const qDigits = q.replace(/\D/g, "");

      return items.filter((request) => {
        if (
          buildings.length &&
          !buildings.includes(this.sectionRequestBuilding(request).toUpperCase())
        ) {
          return false;
        }
        if (!q) return true;

        const phones = this.getEndBusChangePhones(request);
        const haystack = [
          request.studentNameHeb,
          request.studentName,
          request.studentID,
          request.id || request.ID,
          this.getEndBusChangeName(request),
          request.busChangeAddress,
          this.sectionRequestBuilding(request),
          request.class,
          request.branch,
          phones.join(" "),
        ]
          .join(" ")
          .toLowerCase();
        if (haystack.indexOf(q) !== -1) return true;
        if (qDigits.length >= 3) {
          const phoneDigits = phones
            .map((p) => String(p).replace(/\D/g, ""))
            .join(" ");
          return phoneDigits.indexOf(qDigits) !== -1;
        }
        return false;
      });
    },

    getAddEffectiveBaseDate() {
      const today = moment().startOf("day");
      if (this.isAddTodayDeadlinePassed()) {
        return today.clone().add(1, "day");
      }
      return today;
    },

    getAddTodayDeadline() {
      if (
        !this.addSelectedStudent ||
        !this.addSelectedGradeDeadlines ||
        !this.addSelectedGradeDeadlines.length
      ) {
        return null;
      }

      const today = moment();
      const todayStr = today.format("YYYY-MM-DD");

      const deadline = deadlineForDate(
        this.addSelectedGradeDeadlines,
        todayStr
      );
      if (isNoBusChangeDeadline(deadline)) {
        return {
          closedDay: true,
          deadlineSeconds: 0,
          currentSeconds: 1,
          formatted: "",
          minutesPast: null,
        };
      }
      if (!deadline || !deadline.deadline) return null;

      const deadlineParts = deadline.deadline.split(":");
      if (deadlineParts.length < 2) return null;

      const deadlineSeconds =
        parseInt(deadlineParts[0], 10) * 3600 +
        parseInt(deadlineParts[1], 10) * 60 +
        (deadlineParts[2] ? parseFloat(deadlineParts[2]) : 0);
      const currentSeconds =
        today.hours() * 3600 + today.minutes() * 60 + today.seconds();

      let hours = parseInt(deadlineParts[0], 10);
      const minutes = deadlineParts[1];
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;

      return {
        deadlineSeconds: deadlineSeconds,
        currentSeconds: currentSeconds,
        formatted: hours + ":" + minutes + " " + ampm,
        minutesPast: Math.floor((currentSeconds - deadlineSeconds) / 60),
      };
    },

    isAddTodayDeadlinePassed() {
      if (this.addDeadlineOverrideToday) return false;
      const info = this.getAddTodayDeadline();
      if (!info) return false;
      if (info.closedDay) return true;
      return info.currentSeconds > info.deadlineSeconds;
    },

    isWithinAddDeadlineGraceWindow() {
      const info = this.getAddTodayDeadline();
      if (!info) return false;
      if (info.closedDay) return true;
      const graceSeconds = 45 * 60;
      return (
        info.currentSeconds > info.deadlineSeconds &&
        info.currentSeconds <= info.deadlineSeconds + graceSeconds
      );
    },

    applyAddDurationAfterDeadlineChoice() {
      if (this.addDurationOption !== "custom") {
        this.applyAddDurationPreset(this.addDurationOption);
      }
    },

    chooseAddDeadlineToday() {
      this.addDeadlineOverrideToday = true;
      this.addDeadlineChoiceDialog = false;
      this.applyAddDurationAfterDeadlineChoice();
    },

    chooseAddDeadlineTomorrow() {
      this.addDeadlineOverrideToday = false;
      this.addDeadlineChoiceDialog = false;
      this.applyAddDurationAfterDeadlineChoice();
    },

    async loadAddSelectedGradeDeadlines(grade, scope, isDayCamp = false) {
      if (!this.busChangeGrades.length) {
        await this.loadBusChangeGrades();
      }
      const setup = this.getGradeSetup(grade, scope, isDayCamp);
      if (!setup || !setup.id) {
        this.addSelectedGradeDeadlines = [];
        return;
      }
      try {
        const response = await this.$http.get(
          "api/busChange/getBusChangeDeadlines?gradeId=" + setup.id
        );
        this.addSelectedGradeDeadlines = Array.isArray(response)
          ? response
          : [];
      } catch (e) {
        this.addSelectedGradeDeadlines = [];
      }
    },

    // Process-dialog / request: Day Camp season + enrollment → building scope;
    // otherwise School → branch scope.
    getProcessDeadlineGradeContext() {
      const req = this.selectedRequest;
      const useDayCamp = !!(
        this.dayCampEnrollment && this.isWithinSummerSeason()
      );
      if (useDayCamp) {
        return {
          isDayCamp: true,
          grade:
            this.dayCampEnrollment.class ||
            this.dayCampEnrollment.grade ||
            (req && req.class) ||
            "",
          scope: this.dayCampEnrollment.building || "",
        };
      }
      return {
        isDayCamp: false,
        grade: (req && req.class) || "",
        scope: (req && req.branch) || "",
      };
    },

    addAddressNoFilter() {
      // Server already filters — show all returned suggestions
      return true;
    },

    onAddAddressSearchInput(value) {
      if (this.addAddressSearchDebounce) {
        clearTimeout(this.addAddressSearchDebounce);
      }

      const query = (value || "").toString().trim();
      if (query.length < 2) {
        this.addAddressSuggestions = [];
        this.addAddressSearchLoading = false;
        return;
      }

      this.addAddressSearchDebounce = setTimeout(() => {
        this.searchAddAddresses(query);
      }, 300);
    },

    async searchAddAddresses(query) {
      this.addAddressSearchLoading = true;
      try {
        const url =
          "api/buschange/SearchAddresses?q=" +
          encodeURIComponent(query) +
          "&top=25";
        const response = await this.$http.get(url);
        const list = Array.isArray(response) ? response : [];
        // Accept either ["123 Main St", ...] or [{ address: "..." }, ...]
        this.addAddressSuggestions = list
          .map((item) => {
            if (typeof item === "string") return item.trim();
            if (item && item.address) return String(item.address).trim();
            if (item && item.address1) return String(item.address1).trim();
            return "";
          })
          .filter(Boolean);
      } catch (e) {
        this.addAddressSuggestions = [];
      } finally {
        this.addAddressSearchLoading = false;
      }
    },

    onAddBusChangeAddressChange() {
      this.scheduleAutoSelectBusFromAddress();
    },

    scheduleAutoSelectBusFromAddress() {
      if (this.addAutoSelectBusDebounce) {
        clearTimeout(this.addAutoSelectBusDebounce);
      }
      this.addAutoSelectBusDebounce = setTimeout(() => {
        this.tryAutoSelectBusFromAddress();
      }, 400);
    },

    async tryAutoSelectBusFromAddress() {
      const address = this.normalizeAddAddressValue(
        this.addForm && this.addForm.busChangeAddress
      );
      if (!address || address.length < 4) {
        return;
      }

      const pickupIds = (this.addFilteredPickupRoutes || [])
        .map((r) => r.id)
        .filter((id) => id != null);
      const dropoffIds = (this.addFilteredDropoffRoutes || [])
        .map((r) => r.id)
        .filter((id) => id != null);
      const routeIds = pickupIds.concat(dropoffIds);
      if (!routeIds.length) {
        return;
      }

      const key =
        address.toLowerCase() +
        "|" +
        routeIds
          .slice()
          .sort((a, b) => a - b)
          .join(",");
      if (key === this.addLastAutoSelectKey || this.addAutoSelectingBus) {
        return;
      }

      // Destination address (or the account behind it) changed since the last
      // match — drop any previously selected bus/stop so a stale assignment
      // from the old address doesn't linger, whether it was auto- or hand-picked.
      if (this.addSelectedPickupRoute || this.addSelectedDropoffRoute) {
        this.clearAddPickupSelection();
        this.clearAddDropoffSelection();
      }

      this.addAutoSelectingBus = true;
      try {
        const match = await this.$http.post(
          "api/buschange/FindRoutesByStopAddress",
          { address, routeIds }
        );
        if (!match) {
          return;
        }

        const pickupRouteId = match.pickupRouteId || match.PickupRouteId;
        const dropoffRouteId = match.dropoffRouteId || match.DropoffRouteId;
        const pickupStopNumber =
          match.pickupStopNumber != null
            ? match.pickupStopNumber
            : match.PickupStopNumber;
        const dropoffStopNumber =
          match.dropoffStopNumber != null
            ? match.dropoffStopNumber
            : match.DropoffStopNumber;

        if (!pickupRouteId && !dropoffRouteId) {
          return;
        }

        await this.applyAutoSelectedRoutes({
          pickupRouteId,
          pickupStopNumber,
          dropoffRouteId,
          dropoffStopNumber,
        });
        this.addLastAutoSelectKey = key;
      } catch (e) {
        // Silent — typing an address shouldn't toast when no stop matches
      } finally {
        this.addAutoSelectingBus = false;
        const current = this.normalizeAddAddressValue(
          this.addForm && this.addForm.busChangeAddress
        );
        if (current && current.toLowerCase() !== address.toLowerCase()) {
          this.scheduleAutoSelectBusFromAddress();
        }
      }
    },

    async applyAutoSelectedRoutes(match) {
      if (!match) {
        return;
      }

      if (!this.addIsAfternoonOnly && match.pickupRouteId) {
        const route = (this.addFilteredPickupRoutes || []).find(
          (r) => r.id == match.pickupRouteId
        );
        if (route) {
          this.addSelectedPickupRoute = route;
          this.addSelectedPickupStopNumber = null;
          this.addPickupRouteStops = [];
          await this.loadAddRouteStops("pickup", route.id);
        }
      }

      if (match.dropoffRouteId) {
        const route = (this.addFilteredDropoffRoutes || []).find(
          (r) => r.id == match.dropoffRouteId
        );
        if (route) {
          this.addSelectedDropoffRoute = route;
          this.addSelectedDropoffStopNumber = null;
          this.addDropoffRouteStops = [];
          await this.loadAddRouteStops("dropoff", route.id);
        }
      }

      if (match.pickupStopNumber != null && match.pickupStopNumber !== "") {
        this.addSelectedPickupStopNumber = match.pickupStopNumber;
      }
      if (match.dropoffStopNumber != null && match.dropoffStopNumber !== "") {
        this.addSelectedDropoffStopNumber = match.dropoffStopNumber;
      } else if (this.addSelectedPickupStopNumber) {
        this.copyAddPickupStopToDropoffIfSameRoute(
          this.addSelectedPickupStopNumber
        );
      }
    },

    selectAddDuration(option) {
      this.addDurationOption = option;
      if (option !== "custom") {
        this.applyAddDurationPreset(option);
      } else if (!this.addForm.startDate) {
        // Give the custom pickers a sensible starting point
        this.applyAddDurationPreset("today");
      }
    },

    applyAddDurationPreset(option) {
      // Base date is today, or tomorrow if today's bus-change deadline has passed
      const daysMap = { today: 0, "2days": 1, "1week": 7, "2weeks": 14 };
      const days = daysMap[option];
      if (days == null) return;
      const start = this.getAddEffectiveBaseDate();
      const end = start.clone().add(days, "days");
      this.addForm.startDate = start.format("YYYY-MM-DD");
      this.addForm.endDate = end.format("YYYY-MM-DD");
      this.addForm.startPeriod = "PM";
      this.addForm.endPeriod = "PM";
    },

    async loadAddBusRoutes() {
      this.addLoadingBusRoutes = true;
      try {
        // In day camp season, ask the backend for only the summer routes.
        let url = "api/buschange/GetBusRoutes";
        if (this.isWithinSummerSeason()) {
          url +=
            `?season=${encodeURIComponent(this.addSummerSeasonName)}` +
            "&kind=DayCamp";
        }
        this.addBusRoutes = await this.$http.get(url);
        this.filterAddRoutesByBuilding();
      } catch (e) {
        this.addBusRoutes = [];
        this.addFilteredPickupRoutes = [];
        this.addFilteredDropoffRoutes = [];
      } finally {
        this.addLoadingBusRoutes = false;
      }
    },

    filterAddRoutesByBuilding() {
      const students = this.addDisplayStudents;
      // Default: a shared bus must list every selected student's grade.
      // Override when staff confirms brothers ride together anyway.
      const requireAllGrades =
        this.addSiblingsHaveDifferentGrades && !this.addSiblingsShareSameBus;

      const byDirection = (direction) => {
        return (this.addBusRoutes || []).filter((r) => {
          if (r.direction !== direction || !routeHasDriver(r)) return false;
          if (!students.length) return true;
          return routeMatchesStudents(r, students, requireAllGrades);
        });
      };

      this.addFilteredPickupRoutes = this.filterAddRoutesBySeason(
        byDirection("Pickup")
      );
      this.addFilteredDropoffRoutes = this.filterAddRoutesBySeason(
        byDirection("Dropoff")
      );

      // A bus picked from an extra list (another building, going home
      // earlier) stays selected as long as it is still offered there.
      if (
        this.addSelectedPickupRoute &&
        !this.isRouteInPicker(
          this.addSelectedPickupRoute,
          this.addFilteredPickupRoutes,
          this.addPickupExtraGroups
        )
      ) {
        this.clearAddPickupSelection();
      }
      if (
        this.addSelectedDropoffRoute &&
        !this.isRouteInPicker(
          this.addSelectedDropoffRoute,
          this.addFilteredDropoffRoutes,
          this.addDropoffExtraGroups
        )
      ) {
        this.clearAddDropoffSelection();
      }

      this.scheduleAutoSelectBusFromAddress();
    },

    filterAddRoutesBySeason(routes) {
      if (!this.addDayCampEnrollment) return routes;
      if (!routes.some((r) => r.season || r.kind)) return routes;
      const target = this.addSummerSeasonName.trim().toLowerCase();
      return routes.filter((r) => {
        const kind = (r.kind || "").trim().toLowerCase();
        if (kind === "daycamp") return true;
        const season = (r.season || "").trim().toLowerCase();
        if (!season) return true;
        return season === target;
      });
    },

    async onAddPickupRouteChange(route) {
      this.addSelectedPickupStopNumber = null;
      this.addPickupRouteStops = [];

      if (!route) {
        return;
      }

      await this.loadAddRouteStops("pickup", route.id);
    },

    async onAddDropoffRouteChange(route) {
      this.addSelectedDropoffStopNumber = null;
      this.addDropoffRouteStops = [];

      if (!route) {
        return;
      }

      await this.loadAddRouteStops("dropoff", route.id);
      this.copyAddPickupStopToDropoffIfSameRoute(
        this.addSelectedPickupStopNumber
      );
    },

    onAddPickupStopNumberChange(stopNumber) {
      this.copyAddPickupStopToDropoffIfSameRoute(stopNumber);
    },

    copyAddPickupStopToDropoffIfSameRoute(stopNumber) {
      if (stopNumber == null || stopNumber === "") return;
      if (
        !this.isSameBusRoute(
          this.addSelectedPickupRoute,
          this.addSelectedDropoffRoute
        )
      ) {
        return;
      }
      if (
        !this.addDropoffRouteStops ||
        !this.addDropoffRouteStops.some((s) => s.stopNumber === stopNumber)
      ) {
        return;
      }
      this.addSelectedDropoffStopNumber = stopNumber;
    },

    async loadAddRouteStops(direction, routeId) {
      if (!routeId) {
        return;
      }

      const loadingKey =
        direction === "pickup"
          ? "addLoadingPickupStops"
          : "addLoadingDropoffStops";
      const itemsKey =
        direction === "pickup" ? "addPickupRouteStops" : "addDropoffRouteStops";

      this[loadingKey] = true;
      try {
        const stops = await this.$http.get(
          `api/buschange/GetRouteStops?routeId=${routeId}`
        );
        this[itemsKey] = Array.isArray(stops) ? stops : [];
      } catch (e) {
        this[itemsKey] = [];
      } finally {
        this[loadingKey] = false;
      }
    },

    getAddEnrollmentDateWarning() {
      if (!this.addDayCampEnrollment) return null;

      const weeks = this.addDayCampEnrollment.weeks || [];
      if (!weeks.length) return null;

      const start = this.addForm.startDate;
      const end = this.addForm.endDate;
      if (!start || !end) return null;

      const reqStart = moment(start, "YYYY-MM-DD").startOf("day");
      const reqEnd = moment(end, "YYYY-MM-DD").startOf("day");
      if (!reqStart.isValid() || !reqEnd.isValid()) return null;
      if (reqEnd.isBefore(reqStart)) return null;

      const ranges = weeks
        .filter((w) => w.startDate && w.endDate)
        .map((w) => ({
          s: moment(w.startDate).startOf("day"),
          e: moment(w.endDate).startOf("day"),
        }))
        .filter((r) => r.s.isValid() && r.e.isValid());
      if (!ranges.length) return null;

      // Every day in the requested range must fall within an enrolled week
      // (small tolerance bridges weekend gaps between adjacent weeks).
      const covers = (day) =>
        ranges.some(
          (r) =>
            day.isSameOrAfter(r.s) &&
            day.isSameOrBefore(r.e.clone().add(2, "days"))
        );

      const cursor = reqStart.clone();
      let hasUncovered = false;
      while (cursor.isSameOrBefore(reqEnd)) {
        if (!covers(cursor)) {
          hasUncovered = true;
          break;
        }
        cursor.add(1, "day");
      }
      if (!hasUncovered) return null;

      return (
        "This student is only enrolled in day camp for: " +
        this.getEnrollmentWeeksLabel(this.addDayCampEnrollment) +
        ". The selected date range includes dates outside their enrollment."
      );
    },

    // Combobox may bind { text, value } or "MCEL: 347-..." — always digits/phone string
    normalizeAddPhoneValue(val) {
      if (val == null || val === "") return "";
      let raw = val;
      if (typeof val === "object") {
        raw = val.value || val.text || val.phoneNum || val.phone || "";
      }
      const digits = String(raw).replace(/\D/g, "");
      if (
        digits.length === 10 ||
        (digits.length === 11 && digits.charAt(0) === "1")
      ) {
        return this.formatPhone(digits) || digits;
      }
      return String(raw).trim();
    },

    isValidPhoneDigits(phone) {
      if (!phone) return true;
      const digits = String(phone).replace(/\D/g, "");
      return (
        digits.length === 10 ||
        (digits.length === 11 && digits.charAt(0) === "1")
      );
    },

    normalizeAddAddressValue(val) {
      if (val == null || val === "") return "";
      if (typeof val === "object") {
        return String(
          val.address || val.address1 || val.value || val.text || ""
        ).trim();
      }
      return String(val).trim();
    },

    async saveAddBusChange() {
      if (!this.addSelectedStudent) return;

      // Flush in-progress notification combobox text before validating/saving
      if (this.addNotificationEditing) {
        this.commitAddNotificationPhoneSearch();
      }

      // Normalize combobox object values so .replace / save never crash
      this.addForm.busChangePhoneNumber = this.normalizeAddPhoneValue(
        this.addForm.busChangePhoneNumber
      );
      this.addForm.geoPhoneNumber = this.normalizeAddPhoneValue(
        this.addForm.geoPhoneNumber
      );
      this.addForm.notificationPhoneNumber = this.normalizeAddPhoneValue(
        this.addForm.notificationPhoneNumber
      );
      this.addForm.busChangeAddress = this.normalizeAddAddressValue(
        this.addForm.busChangeAddress
      );

      if (!this.addForm.startDate) {
        this.$notify({
          text: "Start Date is required",
          group: "notification",
          type: "error",
        });
        return;
      }

      const todayStr = moment().format("YYYY-MM-DD");
      const closed = closedDayMessage(
        deadlineForDate(this.addSelectedGradeDeadlines, this.addForm.startDate)
      );
      if (
        closed &&
        !(this.addDeadlineOverrideToday && this.addForm.startDate === todayStr)
      ) {
        this.$notify({
          text: closed + " Saving anyway.",
          group: "notification",
          type: "warning",
        });
      }

      if (!this.addForm.startPeriod) {
        this.$notify({
          text: "Starting Run is required",
          group: "notification",
          type: "error",
        });
        return;
      }

      if (!this.addForm.endPeriod) {
        this.$notify({
          text: "Ending After is required",
          group: "notification",
          type: "error",
        });
        return;
      }

      if (!this.addForm.busChangeAddress) {
        this.$notify({
          text: "Destination Address is required",
          group: "notification",
          type: "error",
        });
        return;
      }

      if (
        this.addForm.busChangePhoneNumber &&
        !this.isValidPhoneDigits(this.addForm.busChangePhoneNumber)
      ) {
        this.$notify({
          text:
            "Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return;
      }

      if (
        this.addForm.geoPhoneNumber &&
        !this.isValidPhoneDigits(this.addForm.geoPhoneNumber)
      ) {
        this.$notify({
          text:
            "Geo Tracking Phone is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return;
      }

      if (
        this.addForm.notificationPhoneNumber &&
        !this.isValidPhoneDigits(this.addForm.notificationPhoneNumber)
      ) {
        this.$notify({
          text:
            "Notification Phone is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return;
      }

      const addAllSelectedStudents = [
        this.addSelectedStudent,
        ...this.addSiblingStudents,
      ].filter(Boolean);

      const isDayCamp = this.addIsDayCampMode;
      if (!this.busChangeGrades.length) {
        await this.loadBusChangeGrades();
      }
      for (const student of addAllSelectedStudents) {
        const gradeScope = isDayCamp ? student.building : student.branch;
        const gradeCheck = this.checkGradeAllowsBusChange(
          student.class,
          gradeScope,
          isDayCamp
        );
        if (!gradeCheck.allowed) {
          this.addGradeError = gradeCheck.error;
          this.$notify({
            text:
              (student.hebName || student.studentName || student.studentID) +
              ": " +
              gradeCheck.error,
            group: "notification",
            type: "error",
          });
          return;
        }
      }
      this.addGradeError = "";

      // Validate endDate is not before startDate
      if (this.addForm.endDate && this.addForm.startDate) {
        const startDate = moment(this.addForm.startDate);
        const endDate = moment(this.addForm.endDate);

        if (endDate.isBefore(startDate, "day")) {
          this.$notify({
            text: "End Date cannot be before Start Date",
            group: "notification",
            type: "error",
          });
          return;
        }

        if (
          endDate.isSame(startDate, "day") &&
          this.isEndPeriodBeforeStartPeriod(
            this.addForm.startPeriod,
            this.addForm.endPeriod
          )
        ) {
          this.$notify({
            text: "Ending After cannot be before Starting Run on the same day.",
            group: "notification",
            type: "error",
          });
          return;
        }
      }

      // Afternoon-only: pickup is not used
      if (this.addIsAfternoonOnly) {
        this.clearAddPickupSelection();
      }

      // Warn when routes are missing / incomplete
      if (!this.addSkipRouteWarning && this.addNeedsIncompleteRouteWarning()) {
        this.warningDialogSource = "add";
        this.incompleteRouteDialog = true;
        return;
      }

      // Warn when a selected route has no Geo stop
      if (!this.addSkipGeoStopWarning) {
        const missingGeoStopMsg = this.getAddMissingGeoStopMessage();
        if (missingGeoStopMsg) {
          this.warningDialogSource = "add";
          this.missingGeoStopMessage = missingGeoStopMsg;
          this.missingGeoStopDialog = true;
          return;
        }
      }

      // Confirm / send notification when at least one route is assigned
      if (!this.skipNotification) {
        const hasPickup =
          !this.addIsAfternoonOnly &&
          this.addSelectedPickupRoute &&
          this.addSelectedPickupRoute.id;
        const hasDropoff =
          this.addSelectedDropoffRoute && this.addSelectedDropoffRoute.id;
        if (hasPickup || hasDropoff) {
          this.notificationDialogSource = "add";
          const hasPhone = !!this.addForm.notificationPhoneNumber;
          const hasType = !!this.addForm.notificationType;
          if (!hasPhone || !hasType) {
            this.missingNotificationFieldsDialog = true;
            return;
          }
          this.notificationConfirmDialog = true;
          return;
        }
      }

      this.addSaving = true;
      try {
        // Shared info (dates, destination, routes) is entered once and
        // applied to every selected student (e.g. 2 brothers going to the
        // same place, same days) so mom only has to say it once.
        const buildAddBusChangeData = (student) => ({
          studentID: (student.studentID || "").toString().trim(),
          startDate: this.addForm.startDate
            ? moment(this.addForm.startDate).format("MM/DD/YYYY")
            : null,
          endDate: this.addForm.endDate
            ? moment(this.addForm.endDate).format("MM/DD/YYYY")
            : null,
          startPeriod: this.addForm.startPeriod,
          endPeriod: this.addForm.endPeriod,
          homeAddress: student.address1 || "",
          busChangeAddress: this.addForm.busChangeAddress,
          busChangePhoneNumber: this.addForm.busChangePhoneNumber,
          geoPhoneNumber: this.addForm.geoPhoneNumber || "",
          busChangeName: this.addForm.busChangeName,
          busChangeAccountID: (this.addForm.busChangeAccountID || "")
            .toString()
            .trim(),
          notificationPhoneNumber: this.addForm.notificationPhoneNumber || "",
          notificationType: this.addForm.notificationType || "",
          pickupRouteID:
            !this.addIsAfternoonOnly && this.addSelectedPickupRoute
              ? this.addSelectedPickupRoute.id
              : null,
          dropoffRouteID: this.addSelectedDropoffRoute
            ? this.addSelectedDropoffRoute.id
            : null,
          specialDayRouteID: this.addSelectedSpecialDayRoute
            ? this.addSelectedSpecialDayRoute.id
            : null,
          updateSpecialDayRoute: true,
          pickupStopNumber:
            !this.addIsAfternoonOnly && this.addSelectedPickupRoute
              ? this.addSelectedPickupStopNumber
              : null,
          dropoffStopNumber: this.addSelectedDropoffStopNumber,
          note: "",
          building: student.building,
          branch: student.branch,
          class: student.class,
          section: student.section,
          term: student.regID ? student.regID.toString().trim() : null,
          enteredBy: this.$store.state.authentication.user,
          source: "ADMIN PORTAL",
        });

        const savedBusChangeIds = [];
        const saveFailures = [];
        let geoSyncErrorText = "";

        for (const student of addAllSelectedStudents) {
          try {
            const response = await this.$http.post(
              "api/buschange/save",
              buildAddBusChangeData(student)
            );

            if (response.error) {
              saveFailures.push(
                (student.hebName || student.studentName || student.studentID) +
                  ": " +
                  response.error
              );
              continue;
            }

            const busChangeId = response.id || response.busChangeID || null;
            if (busChangeId) savedBusChangeIds.push(busChangeId);
            if (response.geoSyncError) {
              geoSyncErrorText = response.geoSyncError;
            }
          } catch (e) {
            saveFailures.push(
              (student.hebName || student.studentName || student.studentID) +
                ": failed to save"
            );
          }
        }

        if (savedBusChangeIds.length) {
          this.$notify({
            text:
              addAllSelectedStudents.length > 1
                ? "Bus change saved successfully for " +
                  savedBusChangeIds.length +
                  " of " +
                  addAllSelectedStudents.length +
                  " students"
                : "Bus change saved successfully",
            group: "notification",
            type: "success",
          });
        }

        if (geoSyncErrorText) {
          this.$notify({
            group: "notification",
            type: "error",
            text: geoSyncErrorText,
          });
        }

        if (saveFailures.length) {
          this.$notify({
            group: "notification",
            type: "error",
            text: "Failed to save: " + saveFailures.join("; "),
          });
        }

        // Send notification if user confirmed (once per saved bus change)
        if (this.savedBusChangeId && savedBusChangeIds.length) {
          for (const busChangeId of savedBusChangeIds) {
            await this.sendBusChangeNotification(busChangeId);
          }
        }

        if (savedBusChangeIds.length) {
          this.closeAddBusChangeDialog();
          this.refreshData();
        }
      } catch (e) {
        this.$notify({
          text: "Failed to save bus change",
          group: "notification",
          type: "error",
        });
      } finally {
        this.addSaving = false;
        this.addSkipRouteWarning = false;
        this.addSkipGeoStopWarning = false;
        this.skipNotification = false;
        this.warningDialogSource = null;
        this.notificationDialogSource = null;
        this.savedBusChangeId = null;
      }
    },

    addNeedsIncompleteRouteWarning() {
      const hasPickup =
        !this.addIsAfternoonOnly &&
        this.addSelectedPickupRoute &&
        this.addSelectedPickupRoute.id;
      const hasDropoff =
        this.addSelectedDropoffRoute && this.addSelectedDropoffRoute.id;

      if (this.addIsAfternoonOnly) {
        return !hasDropoff;
      }
      return !hasPickup || !hasDropoff;
    },

    getAddIncompleteRouteMessage() {
      const hasPickup =
        this.addSelectedPickupRoute && this.addSelectedPickupRoute.id;
      const hasDropoff =
        this.addSelectedDropoffRoute && this.addSelectedDropoffRoute.id;

      if (this.addIsAfternoonOnly) {
        return "You have not selected a Dropoff route.";
      }
      if (hasPickup && !hasDropoff) {
        return "You have only selected the Pickup route.";
      }
      if (!hasPickup && hasDropoff) {
        return "You have only selected the Dropoff route.";
      }
      return "You have not selected any route.";
    },

    getAddMissingGeoStopMessage() {
      const missing = [];
      if (
        !this.addIsAfternoonOnly &&
        this.addSelectedPickupRoute &&
        !this.addSelectedPickupStopNumber
      ) {
        missing.push("pickup");
      }
      if (this.addSelectedDropoffRoute && !this.addSelectedDropoffStopNumber) {
        missing.push("dropoff");
      }
      if (!missing.length) return "";
      if (missing.length === 2) {
        return "Pickup and dropoff routes are assigned, but no Geo stop was selected for either one.";
      }
      return `A ${missing[0]} route is assigned, but no Geo stop was selected.`;
    },

    onBusChangeCardClicked(busChange) {
      const busChangeId =
        busChange.id || busChange.ID || busChange.busChangeID || null;
      if (!busChangeId) return;
      this.viewDialogSendNotificationOnSave = false;
      this.viewBusChangeId = busChangeId;
      this.viewDialog = true;
    },

    // Reads just the "YYYY-MM-DD" calendar digits from a date value rather
    // than letting moment re-interpret any (possibly mislabeled) timezone
    // offset — everything in this app is a local/Eastern calendar date, so
    // comparing raw digits avoids off-by-one-day results near midnight.
    toLocalDateKey(value) {
      if (!value) return null;
      if (typeof value === "string") {
        const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
        if (m) return m[1];
      }
      return moment(value).format("YYYY-MM-DD");
    },

    wasEnteredToday(busChange) {
      const entered = busChange && (busChange.enteredOn || busChange.EnteredOn);
      if (!entered) return false;
      return this.toLocalDateKey(entered) === moment().format("YYYY-MM-DD");
    },

    wasEndedToday(busChange) {
      if (!busChange) return false;
      const finished =
        busChange.finished === true || busChange.Finished === true;
      if (!finished) return false;
      const end = busChange.endDate || busChange.EndDate;
      if (!end) return false;
      return this.toLocalDateKey(end) === moment().format("YYYY-MM-DD");
    },

    isEndedBusChange(busChange) {
      return !!(
        busChange &&
        (busChange.finished === true || busChange.Finished === true)
      );
    },

    getBusChangeOpenDays(busChange) {
      if (!busChange) return 0;
      const start =
        busChange.startDate ||
        busChange.StartDate ||
        busChange.enteredOn ||
        busChange.EnteredOn;
      if (!start) return 0;
      const startDate = moment(start).startOf("day");
      if (!startDate.isValid()) return 0;
      const days = moment()
        .startOf("day")
        .diff(startDate, "days");
      return days > 0 ? days : 0;
    },

    isLongRunningBusChange(busChange) {
      if (this.isEndedBusChange(busChange)) return false;
      return this.getBusChangeOpenDays(busChange) >= 21;
    },

    async printOverdueList() {
      const ids = this.overdueBusChangesForPrint
        .map((bc) => bc.id || bc.ID)
        .filter((id) => id);
      if (!ids.length || this.printingOverdueList) return;
      this.printingOverdueList = true;
      try {
        const response = await this.$http.post(
          "api/buschange/GenerateOverdueListPdf",
          ids,
          { responseType: "blob" }
        );
        const rawBlob =
          response instanceof Blob ? response : new Blob([response]);
        const blobUrl = window.URL.createObjectURL(
          new Blob([rawBlob], { type: "application/pdf" })
        );
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download =
          "BusChanges_Overdue_" + moment().format("YYYYMMDD") + ".pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      } catch (e) {
        console.error("Error downloading overdue report:", e);
      } finally {
        this.printingOverdueList = false;
      }
    },

    getLongRunningLabel(busChange) {
      const days = this.getBusChangeOpenDays(busChange);
      const weeks = Math.floor(days / 7);
      if (weeks >= 1) {
        return "Open " + weeks + (weeks === 1 ? " week" : " weeks");
      }
      return "Open " + days + (days === 1 ? " day" : " days");
    },

    isSimpleSectionCollapsed(section) {
      if (!section) return false;
      if (
        section.key === "pending" &&
        !this.loadingSimpleRequests &&
        section.items.length === 0
      ) {
        return true;
      }
      if (!section.collapsible) return false;
      return this.collapsedSimpleSections[section.key] === true;
    },

    toggleSimpleSection(key) {
      const next = Object.assign({}, this.collapsedSimpleSections);
      next[key] = !next[key];
      this.collapsedSimpleSections = next;
    },

    async openEndBusChangeDialog() {
      this.endBusChangeDialog = true;
      this.endBusChangeSearch = "";
      this.endBusChangeOverdueOnly = false;
      this.endSelectedBusChange = null;
      this.endSelectedReturnRoute = null;
      if (!this.simpleBusChanges.length) {
        await this.loadSimpleViewData();
      }
      await this.loadEndBusRoutes();
    },

    closeEndBusChangeDialog() {
      this.endBusChangeDialog = false;
      this.endSaving = false;
      this.endBusChangeSearch = "";
      this.endBusChangeOverdueOnly = false;
      this.endSelectedBusChange = null;
      this.endSelectedReturnRoute = null;
    },

    clearEndBusChangeSelection() {
      this.endSelectedBusChange = null;
      this.endSelectedReturnRoute = null;
    },

    getEndBusChangeName(bc) {
      if (!bc) return "";
      return String(bc.busChangeName || bc.BusChangeName || "").trim();
    },

    getEndBusChangePhones(bc) {
      if (!bc) return [];
      const values = [
        bc.busChangePhoneNumber || bc.BusChangePhoneNumber,
        bc.notificationPhoneNumber || bc.NotificationPhoneNumber,
        bc.homePhone || bc.HomePhone,
        bc.cellPhone || bc.CellPhone,
      ];
      const seen = {};
      const phones = [];
      values.forEach((raw) => {
        const phone = String(raw || "").trim();
        if (!phone) return;
        const key = phone.replace(/\D/g, "") || phone.toLowerCase();
        if (seen[key]) return;
        seen[key] = true;
        phones.push(phone);
      });
      return phones;
    },

    getEndBusChangePhone(bc) {
      const phones = this.getEndBusChangePhones(bc);
      return phones.length ? phones[0] : "";
    },

    selectEndBusChange(bc) {
      this.endSelectedBusChange = bc;
      this.endSelectedReturnRoute = null;
      this.filterEndReturnRoutes();
    },

    async loadEndBusRoutes() {
      this.endLoadingBusRoutes = true;
      try {
        let url = "api/buschange/GetBusRoutes";
        if (this.isWithinSummerSeason()) {
          url +=
            `?season=${encodeURIComponent(this.addSummerSeasonName)}` +
            "&kind=DayCamp";
        }
        this.endBusRoutes = await this.$http.get(url);
        this.filterEndReturnRoutes();
      } catch (e) {
        this.endBusRoutes = [];
        this.endFilteredReturnRoutes = [];
      } finally {
        this.endLoadingBusRoutes = false;
      }
    },

    filterEndReturnRoutes() {
      const selected = this.endSelectedBusChange;
      const student = {
        building:
          selected && (selected.building || selected.Building),
        class: selected && (selected.class || selected.Class),
        branch: selected && (selected.branch || selected.Branch),
      };
      const hasStudentFilter = !!(student.building || student.class);

      let routes = (this.endBusRoutes || []).filter(
        (r) => (r.direction || "") === "Dropoff" && routeHasDriver(r)
      );

      if (hasStudentFilter) {
        routes = routes.filter((r) => routeMatchesStudent(r, student));
      }

      this.endFilteredReturnRoutes = ensureRouteIncluded(
        routes,
        this.endSelectedReturnRoute
      );
    },

    async saveEndBusChange() {
      if (!this.canSaveEndBusChange) return;
      const id = this.endSelectedBusChange.id || this.endSelectedBusChange.ID;
      const returnRouteID =
        this.endSelectedReturnRoute.id || this.endSelectedReturnRoute.ID;

      this.endSaving = true;
      try {
        const result = await this.$http.post("api/buschange/EndBusChange", {
          id,
          returnRouteID,
        });
        if (result && result.error) {
          this.$notify({
            text: result.error,
            group: "notification",
            type: "error",
          });
          return;
        }
        this.$notify({
          text:
            "Bus change ended. It will appear on today's extended stickers report.",
          group: "notification",
          type: "success",
        });
        this.closeEndBusChangeDialog();
        await this.loadSimpleViewData();
      } catch (e) {
        const msg =
          (e && e.body && (e.body.message || e.body.title || e.body)) ||
          (e && e.message) ||
          "Failed to end bus change";
        this.$notify({
          text: typeof msg === "string" ? msg : "Failed to end bus change",
          group: "notification",
          type: "error",
        });
      } finally {
        this.endSaving = false;
      }
    },

    isSingleDayChange(request) {
      const start = request.startDate || request.StartDate;
      const end = request.endDate || request.EndDate;
      if (!start || !end) return true;
      const startDate = moment(start).startOf("day");
      const endDate = moment(end).startOf("day");
      if (!startDate.isValid() || !endDate.isValid()) return true;
      // Same calendar day or the following day (2-day changes use kid cards)
      return endDate.diff(startDate, "days") <= 1;
    },

    isTwoDayChange(request) {
      if (!this.isSingleDayChange(request)) return false;
      const start = request.startDate || request.StartDate;
      const end = request.endDate || request.EndDate;
      if (!start || !end) return false;
      const startDate = moment(start).startOf("day");
      const endDate = moment(end).startOf("day");
      if (!startDate.isValid() || !endDate.isValid()) return false;
      return endDate.diff(startDate, "days") === 1;
    },

    getChangeTypeChipLabel(request) {
      if (!this.isSingleDayChange(request)) return "Extended";
      return this.isTwoDayChange(request) ? "2 Days" : "Single Day";
    },

    // Student's regular/registered bus address, shown after the bus-change
    // address as "bus change -> original" — only once the change has ended
    // and the student is back on their original route. Empty otherwise
    // (unknown, identical, or still active).
    getCardOriginalAddress(request) {
      if (!request) return "";
      if (!this.isEndedBusChange(request)) return "";
      const original = String(
        request.busAddress1 || request.BusAddress1 || ""
      ).trim();
      if (!original) return "";
      const changed = String(
        request.busChangeAddress || request.BusChangeAddress || ""
      ).trim();
      if (original.toLowerCase() === changed.toLowerCase()) return "";
      return original;
    },

    formatCardPeriod(period) {
      const normalized = (period || "")
        .toString()
        .trim()
        .toUpperCase();
      if (normalized === "AM") return "Morning";
      if (normalized === "PM") return "Afternoon";
      return "";
    },

    formatCardDate(date) {
      if (!date) return "";
      return moment(date).format("MMM D, h:mm A");
    },

    formatCardDateRange(request) {
      const start = request.startDate || request.StartDate;
      const end = request.endDate || request.EndDate;
      if (!start) return "Dates not set";

      const startDate = moment(start);
      if (!startDate.isValid()) return "Dates not set";

      const startPeriod = this.formatCardPeriod(
        request.startPeriod || request.StartPeriod
      );
      const endPeriod = this.formatCardPeriod(
        request.endPeriod || request.EndPeriod
      );

      if (this.isSingleDayChange(request)) {
        const sameCalendarDay = startDate.isSame(moment(end), "day");
        if (sameCalendarDay) {
          let dayPart = "All day";
          if (startPeriod && endPeriod && startPeriod === endPeriod) {
            dayPart = startPeriod + " only";
          } else if (startPeriod && !endPeriod) {
            dayPart = startPeriod + " only";
          } else if (!startPeriod && endPeriod) {
            dayPart = endPeriod + " only";
          }
          return startDate.format("dddd, MMM D") + " · " + dayPart;
        }
      }

      const endDate = moment(end);
      return (
        startDate.format("dddd, MMM D") +
        (startPeriod ? " (" + startPeriod + ")" : "") +
        " → " +
        endDate.format("dddd, MMM D") +
        (endPeriod ? " (" + endPeriod + ")" : "")
      );
    },

    getActiveStatusInfo(busChange) {
      const today = moment().startOf("day");
      const status = this.getRequestStatus(busChange);
      const start = busChange.startDate || busChange.StartDate;
      const end = busChange.endDate || busChange.EndDate || start;

      if (this.isEndedBusChange(busChange)) {
        return { label: "Ended", cssClass: "status-ended" };
      }
      if (status === "FUTURE" || status === "PENDING") {
        const startDate = moment(start);
        return {
          label:
            start && startDate.isValid()
              ? "Starts " + startDate.format("dddd, MMM D")
              : "Upcoming",
          cssClass: "status-upcoming",
        };
      }
      if (end && this.toLocalDateKey(end) === today.format("YYYY-MM-DD")) {
        return { label: "Ends today", cssClass: "status-ending" };
      }
      return { label: "Active now", cssClass: "status-active" };
    },

    getPendingRevertedToDate(request) {
      if (!request) return null;
      return (
        request.statusRevertedToPendingOn ||
        request.StatusRevertedToPendingOn ||
        null
      );
    },

    isCommentAwaitingResponse(comment, index, comments) {
      if (!this.isPendingRequest(this.selectedRequest)) return false;
      const revertedToPendingOn = this.getPendingRevertedToDate(
        this.selectedRequest
      );

      if (revertedToPendingOn) {
        const enteredOn = this.getCommentEnteredOn(comment);
        if (!enteredOn) return false;
        return moment(enteredOn).isSameOrAfter(moment(revertedToPendingOn));
      }

      return comments && index === comments.length - 1;
    },

    promptDeleteRequest() {
      this.deleteRequestConfirmDialog = true;
    },

    async deleteRequest() {
      const requestId = this.getBusChangeRequestId(this.selectedRequest);
      if (!requestId) {
        this.$notify({
          text: "Unable to delete request: missing request ID",
          group: "notification",
          type: "error",
        });
        return;
      }

      this.deletingRequest = true;
      try {
        this.deleteRequestConfirmDialog = false;
        await this.$http.post("api/BusChangeRequests/Delete?id=" + requestId);
        this.$notify({
          text: "Bus change request deleted successfully",
          group: "notification",
          type: "success",
        });
        this.closeDialog();
        this.refreshData();
      } catch (e) {
        this.$notify({
          text: "Failed to delete bus change request",
          group: "notification",
          type: "error",
        });
      } finally {
        this.deletingRequest = false;
      }
    },

    getCommentText(comment) {
      if (!comment) return "";
      return comment.commentText || comment.CommentText || "";
    },

    getCommentEnteredBy(comment) {
      if (!comment) return "";
      const enteredBy = comment.enteredBy || comment.EnteredBy || "";
      const familyId = this.selectedRequest
        ? this.selectedRequest.familyID || this.selectedRequest.FamilyID || ""
        : "";
      const normalizeIdentity = (value) =>
        value
          ? value
              .toString()
              .trim()
              .replace(/\s+/g, "")
              .toUpperCase()
          : "";
      if (
        enteredBy &&
        familyId &&
        normalizeIdentity(enteredBy) === normalizeIdentity(familyId)
      ) {
        return "Parent";
      }
      return enteredBy;
    },

    getCommentEnteredOn(comment) {
      if (!comment) return null;
      return comment.enteredOn || comment.EnteredOn || null;
    },

    getCommentCallerID(comment) {
      if (!comment) return "";
      return comment.callerID || comment.CallerID || "";
    },

    getCommentRecordingUrl(comment) {
      if (!comment) return null;
      return normalizeRecordingUrl(
        comment.recordingURL ||
          comment.recordingUrl ||
          comment.RecordingURL ||
          null
      );
    },

    setTodayOnlyAfternoon() {
      // If today's deadline has passed, treat "today" as tomorrow
      let date = moment().format("YYYY-MM-DD");
      const ctx = this.getProcessDeadlineGradeContext();
      if (this.checkDeadlineWarning(ctx.grade, ctx.scope, date)) {
        date = moment()
          .add(1, "day")
          .format("YYYY-MM-DD");
      }
      this.formData.startDate = date;
      this.formData.endDate = date;
      this.formData.startPeriod = "PM";
      this.formData.endPeriod = "PM";
    },

    searchAccountByPhone() {
      this.openAccountSearch(this.formData.busChangePhoneNumber);
    },

    toggleExtraInfoEdit() {
      if (!this.editingExtraInfo) {
        this.extraInfoBackup = {
          geoPhoneNumber: this.formData.geoPhoneNumber,
          note: this.formData.note,
        };
        this.editingExtraInfo = true;
      } else {
        this.editingExtraInfo = false;
        this.extraInfoBackup = null;
      }
    },

    cancelExtraInfoEdit() {
      if (this.extraInfoBackup) {
        this.formData.geoPhoneNumber = this.extraInfoBackup.geoPhoneNumber;
        this.formData.note = this.extraInfoBackup.note;
      }
      this.editingExtraInfo = false;
      this.extraInfoBackup = null;
    },

    getPeriodOrder(period) {
      if (period === "PM") return 2;
      return 1;
    },

    formatPeriodLabel(period) {
      if (!period) return "-";
      const normalized = period
        .toString()
        .trim()
        .toUpperCase();
      if (normalized === "AM") return "Morning Pickup";
      if (normalized === "PM") return "Afternoon Dropoff";
      return period;
    },

    getIncompleteRouteMessage() {
      if (this.warningDialogSource === "add" || this.addBusChangeDialog) {
        return this.getAddIncompleteRouteMessage();
      }

      const hasPickup = this.selectedPickupRoute && this.selectedPickupRoute.id;
      const hasDropoff =
        this.selectedDropoffRoute && this.selectedDropoffRoute.id;

      if (hasPickup && !hasDropoff) {
        return "You have only selected the Pickup route.";
      }
      if (!hasPickup && hasDropoff) {
        return "You have only selected the Dropoff route.";
      }
      return "You have not selected any route.";
    },

    isEndPeriodBeforeStartPeriod(startPeriod, endPeriod) {
      return this.getPeriodOrder(endPeriod) < this.getPeriodOrder(startPeriod);
    },

    async loadBusChangeComments(busChangeRequestId) {
      this.busChangeComments = [];

      if (!busChangeRequestId) {
        return;
      }

      this.loadingBusChangeComments = true;
      try {
        const response = await this.$http.get(
          `api/BusChange/GetBusChangeComments?busChangeRequestId=${busChangeRequestId}`
        );
        this.busChangeComments = Array.isArray(response) ? response : [];
      } catch (e) {
        this.busChangeComments = [];
      } finally {
        this.loadingBusChangeComments = false;
      }
    },

    async loadBusRoutes() {
      this.loadingBusRoutes = true;
      try {
        // For day camp requests, ask the backend for only the summer season's routes.
        let url = "api/buschange/GetBusRoutes";
        if (this.dayCampEnrollment) {
          url +=
            `?season=${encodeURIComponent(this.summerSeasonName)}` +
            "&kind=DayCamp";
        }
        this.busRoutes = await this.$http.get(url);
        this.filterRoutesByBuilding();
      } catch (e) {
        this.busRoutes = [];
        this.filteredPickupRoutes = [];
        this.filteredDropoffRoutes = [];
      } finally {
        this.loadingBusRoutes = false;
      }
    },

    filterRoutesByBuilding() {
      // In day camp mode prefer the student's day camp building / grade.
      const student = {
        building:
          (this.dayCampEnrollment && this.dayCampEnrollment.building) ||
          (this.selectedRequest && this.selectedRequest.building),
        class:
          (this.dayCampEnrollment && this.dayCampEnrollment.class) ||
          (this.selectedRequest && this.selectedRequest.class),
        branch: this.selectedRequest && this.selectedRequest.branch,
      };
      const hasStudentFilter = !!(student.building || student.class);

      const pickup = this.filterRoutesBySeason(
        (this.busRoutes || []).filter((r) => {
          if (r.direction !== "Pickup" || !routeHasDriver(r)) return false;
          return !hasStudentFilter || routeMatchesStudent(r, student);
        })
      );
      const dropoff = this.filterRoutesBySeason(
        (this.busRoutes || []).filter((r) => {
          if (r.direction !== "Dropoff" || !routeHasDriver(r)) return false;
          return !hasStudentFilter || routeMatchesStudent(r, student);
        })
      );

      this.filteredPickupRoutes = ensureRouteIncluded(
        pickup,
        this.selectedPickupRoute
      );
      this.filteredDropoffRoutes = ensureRouteIncluded(
        dropoff,
        this.selectedDropoffRoute
      );
    },

    filterRoutesBySeason(routes) {
      // In day camp mode, only show summer / DayCamp routes — never year-round.
      if (!this.dayCampEnrollment) return routes;
      if (!routes.some((r) => r.season || r.kind)) return routes;
      const target = this.summerSeasonName.trim().toLowerCase();
      return routes.filter((r) => {
        const kind = (r.kind || "").trim().toLowerCase();
        if (kind === "daycamp") return true;
        const season = (r.season || "").trim().toLowerCase();
        if (!season) return true;
        return season === target;
      });
    },

    // Self-contained — Vuetify calls this with autocomplete as `this`
    customRouteFilter(item, queryText, itemText) {
      if (!queryText) return true;
      const searchUpper = String(queryText)
        .toUpperCase()
        .trim();
      const route = item && typeof item === "object" ? item : null;
      if (!route) {
        return String(itemText || item || "")
          .toUpperCase()
          .includes(searchUpper);
      }
      const bus = (
        route.actualBusNumber ||
        route.busNumber ||
        route.BusNumber ||
        ""
      )
        .toString()
        .trim();
      const fields = [
        route.description,
        route.busNumber,
        route.actualBusNumber,
        route.ActualBusNumber,
        route.BusNumber,
        bus,
        route.driver,
        route.color,
        route.timeStart,
        route.routeIdentifier,
        itemText,
      ];
      return fields.some(
        (f) =>
          f != null &&
          String(f)
            .toUpperCase()
            .includes(searchUpper)
      );
    },

    async onPickupRouteChange(pickupRoute) {
      this.selectedPickupStopNumber = null;
      this.pickupRouteStops = [];

      if (!pickupRoute) {
        return;
      }

      await this.loadRouteStops("pickup", pickupRoute.id);
    },

    async onDropoffRouteChange(dropoffRoute) {
      this.selectedDropoffStopNumber = null;
      this.dropoffRouteStops = [];

      if (!dropoffRoute) {
        return;
      }

      await this.loadRouteStops("dropoff", dropoffRoute.id);
      this.copyPickupStopToDropoffIfSameRoute(this.selectedPickupStopNumber);
    },

    onPickupStopNumberChange(stopNumber) {
      this.copyPickupStopToDropoffIfSameRoute(stopNumber);
    },

    isSameBusRoute(pickupRoute, dropoffRoute) {
      if (!pickupRoute || !dropoffRoute) return false;
      const pickupBus = this.getRouteBusNumber(pickupRoute);
      const dropoffBus = this.getRouteBusNumber(dropoffRoute);
      return !!pickupBus && pickupBus === dropoffBus;
    },

    copyPickupStopToDropoffIfSameRoute(stopNumber) {
      if (stopNumber == null || stopNumber === "") return;
      if (
        !this.isSameBusRoute(
          this.selectedPickupRoute,
          this.selectedDropoffRoute
        )
      ) {
        return;
      }
      if (
        !this.dropoffRouteStops ||
        !this.dropoffRouteStops.some((s) => s.stopNumber === stopNumber)
      ) {
        return;
      }
      this.selectedDropoffStopNumber = stopNumber;
    },

    async loadRouteStops(direction, routeId, preferredStopNumber = null) {
      if (!routeId) {
        return;
      }

      const loadingKey =
        direction === "pickup" ? "loadingPickupStops" : "loadingDropoffStops";
      const itemsKey =
        direction === "pickup" ? "pickupRouteStops" : "dropoffRouteStops";
      const selectedKey =
        direction === "pickup"
          ? "selectedPickupStopNumber"
          : "selectedDropoffStopNumber";

      this[loadingKey] = true;
      try {
        const stops = await this.$http.get(
          `api/buschange/GetRouteStops?routeId=${routeId}`
        );
        this[itemsKey] = Array.isArray(stops) ? stops : [];
        if (
          preferredStopNumber != null &&
          this[itemsKey].some((s) => s.stopNumber === preferredStopNumber)
        ) {
          this[selectedKey] = preferredStopNumber;
        }
      } catch (e) {
        this[itemsKey] = [];
      } finally {
        this[loadingKey] = false;
      }
    },

    getMissingGeoStopMessage() {
      const missing = [];
      if (this.selectedPickupRoute && !this.selectedPickupStopNumber) {
        missing.push("pickup");
      }
      if (this.selectedDropoffRoute && !this.selectedDropoffStopNumber) {
        missing.push("dropoff");
      }
      if (!missing.length) return "";
      if (missing.length === 2) {
        return "Pickup and dropoff routes are assigned, but no Geo stop was selected for either one.";
      }
      return `A ${missing[0]} route is assigned, but no Geo stop was selected.`;
    },

    hasOverlap(str1, str2) {
      if (!str1 || !str2) return false;
      const arr1 = str1
        .split(",")
        .map((s) => s.trim().toUpperCase())
        .filter((s) => s);
      const arr2 = str2
        .split(",")
        .map((s) => s.trim().toUpperCase())
        .filter((s) => s);
      return arr1.some((item) => arr2.includes(item));
    },

    clearEndReturnSelection() {
      this.endSelectedReturnRoute = null;
    },

    getRouteColor(colorName) {
      if (!colorName) return null;

      const colorUpper = colorName.toUpperCase().trim();
      const colorMap = {
        BEIGE: "#F5F5DC",
        BLACK: "#000000",
        BLUE: "#0000FF",
        BROWN: "#8B4513",
        GOLD: "#FFD700",
        GREEN: "#008000",
        ORANGE: "#FFA500",
        PINK: "#FFC0CB",
        PURPLE: "#800080",
        RED: "#FF0000",
        SILVER: "#C0C0C0",
        WHITE: "#E0E0E0",
        YELLOW: "#FFFF00",
        "DARK BLUE": "#00008B",
        "DARK GREEN": "#006400",
        "DARK PINK": "#FF1493",
        "DARK PURPLE": "#4B0082",
        "LIGHT BLUE": "#ADD8E6",
        "LIGHT GREEN": "#90EE90",
        "LIGHT PINK": "#FFB6C1",
        "LIGHT PURPLE": "#DDA0DD",
        GRAY: "#808080",
        GREY: "#808080",
        CYAN: "#00FFFF",
        TEAL: "#008080",
        LIME: "#00FF00",
        INDIGO: "#4B0082",
        NAVY: "#000080",
        MAROON: "#800000",
        OLIVE: "#808000",
        VIOLET: "#EE82EE",
        TURQUOISE: "#40E0D0",
        CORAL: "#FF7F50",
        SALMON: "#FA8072",
        MINT: "#98FF98",
        LAVENDER: "#E6E6FA",
        PEACH: "#FFE5B4",
        SKY: "#87CEEB",
        ROSE: "#FF007F",
      };

      return colorMap[colorUpper] || "#5cb860";
    },

    getRouteBusNumber(route) {
      // Prefer actualBusNumber from GetBusRoutes when present; else busNumber
      if (!route) return "";
      const actual = (route.actualBusNumber || "").toString().trim();
      if (actual) return actual;
      return (route.busNumber || "").toString().trim();
    },

    getRouteChipStyle(route) {
      if (!route || !route.color) {
        return {
          backgroundColor: "#5cb860",
          color: "#ffffff",
        };
      }
      const bgColor = this.getRouteColor(route.color) || "#5cb860";
      const brightness = this.calculateBrightness(bgColor);
      const textColor = brightness > 128 ? "#000000" : "#ffffff";

      return {
        backgroundColor: bgColor,
        color: textColor,
        padding: "2px 8px",
        borderRadius: "12px",
        fontSize: "0.75rem",
        marginLeft: "8px",
        display: "inline-block",
      };
    },

    calculateBrightness(hexColor) {
      const hex = hexColor.replace("#", "");
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return (r * 299 + g * 587 + b * 114) / 1000;
    },

    formatDate(date) {
      if (!date) return "";
      return moment(date).format("MM/DD/YYYY hh:mm A");
    },

    formatPhone(phone) {
      if (!phone) return null;
      const raw =
        typeof phone === "object"
          ? phone.value || phone.text || phone.phoneNum || ""
          : phone;
      const digits = String(raw).replace(/\D/g, "");
      if (digits.length === 10) {
        return digits.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
      } else if (digits.length === 11 && digits.startsWith("1")) {
        return digits.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, "$1-$2-$3-$4");
      }
      return typeof phone === "string" ? phone : String(raw || "");
    },

    formatPhoneNumber(phoneNumber) {
      return this.formatPhone(phoneNumber);
    },

    openAccountSearch(prefillPhone) {
      const phone = typeof prefillPhone === "string" ? prefillPhone : "";
      // Reset dialog search state
      this.dialogPhoneSearch = phone || "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
      this.accountSearchDialog = true;
      if (this.dialogPhoneSearch) {
        this.searchDialogByPhone(this.dialogPhoneSearch);
      }
    },

    closeAccountSearchDialog() {
      this.accountSearchDialog = false;
      this.dialogPhoneSearch = "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
    },

    async searchDialogByPhone(value) {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const phoneNumber = value ? value.trim() : "";

      if (!phoneNumber || phoneNumber.length < 10) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const accounts = await this.$http.get(
            `api/students/getAccountByPhoneNumber?phoneNumber=${phoneNumber}`
          );

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    async searchDialogByName() {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const lastName = this.dialogLastNameSearch
        ? this.dialogLastNameSearch.trim()
        : "";
      const firstName = this.dialogFirstNameSearch
        ? this.dialogFirstNameSearch.trim()
        : "";

      if (!lastName && !firstName) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const url = `api/students/getAccountsByName?last=${lastName}&first=${firstName}&isYiddish=${this
            .dialogSearchNameMode === "yiddish"}`;

          const accounts = await this.$http.get(url);

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    async searchDialogByAddress() {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const houseNumber = this.dialogHouseNumberSearch
        ? this.dialogHouseNumberSearch.trim()
        : "";
      const street = this.dialogStreetSearch
        ? this.dialogStreetSearch.trim()
        : "";

      if (!houseNumber && !street) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const params = new URLSearchParams();
          if (houseNumber) params.append("houseNumber", houseNumber);
          if (street) params.append("street", street);
          if (this.userBranch) params.append("branch", this.userBranch);

          const url = `api/students/getAccountsByAddress?${params.toString()}`;
          const accounts = await this.$http.get(url);

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    onAccountSelected(account) {
      this.accountSearchDialog = false;
      this.formData.busChangeAccountID = account.accountID;
      // User explicitly picked this account — replace whatever was entered
      this.formData.busChangeName = (account.lastName || "").trim();
      if (account.address1) {
        this.formData.busChangeAddress = account.address1;
      }
      if (!this.formData.busChangePhoneNumber) {
        const accountPhone = account.homePhone || account.cellPhone || "";
        this.formData.busChangePhoneNumber = accountPhone
          ? this.formatPhone(accountPhone) || accountPhone
          : "";
      }

      // Clear dialog search state
      this.dialogPhoneSearch = "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
    },

    validateForm() {
      if (!this.formData.startDate) {
        this.$notify({
          text: "Start Date is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.formData.busChangeAddress) {
        this.$notify({
          text: "Bus Change Address is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.formData.startPeriod) {
        this.$notify({
          text: "Starting Run is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.formData.endPeriod) {
        this.$notify({
          text: "Ending After is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      // Validate phone numbers if they have values
      const validatePhone = (phoneNumber) => {
        if (!phoneNumber) return true; // Empty is ok (not required)
        const digits = phoneNumber.replace(/[-\s()]/g, "");
        return [10, 11].includes(digits.length) && /^\d+$/.test(digits);
      };

      if (
        this.formData.busChangePhoneNumber &&
        !validatePhone(this.formData.busChangePhoneNumber)
      ) {
        this.$notify({
          text:
            "Bus Change Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (
        this.formData.geoPhoneNumber &&
        !validatePhone(this.formData.geoPhoneNumber)
      ) {
        this.$notify({
          text:
            "Geo Tracking Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return false;
      }

      return true;
    },

    // Notification dialog handlers
    resumeSaveAfterNotificationChoice() {
      if (this.notificationDialogSource === "add") {
        this.saveAddBusChange();
        return;
      }
      this.saveRequest();
    },

    proceedWithNotification() {
      this.notificationConfirmDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = true; // Flag to send notification after save
      this.resumeSaveAfterNotificationChoice();
    },

    proceedWithoutNotification() {
      this.notificationConfirmDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = null; // Don't send notification
      this.resumeSaveAfterNotificationChoice();
    },

    cancelSave() {
      this.notificationConfirmDialog = false;
      this.missingNotificationFieldsDialog = false;
      this.clearedNotificationWarningDialog = false;
      this.skipRouteWarning = false;
      this.addSkipRouteWarning = false;
      this.addSkipGeoStopWarning = false;
      this.skipNotification = false;
      this.skipEnrollmentWarning = false;
      this.savedBusChangeId = null;
      this.notificationDialogSource = null;
    },

    saveWithoutNotification() {
      this.missingNotificationFieldsDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = null; // Don't send notification
      this.resumeSaveAfterNotificationChoice();
    },

    cancelClearedNotificationWarning() {
      this.clearedNotificationWarningDialog = false;
      this.skipNotification = false;
      // User stays in edit mode
    },

    confirmSaveWithoutNotification() {
      this.clearedNotificationWarningDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = null; // Don't send notification
      this.saveRequest();
    },

    async sendBusChangeNotification(busChangeId) {
      this.sendingNotification = true;
      try {
        const apiUrl = `https://broadcast-3t6ck5zrrq-ue.a.run.app/busChangeNotification/sendBusChangeNotification?busChangeId=${busChangeId}`;
        const response = await this.$http.post(apiUrl, {});
        const notices = getBusChangeNotificationNotices(response);
        notices.forEach((notice) => {
          this.$notify({
            text: notice.text,
            group: "notification",
            type: notice.type,
            duration: 8000,
          });
        });
      } catch (error) {
        this.$notify({
          text:
            "Failed to send bus change notification. The bus change was saved but notification failed.",
          group: "notification",
          type: "warning",
        });
      } finally {
        this.sendingNotification = false;
      }
    },

    async loadBusChangeGrades() {
      try {
        const response = await this.$http.get(
          "api/busChange/getBusChangeGrades"
        );
        this.busChangeGrades = Array.isArray(response) ? response : [];
      } catch (e) {
        this.busChangeGrades = [];
      }
    },

    isDayCampGrade(grade) {
      const source = ((grade && grade.source) || "").trim().toLowerCase();
      return source === "day camp" || source === "daycamp";
    },

    // Grade + Branch for School; Grade + Building for Day Camp.
    // Only rules of the matching source are considered (matches BusChanges.vue).
    getGradeSetup(grade, scope, isDayCamp = false) {
      if (!grade || !scope || !this.busChangeGrades.length) return null;
      const g = grade.trim();
      const s = scope.trim();
      return this.busChangeGrades.find((x) => {
        if (this.isDayCampGrade(x) !== isDayCamp) return false;
        if (!x.grade || x.grade.trim() !== g) return false;
        const key = isDayCamp ? x.building : x.branch;
        return key && key.trim() === s;
      });
    },

    checkGradeAllowsBusChange(grade, scope, isDayCamp = false) {
      const setup = this.getGradeSetup(grade, scope, isDayCamp);
      if (!setup)
        return {
          allowed: false,
          error: "Bus changes are not allowed for this grade.",
        };

      if (!setup.allowBusChange) {
        return {
          allowed: false,
          error: "Bus changes are not allowed for this grade.",
        };
      }

      return { allowed: true, error: null };
    },

    async loadSelectedGradeDeadlines(grade, scope, isDayCamp = false) {
      if (!this.busChangeGrades.length) {
        await this.loadBusChangeGrades();
      }
      const setup = this.getGradeSetup(grade, scope, isDayCamp);
      if (!setup || !setup.id) {
        this.selectedGradeDeadlines = [];
        return;
      }
      try {
        const response = await this.$http.get(
          "api/busChange/getBusChangeDeadlines?gradeId=" + setup.id
        );
        this.selectedGradeDeadlines = Array.isArray(response) ? response : [];
      } catch (e) {
        this.selectedGradeDeadlines = [];
      }
    },

    checkDeadlineWarning(grade, scope, startDate, endDate) {
      if (!grade || !scope) return null;
      if (!startDate || !this.selectedGradeDeadlines.length) return null;

      const closed = closedDayInRange(
        this.selectedGradeDeadlines,
        startDate,
        endDate
      );
      if (closed) return closed;

      const deadline = deadlineForDate(this.selectedGradeDeadlines, startDate);

      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(
        today.getMonth() + 1
      ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      if (startDate > todayStr) return null;

      if (!deadline || !deadline.deadline) return null;

      const currentTime =
        today.getHours() * 3600 + today.getMinutes() * 60 + today.getSeconds();
      const deadlineParts = deadline.deadline.split(":");
      if (deadlineParts.length < 2) return null;

      const deadlineSeconds =
        parseInt(deadlineParts[0]) * 3600 +
        parseInt(deadlineParts[1]) * 60 +
        (deadlineParts[2] ? parseFloat(deadlineParts[2]) : 0);
      if (currentTime <= deadlineSeconds) return null;

      let hours = parseInt(deadlineParts[0]);
      const minutes = deadlineParts[1];
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      const deadlineFormatted = `${hours}:${minutes} ${ampm}`;
      return `Note: The deadline for bus changes has passed (${deadlineFormatted}).`;
    },

    notifyDeadlineWarningIfNeeded() {
      if (!this.deadlineWarningMessage) return;
      this.$notify({
        text: this.deadlineWarningMessage,
        group: "notification",
        type: "warning",
      });
    },

    async saveRequest() {
      if (!this.validateForm()) return;

      if (!this.busChangeGrades.length) {
        await this.loadBusChangeGrades();
      }
      const gradeCtx = this.getProcessDeadlineGradeContext();
      const gradeCheck = this.checkGradeAllowsBusChange(
        gradeCtx.grade,
        gradeCtx.scope,
        gradeCtx.isDayCamp
      );
      if (!gradeCheck.allowed) {
        this.$notify({
          text: gradeCheck.error,
          group: "notification",
          type: "error",
        });
        return;
      }

      this.notifyDeadlineWarningIfNeeded();

      // Validate endDate is not before startDate
      if (this.formData.endDate && this.formData.startDate) {
        const startDate = moment(this.formData.startDate);
        const endDate = moment(this.formData.endDate);

        if (endDate.isBefore(startDate, "day")) {
          this.$notify({
            text: "End Date cannot be before Start Date",
            group: "notification",
            type: "error",
          });
          return;
        }

        if (
          endDate.isSame(startDate, "day") &&
          this.isEndPeriodBeforeStartPeriod(
            this.formData.startPeriod,
            this.formData.endPeriod
          )
        ) {
          this.$notify({
            text: "Ending After cannot be before Starting Run on the same day.",
            group: "notification",
            type: "error",
          });
          return;
        }
      }

      // Day camp: warn (soft) if the date range falls outside enrolled weeks.
      if (!this.skipEnrollmentWarning && this.getEnrollmentDateWarning()) {
        this.enrollmentWarningDialog = true;
        return;
      }

      // Check if notification fields are missing for IVR sources
      if (!this.skipNotification && this.selectedRequest) {
        const hadNotificationInfo =
          this.selectedRequest.notificationPhoneNumber ||
          this.selectedRequest.notificationType;

        // If request originally had notification info but it's now missing
        if (
          hadNotificationInfo &&
          (!this.selectedRequest.notificationPhoneNumber ||
            !this.selectedRequest.notificationType)
        ) {
          this.clearedNotificationWarningDialog = true;
          return;
        }
      }

      // Check for incomplete route selection warning rules
      if (!this.skipRouteWarning) {
        const hasPickup =
          this.selectedPickupRoute && this.selectedPickupRoute.id;
        const hasDropoff =
          this.selectedDropoffRoute && this.selectedDropoffRoute.id;
        const originalHasPickup = !!this.originalPickupRouteID;
        const originalHasDropoff = !!this.originalDropoffRouteID;
        const originallyNoRoutes = !originalHasPickup && !originalHasDropoff;
        const hasOnlyOneRoute =
          (hasPickup && !hasDropoff) || (!hasPickup && hasDropoff);

        if (
          (!hasPickup && !hasDropoff) ||
          (hasOnlyOneRoute && originallyNoRoutes)
        ) {
          this.warningDialogSource = "process";
          this.incompleteRouteDialog = true;
          return;
        }
      }

      // Warn when a route is assigned without a Geo stop (GeoTrack will not sync)
      if (!this.skipGeoStopWarning) {
        const missingGeoStopMsg = this.getMissingGeoStopMessage();
        if (missingGeoStopMsg) {
          this.warningDialogSource = "process";
          this.missingGeoStopMessage = missingGeoStopMsg;
          this.missingGeoStopDialog = true;
          return;
        }
      }

      // Check if notification should be sent (at least one route is assigned)
      if (!this.skipNotification && this.selectedRequest) {
        const currentPickupID = this.selectedPickupRoute
          ? this.selectedPickupRoute.id
          : null;
        const currentDropoffID = this.selectedDropoffRoute
          ? this.selectedDropoffRoute.id
          : null;

        // Allow notification when at least one route is assigned
        const atLeastOneRouteSelected = currentPickupID || currentDropoffID;

        if (atLeastOneRouteSelected) {
          this.notificationDialogSource = "process";
          // Check if notification fields are filled
          const hasNotificationPhone = this.selectedRequest
            .notificationPhoneNumber;
          const hasNotificationType = this.selectedRequest.notificationType;

          if (!hasNotificationPhone || !hasNotificationType) {
            // Show missing fields dialog
            this.missingNotificationFieldsDialog = true;
            return;
          } else {
            // Show notification confirmation dialog
            this.notificationConfirmDialog = true;
            return;
          }
        }
      }

      this.saving = true;
      try {
        const data = {
          busChangeRequestID: this.selectedRequest.id,
          enteredBy: this.$store.state.authentication.user,
          startDate: this.formData.startDate
            ? moment(this.formData.startDate).format("MM/DD/YYYY")
            : null,
          endDate: this.formData.endDate
            ? moment(this.formData.endDate).format("MM/DD/YYYY")
            : null,
          startPeriod: this.formData.startPeriod,
          endPeriod: this.formData.endPeriod,
          note: this.formData.note,
          studentID: this.selectedRequest.studentID,
          homeAddress: this.selectedRequest.busAddress1 || "",
          busChangeAddress: this.formData.busChangeAddress,
          building: this.selectedRequest.building,
          branch: this.selectedRequest.branch,
          class: this.selectedRequest.class,
          section: this.selectedRequest.section,
          term: this.selectedRequest.regID,
          busChangePhoneNumber: this.formData.busChangePhoneNumber,
          geoPhoneNumber: this.formData.geoPhoneNumber,
          busChangeName: this.formData.busChangeName,
          busChangeAccountID: this.formData.busChangeAccountID,
          notificationPhoneNumber: this.selectedRequest
            ? this.selectedRequest.notificationPhoneNumber || ""
            : "",
          notificationType: this.selectedRequest
            ? this.selectedRequest.notificationType || ""
            : "",
          pickupRouteID: this.selectedPickupRoute
            ? this.selectedPickupRoute.id
            : null,
          dropoffRouteID: this.selectedDropoffRoute
            ? this.selectedDropoffRoute.id
            : null,
          pickupStopNumber: this.selectedPickupStopNumber,
          dropoffStopNumber: this.selectedDropoffStopNumber,
        };

        const response = await this.$http.post(
          "api/BusChangeRequests/convertToBusChange",
          data
        );

        if (response.error) {
          return this.$notify({
            group: "notification",
            type: "error",
            text: response.error,
          });
        }

        // Get the bus change ID from response
        const busChangeId = response.busChange ? response.busChange.id : null;

        this.$notify({
          text: "Bus change request completed successfully",
          group: "notification",
          type: "success",
        });

        if (response.geoSyncError) {
          this.$notify({
            group: "notification",
            type: "error",
            text: response.geoSyncError,
          });
        }

        // Send notification if needed
        if (this.savedBusChangeId && busChangeId) {
          await this.sendBusChangeNotification(busChangeId);
        }

        this.closeDialog();
        this.refreshData();
      } catch (e) {
        this.$notify({
          text: "Failed to save bus change request",
          group: "notification",
          type: "error",
        });
      } finally {
        this.saving = false;
        this.skipRouteWarning = false;
        this.skipGeoStopWarning = false;
        this.skipNotification = false;
        this.skipEnrollmentWarning = false;
        this.savedBusChangeId = null;
        this.notificationDialogSource = null;
      }
    },

    confirmSaveWithIncompleteRoute() {
      this.incompleteRouteDialog = false;
      if (this.warningDialogSource === "add") {
        this.addSkipRouteWarning = true;
        this.warningDialogSource = null;
        this.saveAddBusChange();
        return;
      }
      this.skipRouteWarning = true;
      this.warningDialogSource = null;
      this.saveRequest();
    },

    confirmSaveWithoutGeoStop() {
      this.missingGeoStopDialog = false;
      if (this.warningDialogSource === "add") {
        this.addSkipGeoStopWarning = true;
        this.warningDialogSource = null;
        this.saveAddBusChange();
        return;
      }
      this.skipGeoStopWarning = true;
      this.warningDialogSource = null;
      this.saveRequest();
    },

    confirmSaveWithEnrollmentWarning() {
      this.enrollmentWarningDialog = false;
      this.skipEnrollmentWarning = true;
      this.saveRequest();
    },

    cancelEnrollmentWarning() {
      this.enrollmentWarningDialog = false;
      this.skipEnrollmentWarning = false;
    },

    async loadDayCampEnrollmentForRequest(row) {
      this.dayCampEnrollment = null;
      if (!row) return;

      const accountID = (
        row.familyID ||
        row.FamilyID ||
        row.accountID ||
        row.AccountID ||
        ""
      )
        .toString()
        .trim();
      const studentID = (row.studentID || row.StudentID || "")
        .toString()
        .trim();
      if (!accountID || !studentID) return;

      try {
        const students = await this.$http.get(
          `api/daycamp/getEnrolledStudentsByAccountID?accountID=${accountID}`
        );
        if (!students || !students.length) return;

        const match = students.find(
          (s) =>
            s.studentID &&
            s.studentID
              .toString()
              .trim()
              .toUpperCase() === studentID.toUpperCase()
        );
        this.dayCampEnrollment = match || null;
      } catch (e) {
        this.dayCampEnrollment = null;
      }
    },

    getEnrollmentSummary(student) {
      if (!student) return "";
      const type = (student.regType || "").trim();
      if (!type) return "";
      const weekCount = (student.weeks || []).length;
      if (type.toUpperCase() === "CUSTOM") {
        return `Custom (${weekCount} week${weekCount === 1 ? "" : "s"})`;
      }
      return type;
    },

    getEnrollmentDisplay(student) {
      if (!student) return "";
      const type = (student.regType || "").trim();
      if (!type) return "";
      const detail = this.getEnrollmentWeeksLabel(student);
      if (type.toUpperCase() === "CUSTOM") {
        const weekCount = (student.weeks || []).length;
        const label = `Custom (${weekCount} week${weekCount === 1 ? "" : "s"})`;
        return detail
          ? `Enrolled for ${label}: ${detail}`
          : `Enrolled for ${label}`;
      }
      return detail
        ? `Enrolled for ${type} (${detail})`
        : `Enrolled for ${type}`;
    },

    getEnrollmentWeeksLabel(student) {
      if (!student) return "";
      const weeks = student.weeks || [];
      const type = (student.regType || "").trim().toUpperCase();

      // For non-custom enrollments (FULL / HALF / etc.) show the overall
      // enrolled range instead of listing every week.
      if (type !== "CUSTOM") {
        const start =
          student.enrollmentStartDate ||
          (weeks.length ? weeks[0].startDate : null);
        const end =
          student.enrollmentEndDate ||
          (weeks.length ? weeks[weeks.length - 1].endDate : null);
        if (start && end) {
          return `${moment(start).format("MMM D")}–${moment(end).format(
            "MMM D"
          )}`;
        }
        return "";
      }

      if (!weeks.length) {
        if (student.enrollmentStartDate && student.enrollmentEndDate) {
          return `${moment(student.enrollmentStartDate).format(
            "MMM D"
          )}–${moment(student.enrollmentEndDate).format("MMM D")}`;
        }
        return "";
      }
      return weeks
        .map((w) => {
          const label = w.parshe ? w.parshe : `Week ${w.weekNumber}`;
          const dates =
            w.startDate && w.endDate
              ? ` (${moment(w.startDate).format("MMM D")}–${moment(
                  w.endDate
                ).format("MMM D")})`
              : "";
          return label + dates;
        })
        .join(", ");
    },

    getEnrollmentDateWarning() {
      if (!this.dayCampEnrollment) return null;

      const weeks = this.dayCampEnrollment.weeks || [];
      if (!weeks.length) return null;

      const start = this.formData.startDate;
      const end = this.formData.endDate;
      if (!start || !end) return null;

      const reqStart = moment(start, "YYYY-MM-DD").startOf("day");
      const reqEnd = moment(end, "YYYY-MM-DD").startOf("day");
      if (!reqStart.isValid() || !reqEnd.isValid()) return null;
      if (reqEnd.isBefore(reqStart)) return null;

      const ranges = weeks
        .filter((w) => w.startDate && w.endDate)
        .map((w) => ({
          s: moment(w.startDate).startOf("day"),
          e: moment(w.endDate).startOf("day"),
        }))
        .filter((r) => r.s.isValid() && r.e.isValid());
      if (!ranges.length) return null;

      // Every day in the requested range must fall within an enrolled week.
      // A small tolerance bridges weekend gaps between adjacent enrolled weeks
      // while still flagging real multi-week gaps (e.g. custom weeks 1-3, 7-9).
      const covers = (day) =>
        ranges.some(
          (r) =>
            day.isSameOrAfter(r.s) &&
            day.isSameOrBefore(r.e.clone().add(2, "days"))
        );

      const cursor = reqStart.clone();
      let hasUncovered = false;
      while (cursor.isSameOrBefore(reqEnd)) {
        if (!covers(cursor)) {
          hasUncovered = true;
          break;
        }
        cursor.add(1, "day");
      }
      if (!hasUncovered) return null;

      return (
        "This student is only enrolled in day camp for: " +
        this.getEnrollmentWeeksLabel(this.dayCampEnrollment) +
        ". The selected date range includes dates outside their enrollment."
      );
    },

    getTeacherDisplay(student) {
      const teachers = [];
      if (student.teacherH) teachers.push(student.teacherH);
      if (student.teacherH2) teachers.push(student.teacherH2);
      return teachers.join(" / ") || "-";
    },

    getDcTeacher(student) {
      if (!student) return "";
      const teachers = [];
      if (student.teacherH) teachers.push(student.teacherH);
      if (student.teacherH2) teachers.push(student.teacherH2);
      return teachers.join(" / ");
    },

    onBusChangeSaved() {
      // Reload grid/simple view when bus change is saved
      this.refreshData();
    },

    onViewDialogClose() {
      this.viewDialog = false;
      this.viewBusChangeId = null;
      this.viewDialogSendNotificationOnSave = false;
    },
  },
};
</script>

<style scoped>
.request-dialog .dialog-header {
  background: linear-gradient(135deg, rgb(92, 184, 96) 0%, #388e3c 100%);
  color: white;
  padding: 0px 16px;
}

.request-dialog .dialog-title {
  font-size: 20px;
  font-weight: 400;
}

.dialog-header {
  background: linear-gradient(135deg, rgb(92, 184, 96) 0%, #388e3c 100%);
  color: white;
  padding: 0px 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 400;
}

.dialog-body {
  background: #f2f4f2;
}

.section-header {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: rgb(92, 184, 96);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e8f5e9;
}

.recording-card {
  background-color: #fafafa;
}

.audio-player {
  width: 100%;
  min-height: 40px;
  display: block;
}

.compact-info {
  font-size: 0.8125rem;
}

.info-row {
  display: flex;
  padding: 4px 0;
  border-bottom: 1px solid #f5f5f5;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 0.75rem;
  color: #757575;
  min-width: 78px;
  padding-right: 6px;
  flex-shrink: 0;
}

.info-value {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #212121;
  flex: 1;
}

.info-value-full {
  margin-left: 0;
}

.info-row-audio {
  align-items: center;
}

.info-value-audio {
  display: flex;
  align-items: center;
}

.inline-audio-player {
  width: 100%;
  max-width: 230px;
  height: 30px;
}

.original-request-card {
  background: linear-gradient(135deg, #fff7f7 0%, #fff1f1 100%);
  border: 2px solid #ff9e9e !important;
  box-shadow: 0 3px 10px rgba(255, 107, 107, 0.16);
  border-radius: 4px;
}

.pending-request-indicator {
  font-size: 12px;
  font-weight: 600;
  color: #b71c1c;
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: 6px;
  padding: 6px 8px;
}

.save-btn {
  min-width: 140px;
}

.route-list-item {
  padding-top: 0px;
  padding-bottom: 0px;
  padding-left: 8px;
  transition: all 0.2s ease;
}

.route-list-item:hover {
  background-color: rgba(92, 184, 96, 0.05);
}

.route-list-item .v-list__tile__sub-title .v-icon {
  opacity: 1;
}

.route-detail-chip {
  background-color: #e0e0e0;
  color: #000;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  margin-left: 8px;
  display: inline-block;
}

.route-selection-wrapper {
  width: 100%;
}

.route-selection-main {
  display: flex;
  align-items: center;
}

.route-selection-driver {
  font-size: 0.75rem;
  color: #757575;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.comments-card {
  background: #fafafa;
  max-height: 460px;
  overflow-y: auto;
}

.comment-entry {
  border: 1px solid #ececec;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 8px;
  background: white;
  text-align: left !important;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.comment-entry:last-child {
  margin-bottom: 0;
}

.comment-meta {
  font-size: 12px;
  color: #616161;
  margin-bottom: 4px;
  text-align: left !important;
  width: 100%;
}

.comment-caller {
  font-size: 12px;
  margin-top: 4px;
  margin-bottom: 0;
  text-align: left !important;
  width: 100%;
}

.comment-text {
  font-size: 13px;
  white-space: normal;
  text-align: left !important;
  line-height: 1.4;
  width: 100%;
  margin-top: 8px;
  word-break: break-word;
}

.comment-audio {
  width: 100%;
  min-height: 36px;
}

.comment-awaiting {
  font-size: 11px;
  font-weight: 600;
  color: #b26a00;
  background: #fff8e1;
  border: 1px solid #ffd54f;
  border-radius: 4px;
  padding: 2px 6px;
}

.period-helper-text {
  line-height: 1.35;
  padding-left: 2px;
}

.period-section-card {
  background: #fcfcfc;
  border-color: #e9e9e9 !important;
}

.form-section-card {
  background: #ffffff;
  border: 1px solid #e2e8e2 !important;
  border-radius: 8px !important;
}

.add-phone-search-panel {
  display: flex;
  align-items: center;
  gap: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #f4faf4 100%);
  border: 1px solid #cfe8cf;
  border-radius: 12px;
  padding: 12px 18px;
  box-shadow: 0 1px 3px rgba(46, 125, 50, 0.08),
    0 4px 14px rgba(46, 125, 50, 0.1);
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}

.add-phone-search-panel:focus-within {
  border-color: rgb(92, 184, 96);
  box-shadow: 0 1px 3px rgba(46, 125, 50, 0.1),
    0 6px 18px rgba(46, 125, 50, 0.18);
}

.add-phone-search-avatar {
  flex: none;
  box-shadow: 0 2px 6px rgba(46, 125, 50, 0.35);
}

.add-phone-search-body {
  flex: 1;
  min-width: 0;
}

.add-phone-search-label {
  font-size: 11px;
  font-weight: 700;
  color: #4a8f4d;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 2px;
}

.add-phone-search-field.v-text-field {
  margin: 0;
  padding: 0;
}

.add-phone-search-field .v-input__slot {
  min-height: 32px !important;
}

.add-phone-search-field input {
  font-size: 20px;
  font-weight: 600;
  color: #263238;
  letter-spacing: 0.3px;
}

.add-search-or-divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: #9e9e9e;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.add-search-or-divider::before,
.add-search-or-divider::after {
  content: "";
  flex: 1;
  border-bottom: 1px solid #e0e0e0;
}

.add-search-or-divider span {
  padding: 0 10px;
}

.geo-stop-picker >>> .v-select__selections {
  flex-wrap: nowrap;
  min-width: 0;
  overflow: hidden;
}

.geo-stop-picker >>> .v-select__slot {
  min-width: 0;
}

.geo-stop-selection {
  max-width: 100%;
}

.geo-stop-picker {
  background: #f7faf7;
  border: 1px dashed #c5d6c5;
  border-radius: 8px;
  padding: 8px 8px 2px;
}

.geo-stop-selection {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.geo-stop-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: #2e7d32;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.geo-stop-address {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.geo-stop-item-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.period-title {
  color: #5f5f5f;
  font-weight: 600;
  letter-spacing: 0.3px;
}

/* Account Search Styles */
.search-toggle {
  height: 32px !important;
  border-radius: 6px;
}

.search-toggle .v-btn {
  font-size: 13px;
  padding: 6px 12px !important;
  min-width: 70px;
}

.search-toggle.v-btn-toggle .v-btn.v-btn--active {
  background-color: rgb(92, 184, 96) !important;
  color: white !important;
}

.account-list-container {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}

.account-list {
  background: transparent;
}

.account-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #eeeeee;
}

.account-item:hover {
  background-color: #e8f5e9 !important;
}

.account-item:last-child {
  border-bottom: none;
}

.add-student-check-action {
  min-width: 32px;
}

.add-multi-select-bar {
  background: #e8f5e9;
  border: 1px solid rgb(92, 184, 96);
  border-radius: 8px;
}

.add-selected-students-hint {
  padding-left: 4px;
}

.delete-warning-banner {
  border-radius: 8px;
}

.delete-summary-card {
  border: 1px solid #ffcdd2;
  background: #fff5f5;
  border-radius: 10px;
  padding: 10px 12px;
}

.delete-summary-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #c62828;
  margin-bottom: 8px;
}

.delete-summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 12px;
}

.delete-summary-item--full {
  grid-column: 1 / -1;
}

.delete-label {
  font-size: 11px;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.delete-value {
  font-size: 13px;
  color: #212121;
  font-weight: 500;
  line-height: 1.35;
}

/* Header Action Bar */
.view-mode-bar {
  border-radius: 8px;
}

.header-main-action-btn {
  height: 38px !important;
  padding: 0 18px !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px !important;
  text-transform: none !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16) !important;
}

.header-report-btn {
  border: 2px solid rgb(92, 184, 96) !important;
  color: #2e7d32 !important;
  background-color: #f4fbf5 !important;
  box-shadow: 0 2px 5px rgba(46, 125, 50, 0.14) !important;
}

.header-report-btn:hover {
  background-color: #e8f5e9 !important;
}

.header-add-btn {
  background-color: rgb(92, 184, 96) !important;
  box-shadow: 0 3px 8px rgba(46, 125, 50, 0.35) !important;
}

/* Simple Card View */
.simple-section {
  border-radius: 10px;
  overflow: hidden;
}

.simple-section-minimized .simple-section-header {
  padding: 8px 16px;
}

.section-filter-bar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  margin: 0 4px 12px;
}

.section-filter-search {
  flex: 0 0 240px;
  max-width: 240px;
  padding-top: 0 !important;
  margin-top: 0 !important;
}

.section-filter-buildings {
  flex: 0 1 220px;
  max-width: 220px;
  padding-top: 0 !important;
  margin-top: 0 !important;
}

.simple-section-header-clickable {
  cursor: pointer;
  user-select: none;
}

.simple-section-header-clickable:hover {
  background: #f0f0f0;
}

.enteredToday-count {
  background: rgb(92, 184, 96);
}

.stillOpen-count {
  background: #5c6bc0;
}

.section-overdue-count {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  min-height: 22px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.3px;
  color: #fff;
  background: linear-gradient(135deg, #e53935, #b71c1c);
  border-radius: 13px;
  padding: 2px 10px;
  box-shadow: 0 1px 6px rgba(183, 28, 28, 0.45);
  text-transform: uppercase;
}

.enteredToday-card {
  border-left-color: rgb(92, 184, 96) !important;
}

.stillOpen-card {
  border-left-color: #5c6bc0 !important;
}

.stillOpen-card-overdue {
  border-left-color: #e53935 !important;
  background: #fff8f7;
}

.overdue-banner {
  display: inline-flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: #b71c1c;
  background: #ffebee;
  border: 1px solid #ef9a9a;
  border-radius: 8px;
  padding: 3px 8px;
}

.simple-section-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 12px 16px;
  background: #fafafa;
}

.simple-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #424242;
}

.section-count {
  margin-left: 10px;
  min-width: 26px;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  border-radius: 13px;
  padding: 2px 8px;
}

.pending-count {
  background: #ff9800;
}

.active-count {
  background: rgb(92, 184, 96);
}

.section-hint {
  font-style: italic;
}

.simple-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
}

.simple-empty {
  text-align: center;
  padding: 28px 0;
}

.simple-card {
  border-radius: 10px !important;
  border: 1px solid #e2e8e2;
  border-left-width: 5px !important;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  height: 100%;
}

.theme--dark.v-btn.end-bus-change-btn.v-btn--disabled:not(.v-btn--icon):not(.v-btn--flat):not(.v-btn--outline) {
  background-color: #e0e0e0 !important;
  color: #757575 !important;
  opacity: 1 !important;
}

.end-bus-change-btn.v-btn--disabled >>> .v-btn__content,
.end-bus-change-btn.v-btn--disabled >>> .v-icon {
  color: #757575 !important;
}

.end-bc-list {
  max-height: 420px;
  overflow-y: auto;
  padding-right: 2px;
}

.end-bc-item {
  display: flex;
  align-items: flex-start;
  padding: 10px 12px;
  margin-bottom: 8px;
  border: 1px solid #e2e8e2;
  border-left: 4px solid rgb(92, 184, 96);
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease,
    background 0.15s ease;
}

.end-bc-item:hover {
  background: #f7fbf7;
  border-color: #c8e6c9;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.end-bc-item-overdue {
  border-left-color: #e53935;
  background: #fff8f7;
}

.end-bc-item-overdue:hover {
  background: #fff3f1;
  border-color: #ef9a9a;
}

.end-bc-overdue-filter {
  color: #fff !important;
  border: 1px solid rgba(255, 255, 255, 0.85) !important;
  background: transparent !important;
  text-transform: none !important;
  letter-spacing: 0.2px;
  height: 28px !important;
  font-weight: 700;
}

.end-bc-overdue-filter-on {
  background: #b71c1c !important;
  border-color: #b71c1c !important;
}

.end-bc-item-body {
  flex: 1;
  min-width: 0;
}

.end-bc-item-title {
  font-size: 14px;
  font-weight: 700;
  color: #212121;
  line-height: 1.3;
}

.end-bc-item-id {
  font-weight: 400;
  margin-right: 4px;
}

.end-bc-item-eng {
  font-size: 12px;
  color: #757575;
  margin-top: 1px;
}

.end-bc-item-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 5px;
  gap: 2px 10px;
}

.end-bc-meta-chip {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: #616161;
  line-height: 1.3;
}

.end-bc-meta-chip .v-icon {
  margin-right: 4px;
  color: #9e9e9e;
}

.end-bc-item-chevron {
  flex-shrink: 0;
  margin-top: 8px;
  margin-left: 4px;
}

.simple-card:hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.pending-card {
  border-left-color: #ff9800 !important;
}

.card-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 2px;
}

.card-student-name {
  font-size: 16px;
  font-weight: 700;
  color: #212121;
  line-height: 1.3;
  padding-right: 8px;
}

.card-student-id {
  font-size: 11px;
  font-weight: 400;
  color: #9e9e9e;
}

.card-eng-name {
  font-size: 12px;
  color: #757575;
  margin-bottom: 4px;
}

.change-type-chip {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  border-radius: 12px;
  padding: 3px 8px;
  white-space: nowrap;
}

.chip-single {
  background: #1e88e5;
}

.chip-extended {
  background: #8e24aa;
}

.card-info-line {
  display: flex;
  align-items: center;
  font-size: 12.5px;
  color: #424242;
  margin-top: 6px;
  line-height: 1.35;
}

.card-line-icon {
  margin-right: 6px;
  color: #9e9e9e;
}

.address-line-content {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
}

.address-original {
  color: #757575;
}

.address-arrow {
  color: #bdbdbd !important;
  margin: 0 1px;
}

.address-changed {
  font-weight: 700;
  color: #e65100;
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: 6px;
  padding: 1px 7px;
}

.card-footer-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

.card-meta {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  color: #757575;
  margin-right: 12px;
}

.card-meta .v-icon {
  margin-right: 3px;
}

.active-status-chip {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  border-radius: 10px;
  padding: 2px 8px;
  margin-right: 10px;
}

.status-active {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}

.status-ending {
  background: #fff3e0;
  color: #b26a00;
  border: 1px solid #ffcc80;
}

.status-upcoming {
  background: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
}

.status-ended {
  background: linear-gradient(135deg, #e53935, #b71c1c);
  color: #ffffff;
  border: 1px solid #8e0000;
  font-weight: 700;
  box-shadow: 0 1px 4px rgba(183, 28, 28, 0.55);
  letter-spacing: 0.2px;
}

/* Simple Add Bus Change Dialog */
.add-selected-student-card {
  position: relative;
  border: 1px solid #c8e6c9 !important;
  background: #fbfefb;
  padding-right: 28px !important;
}

.add-student-clear-btn {
  position: absolute !important;
  top: 2px;
  right: 2px;
  z-index: 1;
}

.add-student-row {
  min-height: 0;
}

.add-student-main {
  min-width: 0;
}

.add-student-name {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.25;
  color: #424242;
}

.add-student-class-chip {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  max-width: 100%;
  margin-top: 3px;
  padding: 2px 8px;
  border-radius: 12px;
  background: #e8f5e9;
  color: #1b5e20;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
  white-space: normal;
}

.add-student-teacher {
  white-space: normal;
  overflow-wrap: anywhere;
}

.sibling-share-bus-check {
  margin-top: 0;
}

.sibling-share-bus-check >>> .v-label {
  font-size: 13px;
}

.add-student-meta-col {
  border-left: 1px solid #e8e8e8;
  padding-right: 4px;
}

.add-student-meta-line {
  line-height: 1.3;
  margin-bottom: 1px;
}

.daycamp-enrollment-line {
  color: #2e7d32;
}

@media (max-width: 599px) {
  .add-student-meta-col {
    border-left: none;
    border-top: 1px solid #e8e8e8;
    margin-top: 6px;
    padding-top: 6px;
  }
}

.duration-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.duration-option {
  flex: 1 1 140px;
  border: 1.5px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px 10px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  background: #fff;
}

.duration-option:hover {
  border-color: rgb(92, 184, 96);
  background: #f6fbf6;
}

.duration-option-selected,
.duration-option-selected:hover {
  border-color: #1b5e20;
  background: #2e7d32;
  box-shadow: 0 2px 8px rgba(46, 125, 54, 0.45);
}

.duration-option-title {
  font-size: 14px;
  font-weight: 600;
  color: #424242;
  text-align: center;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
}

.duration-option-selected .duration-option-title {
  color: #ffffff;
  font-weight: 700;
}

.duration-option-selected .duration-option-check {
  color: #ffffff !important;
}

.duration-selected-caption {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #616161;
  margin-top: 8px;
  padding-left: 2px;
}

.add-notification-row {
  border-top: 1px dashed #e0e0e0;
  padding-top: 8px;
}

.add-notification-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  line-height: 1.4;
}

.text-none {
  text-transform: none;
}
</style>
